/**
 * Procedural oil platform — built at genuine production scale.
 *
 * The player stands roughly 30 storeys above the sea on a cantilevered observation
 * platform, with another ~20 storeys of derrick and module stack above. That vertical
 * extent is the point: the drop to the water is what makes the ocean frightening, and a
 * structure that continues past the top of the frame is what makes the rig feel like a
 * place rather than a prop.
 *
 * Everything is primitives (no free offshore-platform model has a usable licence — see
 * ASSET_AND_LICENSE_PLAN.md), accumulated into one buffer per material so the whole
 * structure costs five draw calls.
 */

import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { CreateBoxVertexData } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinderVertexData } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreatePlaneVertexData } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { Matrix, Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { SpotLight } from '@babylonjs/core/Lights/spotLight';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import type { Scene } from '@babylonjs/core/scene';
import type { PBRMetallicRoughnessMaterial } from '@babylonjs/core/Materials/PBR/pbrMetallicRoughnessMaterial';

import {
  createRigMaterials, createLampMaterial, type RigMaterials,
} from './rigMaterials';
import { maxSimultaneousLightsFor } from '../lightBudget';
import { createPhysicalUvs, type RigPrimitiveKind } from './physicalUvs';
import { createBeveledBoxVertexData } from './beveledBox';
import { DECK_HEIGHT, EYE_Z } from '../constants';

/** Nominal storey height used to lay the structure out, metres. */
const FLOOR = 3.5;

/**
 * Observation platform walking surface. The eye sits 1.7m above it — actual standing eye
 * height. Anything taller pushes the handrail below the bottom of the frame as soon as
 * the player steps up to it.
 */
export const DECK_Y = DECK_HEIGHT - 1.7;
/** Main production deck, two storeys below the observation platform. */
const MAIN_DECK_Y = DECK_Y - FLOOR * 2;
/** Cellar deck — the last plated level before open jacket. */
const CELLAR_Y = MAIN_DECK_Y - FLOOR * 2.4;
/** Top of the derrick, ~20 storeys above the player. */
const TOWER_TOP = DECK_HEIGHT + FLOOR * 20;

/** Jacket legs are raked: they splay outboard on the way down to the sea. */
const LEG_TOP_RADIUS = 17;
const LEG_BASE_RADIUS = 30;
const LEG_BASE_Y = -22;

class MeshAccumulator {
  private positions: number[] = [];
  private normals: number[] = [];
  private uvs: number[] = [];
  private colors: number[] = [];
  private indices: number[] = [];

  constructor(
    private readonly tilesPerMetre: number,
    private readonly options: {
      bevelBoxes?: boolean;
      edgeWeatherColor?: readonly [number, number, number];
    } = {},
  ) {}

  /**
   * How many vertices have been accumulated so far.
   *
   * Paired with `translateFrom`, this lets a whole named structure be relocated after it
   * has been built, without every coordinate inside it having to be found and edited.
   * That matters because the rig's sections express positions as named constants, bare
   * arrays, destructured tuples AND inline arguments — four forms that no search and
   * replace catches together. Moving one by hand tore it three separate ways.
   */
  get vertexMark(): number {
    return this.positions.length;
  }

  /** Translate every vertex added since `mark`. The move is exact and cannot miss one. */
  translateFrom(mark: number, dx: number, dy: number, dz: number): void {
    for (let i = mark; i < this.positions.length; i += 3) {
      (this.positions[i] as number) += dx;
      (this.positions[i + 1] as number) += dy;
      (this.positions[i + 2] as number) += dz;
    }
  }

  /** Axis-aligned centre of everything added since `mark` — the natural pivot. */
  centreFrom(mark: number): Vector3 {
    if (this.positions.length <= mark) return Vector3.Zero();
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let i = mark; i < this.positions.length; i += 3) {
      const x = this.positions[i] as number;
      const y = this.positions[i + 1] as number;
      const z = this.positions[i + 2] as number;
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
      if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
    }
    return new Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
  }

  /**
   * Apply an arbitrary transform to everything added since `mark`.
   *
   * Normals are transformed too, and deliberately as directions rather than points: skip
   * that and a rotated building keeps the lighting of its old orientation, which reads as
   * every face being lit from the wrong side.
   */
  transformFrom(mark: number, m: Matrix): void {
    /*
     * Normals transform by the inverse-transpose, not by the matrix itself.
     *
     * For a pure rotation the two are identical, so this made no difference until the
     * Power Station was stretched. Under a non-uniform scale they diverge: using `m`
     * directly skews every normal on the stretched faces and the building lights as if
     * its walls were at angles they are not.
     */
    const normalMatrix = m.clone().invert().transpose();
    const p = new Vector3();
    for (let i = mark; i < this.positions.length; i += 3) {
      p.set(
        this.positions[i] as number,
        this.positions[i + 1] as number,
        this.positions[i + 2] as number,
      );
      const tp = Vector3.TransformCoordinates(p, m);
      this.positions[i] = tp.x;
      this.positions[i + 1] = tp.y;
      this.positions[i + 2] = tp.z;

      p.set(
        this.normals[i] as number,
        this.normals[i + 1] as number,
        this.normals[i + 2] as number,
      );
      const tn = Vector3.TransformNormal(p, normalMatrix).normalize();
      this.normals[i] = tn.x;
      this.normals[i + 1] = tn.y;
      this.normals[i + 2] = tn.z;
    }
  }

  add(
    data: VertexData,
    transform: Matrix,
    uvScale = 1,
    surfaceTint: readonly [number, number, number] = [1, 1, 1],
    bevelBox = true,
  ): void {
    const base = this.positions.length / 3;
    const primitiveKind = primitiveKinds.get(data);
    if (!primitiveKind) throw new Error('rig accumulator received an unknown primitive');

    let source = data;
    let edgeWeights: readonly number[] | undefined;
    if (primitiveKind === 'box' && this.options.bevelBoxes !== false && bevelBox) {
      const beveled = createBeveledBoxVertexData(transform);
      source = beveled.data;
      edgeWeights = beveled.edgeWeights;
    }

    const pos = source.positions as number[];
    const nrm = source.normals as number[];
    const idx = source.indices as number[];
    const normalMatrix = transform.clone();
    normalMatrix.setTranslationFromFloats(0, 0, 0);

    const p = new Vector3();
    const n = new Vector3();
    for (let i = 0; i < pos.length; i += 3) {
      p.set(pos[i] as number, pos[i + 1] as number, pos[i + 2] as number);
      const tp = Vector3.TransformCoordinates(p, transform);
      this.positions.push(tp.x, tp.y, tp.z);
      n.set(nrm[i] as number, nrm[i + 1] as number, nrm[i + 2] as number);
      const tn = Vector3.TransformNormal(n, normalMatrix).normalize();
      this.normals.push(tn.x, tn.y, tn.z);

      const edgeWeight = edgeWeights?.[i / 3] ?? 0;
      const weatherColor = this.options.edgeWeatherColor ?? [0.72, 0.58, 0.46];
      // Position-stable variation keeps corrosion from becoming a uniform dark outline.
      // It affects only the new bevel vertices; broad faces retain the full scan.
      const hash = Math.sin(
        tp.x * 12.9898 + tp.y * 78.233 + tp.z * 37.719,
      ) * 43758.5453;
      const noise = hash - Math.floor(hash);
      const weathering = edgeWeight * (0.42 + noise * 0.34);
      this.colors.push(
        surfaceTint[0] * (1 + (weatherColor[0] - 1) * weathering),
        surfaceTint[1] * (1 + (weatherColor[1] - 1) * weathering),
        surfaceTint[2] * (1 + (weatherColor[2] - 1) * weathering),
        1,
      );
    }
    this.uvs.push(
      ...createPhysicalUvs(source, transform, primitiveKind, this.tilesPerMetre * uvScale),
    );
    for (const v of idx) this.indices.push(base + v);
  }

  get isEmpty(): boolean {
    return this.indices.length === 0;
  }

  build(scene: Scene, name: string, material: PBRMetallicRoughnessMaterial): Mesh | null {
    if (this.isEmpty) return null;
    const mesh = new Mesh(name, scene);
    const vd = new VertexData();
    vd.positions = this.positions;
    vd.normals = this.normals;
    vd.uvs = this.uvs;
    vd.colors = this.colors;
    vd.indices = this.indices;
    vd.applyToMesh(mesh, false);
    mesh.material = material;
    mesh.useVertexColors = true;
    mesh.hasVertexAlpha = false;
    mesh.isPickable = false;
    mesh.receiveShadows = false;
    return mesh;
  }
}

const shapeCache = new Map<string, VertexData>();
const primitiveKinds = new WeakMap<VertexData, RigPrimitiveKind>();
/**
 * Dark blue profiled cladding, shared by every clad wall on the rig.
 *
 * Blue sits near unity while red and green are crushed almost to nothing, because the
 * tint MULTIPLIES through a grey plate scan, a grey base colour and then the ACES grade.
 * A tint that merely leans blue arrives as cold grey; this survives the chain. Shared
 * rather than repeated so the panels cannot drift apart the first time one is retuned.
 */
const CLAD_BLUE_SHEET: readonly [number, number, number] = [0.004, 0.022, 0.66];
/** Ribs sit slightly brighter so the profile separates from the sheet behind it. */
const CLAD_BLUE_RIB: readonly [number, number, number] = [0.006, 0.034, 0.88];

function unitBox(): VertexData {
  let v = shapeCache.get('box');
  if (!v) {
    v = CreateBoxVertexData({ size: 1 });
    shapeCache.set('box', v);
  }
  primitiveKinds.set(v, 'box');
  return v;
}
function unitCylinder(tessellation: number): VertexData {
  const key = `cyl${tessellation}`;
  let v = shapeCache.get(key);
  if (!v) {
    v = CreateCylinderVertexData({ height: 1, diameter: 1, tessellation });
    shapeCache.set(key, v);
  }
  primitiveKinds.set(v, 'cylinder');
  return v;
}

function boxTransform(cx: number, cy: number, cz: number, sx: number, sy: number, sz: number): Matrix {
  return Matrix.Scaling(sx, sy, sz).multiply(Matrix.Translation(cx, cy, cz));
}

function boxYawTransform(
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
  yaw: number,
): Matrix {
  return Matrix.Scaling(sx, sy, sz)
    .multiply(Matrix.RotationY(yaw))
    .multiply(Matrix.Translation(cx, cy, cz));
}

/** A member running between two arbitrary points — the workhorse for trusses and bracing. */
function memberTransform(a: Vector3, b: Vector3, thickness: number): Matrix {
  const dir = b.subtract(a);
  const len = dir.length();
  if (len < 1e-5) return Matrix.Identity();
  const mid = a.add(b).scale(0.5);
  const nd = dir.normalize();
  const up = new Vector3(0, 1, 0);
  const axis = Vector3.Cross(up, nd);
  const dot = Vector3.Dot(up, nd);
  let rot: Matrix;
  if (axis.lengthSquared() < 1e-9) {
    rot = dot > 0 ? Matrix.Identity() : Matrix.RotationX(Math.PI);
  } else {
    const q = Quaternion.RotationAxis(axis.normalize(), Math.acos(Math.max(-1, Math.min(1, dot))));
    rot = new Matrix();
    q.toRotationMatrix(rot);
  }
  return Matrix.Scaling(thickness, len, thickness)
    .multiply(rot)
    .multiply(Matrix.Translation(mid.x, mid.y, mid.z));
}

export interface RigBuildResult {
  meshes: Mesh[];
  lights: {
    spots: SpotLight[];
    points: PointLight[];
    lampMeshes: Mesh[];
    oceanFloods: RigOceanFlood[];
  };
  drawCalls: number;
}

export interface RigOceanFlood {
  position: Vector3;
  direction: Vector3;
}

export function buildRig(
  scene: Scene,
  detail: 'desktop' | 'mobile' = 'desktop',
): RigBuildResult {
  const mats: RigMaterials = createRigMaterials(scene);
  const bevelBoxes = detail === 'desktop';

  // One base density per material is the scale authority. The existing per-object
  // uvScale remains a positive local multiplier, but now changes both axes in metres.
  const yellow = new MeshAccumulator(0.32, {
    edgeWeatherColor: [0.76, 0.58, 0.30],
    bevelBoxes,
  });
  const steel = new MeshAccumulator(0.15, {
    edgeWeatherColor: [0.67, 0.51, 0.40],
    bevelBoxes,
  });
  const dark = new MeshAccumulator(0.13, {
    edgeWeatherColor: [0.63, 0.51, 0.43],
    bevelBoxes,
  });
  // MetalPlates013 is catalogued as a 1.6m square; the common 3–5 local multipliers
  // keep its effective span around that real-world size.
  const plate = new MeshAccumulator(0.18, {
    edgeWeatherColor: [0.68, 0.48, 0.34],
    bevelBoxes,
  });
  // Observation decks use multipliers 4–5, yielding roughly one walkway scan per metre.
  const openGrate = new MeshAccumulator(0.22, {
    bevelBoxes: false,
  });
  // Machined near-black steel: the crane's hook block and its trunnion nut.
  const machined = new MeshAccumulator(0.13, {});

  const box = unitBox();
  // Thin lamp bezels do not need the 44-triangle building-beam bevel. A five-sided
  // galvanized profile keeps their external corners softly faceted at a quarter of the
  // triangle cost, which lets the rig carry dozens of repeated work lights.
  const floodFrameMember = unitCylinder(5);
  const cyl = unitCylinder(detail === 'mobile' ? 6 : 16);
  // Twenty-four sides remain visually round at the rig's scale while recovering enough
  // geometry for inhabited decks and circulation. The old 32-sided profile spent hero
  // mesh density on every distant flange, exhaust and vessel ring.
  const cylHi = unitCylinder(detail === 'mobile' ? 8 : 24);

  const legCorners: [number, number][] = [
    [-1, -1], [1, -1], [-1, 1], [1, 1],
  ];

  // Railing helper is declared early because the tower walkways use it too.
  const addRailRun = (
    y: number, x0: number, z0: number, x1: number, z1: number, spacing = 1.6,
  ): void => {
    const dx = x1 - x0;
    const dz = z1 - z0;
    const len = Math.hypot(dx, dz);
    const n = Math.max(1, Math.round(len / spacing));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const px = x0 + dx * t;
      const pz = z0 + dz * t;
      // Tubular stanchion with a slight base plate.
      yellow.add(cyl, boxTransform(px, y + 0.58, pz, 0.075, 1.16, 0.075), 1);
      steel.add(box, boxTransform(px, y + 0.03, pz, 0.19, 0.06, 0.19), 1);
    }
    // Top and mid rails are round tube; the toe board stays flat plate.
    for (const [h, th] of [[1.11, 0.062], [0.6, 0.05]] as const) {
      yellow.add(cyl, memberTransform(new Vector3(x0, y + h, z0), new Vector3(x1, y + h, z1), th), 3);
    }
    steel.add(box, memberTransform(
      new Vector3(x0, y + 0.13, z0), new Vector3(x1, y + 0.13, z1), 0.11,
    ), 2);
  };

  /**
   * Close camera rails need more than a yellow material. Short exposed-steel sleeves,
   * welded repair collars and worn stanchion sections break the silhouette and make the
   * peeling paint legible even when the photographic albedo is seen at a grazing angle.
   * This is reserved for the observation deck; repeating it on every distant gallery
   * would add triangles without surviving on screen.
   */
  const addRailWear = (
    y: number,
    x0: number,
    z0: number,
    x1: number,
    z1: number,
    spacing = 1.6,
    phase = 0,
  ): void => {
    const start = new Vector3(x0, 0, z0);
    const end = new Vector3(x1, 0, z1);
    const direction = end.subtract(start);
    const len = direction.length();
    if (len < 0.25) return;
    const axis = direction.normalize();

    const scars = [
      [0.22, 0.12, false],
      [0.72, 0.16, false],
    ] as const;
    for (const [baseT, scarLength, oxidised] of scars) {
      const t = Math.max(0.03, Math.min(0.97, baseT + phase * 0.013));
      const centre = Vector3.Lerp(start, end, t);
      const half = axis.scale(Math.min(scarLength, len * 0.12) * 0.5);
      const acc = oxidised ? dark : steel;
      for (const [height, diameter] of [[1.11, 0.065], [0.6, 0.053]] as const) {
        acc.add(cyl, memberTransform(
          new Vector3(centre.x - half.x, y + height, centre.z - half.z),
          new Vector3(centre.x + half.x, y + height, centre.z + half.z),
          diameter,
        ), 5);
      }
    }

    // Weld beads at old rail joins. They share the painted material so grazing-angle
    // specular light reveals the fabrication without creating clean black tape bands.
    for (const baseT of [0.285, 0.62, 0.82]) {
      const t = Math.max(0.04, Math.min(0.96, baseT - phase * 0.009));
      const centre = Vector3.Lerp(start, end, t);
      const half = axis.scale(0.032);
      yellow.add(cyl, memberTransform(
        new Vector3(centre.x - half.x, y + 1.11, centre.z - half.z),
        new Vector3(centre.x + half.x, y + 1.11, centre.z + half.z),
        0.074,
      ), 4);
    }

    const stanchions = Math.max(1, Math.round(len / spacing));
    for (let i = 0; i <= stanchions; i++) {
      if ((i + Math.round(phase * 5)) % 3 === 1) continue;
      const t = i / stanchions;
      const p = Vector3.Lerp(start, end, t);
      const wearBottom = y + 0.16 + ((i + Math.round(phase * 7)) % 3) * 0.12;
      const wearLength = 0.22 + ((i + 1) % 2) * 0.15;
      (i % 2 === 0 ? dark : steel).add(cyl, memberTransform(
        new Vector3(p.x, wearBottom, p.z),
        new Vector3(p.x, wearBottom + wearLength, p.z),
        0.081,
      ), 4);
      // Two raised bolt heads on the base plate are tiny in the wide view, but become
      // clear human-scale fittings in the downward/vertigo benchmark.
      for (const side of [-1, 1]) {
        dark.add(cyl, boxTransform(
          p.x + axis.z * side * 0.055,
          y + 0.075,
          p.z - axis.x * side * 0.055,
          0.055,
          0.09,
          0.055,
        ), 2);
      }
    }
  };

  /**
   * A continuous process line assembled from straight tubular sections. Overlapping
   * sections at each corner read as welded elbows at the distances used by the wide
   * camera. Real flange collars hide the joins and, more importantly, give the pipes
   * a believable sense of scale.
   */
  const addPipeRun = (
    acc: MeshAccumulator,
    points: Vector3[],
    diameter: number,
    uvScale = 6,
    addCollars = true,
    surfaceTint: readonly [number, number, number] = [1, 1, 1],
  ): void => {
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i] as Vector3;
      const b = points[i + 1] as Vector3;
      acc.add(cylHi, memberTransform(a, b, diameter), uvScale, surfaceTint);
      if (!addCollars) continue;

      const axis = b.subtract(a).normalize();
      const collarHalfLength = diameter * 0.16;
      for (const t of [0.035, 0.965]) {
        const centre = Vector3.Lerp(a, b, t);
        dark.add(cylHi, memberTransform(
          centre.subtract(axis.scale(collarHalfLength)),
          centre.add(axis.scale(collarHalfLength)),
          diameter * 1.34,
        ), 3);
      }
    }
  };

  /** A handwheel is a tiny detail up close but a powerful human-scale cue in silhouette. */
  const addValveWheel = (
    centre: Vector3,
    radius: number,
    normalAxis: 'x' | 'z' | Vector3 = 'z',
  ): void => {
    const normal = typeof normalAxis === 'string'
      ? (normalAxis === 'z' ? new Vector3(0, 0, 1) : new Vector3(1, 0, 0))
      : normalAxis.clone().normalize();
    const lateral = new Vector3(normal.z, 0, -normal.x).normalize();
    const pointOnWheel = (angle: number): Vector3 => centre
      .add(lateral.scale(Math.cos(angle) * radius))
      .add(new Vector3(0, Math.sin(angle) * radius, 0));
    const segments = 10;
    for (let i = 0; i < segments; i++) {
      const a = (i / segments) * Math.PI * 2;
      const b = ((i + 1) / segments) * Math.PI * 2;
      yellow.add(cyl, memberTransform(
        pointOnWheel(a),
        pointOnWheel(b),
        0.055,
      ), 2);
    }
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      yellow.add(cyl, memberTransform(
        centre,
        pointOnWheel(a),
        0.045,
      ), 2);
    }
    dark.add(cyl, memberTransform(
      centre.subtract(normal.scale(0.22)),
      centre.add(normal.scale(0.22)),
      0.16,
    ), 2);
  };

  // =================================================================================
  // JACKET — four raked legs from below the waterline to the deck.
  // Looking down over the platform edge, these are what read as "thirty storeys".
  // =================================================================================

  /** Leg centreline at height y — interpolates the rake from base spread to deck spread. */
  const legAt = (corner: number, y: number): Vector3 => {
    const c = legCorners[corner] as [number, number];
    const t = Math.max(0, Math.min(1, (y - LEG_BASE_Y) / (MAIN_DECK_Y - LEG_BASE_Y)));
    const radius = LEG_BASE_RADIUS + (LEG_TOP_RADIUS - LEG_BASE_RADIUS) * t;
    return new Vector3(c[0] * radius, y, c[1] * radius);
  };

  for (let i = 0; i < 4; i++) {
    const SEGMENTS = 10;
    for (let s = 0; s < SEGMENTS; s++) {
      const y0 = LEG_BASE_Y + ((MAIN_DECK_Y - LEG_BASE_Y) * s) / SEGMENTS;
      const y1 = LEG_BASE_Y + ((MAIN_DECK_Y - LEG_BASE_Y) * (s + 1)) / SEGMENTS;
      steel.add(cylHi, memberTransform(legAt(i, y0), legAt(i, y1), 4.2), 4);
    }
    // Splash-zone collar where the leg meets the sea — the detail that makes the
    // waterline intersection read as real rather than a cylinder through a plane.
    const splash = legAt(i, 2);
    dark.add(cylHi, boxTransform(splash.x, 1.5, splash.z, 5.4, 9, 5.4), 3);
    yellow.add(cylHi, boxTransform(splash.x, 7.5, splash.z, 5.0, 2.2, 5.0), 2);
  }

  const braceLevels = [2, 20, 40, 60, 78, MAIN_DECK_Y - 3];
  for (const y of braceLevels) {
    for (const [a, b] of [[0, 1], [2, 3], [0, 2], [1, 3]] as const) {
      steel.add(box, memberTransform(legAt(a, y), legAt(b, y), 1.5));
    }
  }
  for (let i = 0; i < braceLevels.length - 1; i++) {
    const yA = braceLevels[i] as number;
    const yB = braceLevels[i + 1] as number;
    const yMid = (yA + yB) / 2;
    for (const [a, b] of [[0, 1], [2, 3], [0, 2], [1, 3]] as const) {
      const midTop = legAt(a, yB).add(legAt(b, yB)).scale(0.5);
      steel.add(box, memberTransform(legAt(a, yA), midTop, 1.05));
      steel.add(box, memberTransform(legAt(b, yA), midTop, 1.05));
      steel.add(box, memberTransform(legAt(a, yMid), legAt(b, yMid), 0.6));
    }
  }
  // Conductor pipes dropping from the cellar deck into the sea.
  for (let i = 0; i < 7; i++) {
    dark.add(cyl, boxTransform(-7 + i * 2.4, (CELLAR_Y + LEG_BASE_Y) / 2, -8, 0.85, CELLAR_Y - LEG_BASE_Y, 0.85), 6);
  }

  // =================================================================================
  // DECK LEVELS
  // =================================================================================
  const addLevel = (
    y: number, minX: number, maxX: number, minZ: number, maxZ: number, acc: MeshAccumulator,
  ): void => {
    const w = maxX - minX;
    const d = maxZ - minZ;
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    acc.add(box, boxTransform(cx, y, cz, w, 0.5, d), 5);
    steel.add(box, boxTransform(cx, y - 1.1, minZ, w, 1.8, 1.0), 3);
    steel.add(box, boxTransform(cx, y - 1.1, maxZ, w, 1.8, 1.0), 3);
    steel.add(box, boxTransform(minX, y - 1.1, cz, 1.0, 1.8, d), 3);
    steel.add(box, boxTransform(maxX, y - 1.1, cz, 1.0, 1.8, d), 3);
    for (let x = minX + 4; x < maxX; x += 5) {
      steel.add(box, boxTransform(x, y - 1.0, cz, 0.5, 1.4, d));
    }
  };

  addLevel(CELLAR_Y, -20, 20, -24, 8, plate);
  addLevel(MAIN_DECK_Y, -19, 24, -22, 9, plate);

  // Observation platform: cantilevered forward so the player is genuinely at the edge.
  const OBS_MIN_X = -11;
  const OBS_MAX_X = 11;
  const OBS_MIN_Z = -6;
  // Right up against the handrail — you are leaning on it, not standing back. The
  // offset is 0.95 rather than 1.15 because Adam moved the eye 20 cm north and the rail
  // holds its old absolute position (4.35): the step forward closes the gap, it does
  // not carry the platform edge with it.
  const OBS_MAX_Z = EYE_Z + 0.95;
  addLevel(DECK_Y, OBS_MIN_X, OBS_MAX_X, OBS_MIN_Z, OBS_MAX_Z, openGrate);
  // Cantilever brackets under the overhang, visible when looking down over the rail.
  for (let x = OBS_MIN_X + 2; x <= OBS_MAX_X - 2; x += 4) {
    steel.add(box, memberTransform(
      new Vector3(x, DECK_Y - 1.6, OBS_MAX_Z - 0.4),
      new Vector3(x, MAIN_DECK_Y + 1.2, OBS_MAX_Z - 6),
      0.55,
    ));
  }

  // Railings: the forward run has a deliberate opening into the starboard service
  // catwalk. In the old layout the catwalk was visually and physically fenced off.
  addRailRun(DECK_Y, OBS_MIN_X, OBS_MAX_Z, 6.9, OBS_MAX_Z);
  addRailRun(DECK_Y, 10.7, OBS_MAX_Z, OBS_MAX_X, OBS_MAX_Z);
  addRailRun(DECK_Y, OBS_MIN_X, OBS_MIN_Z, OBS_MIN_X, OBS_MAX_Z);
  addRailRun(DECK_Y, OBS_MAX_X, OBS_MIN_Z, OBS_MAX_X, OBS_MAX_Z);
  addRailWear(DECK_Y, OBS_MIN_X, OBS_MAX_Z, 6.9, OBS_MAX_Z, 1.6, 0);
  addRailWear(DECK_Y, 10.7, OBS_MAX_Z, OBS_MAX_X, OBS_MAX_Z, 1.6, 1);
  addRailWear(DECK_Y, OBS_MIN_X, OBS_MIN_Z, OBS_MIN_X, OBS_MAX_Z, 1.6, 2);
  addRailWear(DECK_Y, OBS_MAX_X, OBS_MIN_Z, OBS_MAX_X, OBS_MAX_Z, 1.6, 3);
  addRailRun(MAIN_DECK_Y, -19, 9, 24, 9);
  addRailRun(MAIN_DECK_Y, -19, -22, -19, 9);
  addRailRun(MAIN_DECK_Y, 24, -22, 24, 9);
  addRailRun(CELLAR_Y, -20, 8, 20, 8);

  // A narrow grating walk carries the eye from the foreground rail into the process
  // tower. It provides the layered, occupied right-hand framing of the references
  // without putting a solid wall in front of the ocean.
  const CATWALK_MIN_Z = OBS_MAX_Z - 0.2;
  const CATWALK_MAX_Z = 22.5;
  const CATWALK_MIN_X = 7.0;
  const CATWALK_MAX_X = 10.6;
  const catwalkMidZ = (CATWALK_MIN_Z + CATWALK_MAX_Z) / 2;
  openGrate.add(box, boxTransform(
    (CATWALK_MIN_X + CATWALK_MAX_X) / 2,
    DECK_Y - 0.12,
    catwalkMidZ,
    CATWALK_MAX_X - CATWALK_MIN_X,
    0.24,
    CATWALK_MAX_Z - CATWALK_MIN_Z,
  ), 4);
  for (const x of [CATWALK_MIN_X, CATWALK_MAX_X]) {
    steel.add(box, boxTransform(
      x,
      DECK_Y - 0.55,
      catwalkMidZ,
      0.28,
      0.75,
      CATWALK_MAX_Z - CATWALK_MIN_Z,
    ), 3);
    addRailRun(DECK_Y, x, CATWALK_MIN_Z, x, CATWALK_MAX_Z, 1.75);
  }
  for (let z = CATWALK_MIN_Z + 2.5; z < CATWALK_MAX_Z; z += 3.4) {
    steel.add(box, memberTransform(
      new Vector3(CATWALK_MIN_X, DECK_Y - 0.6, z),
      new Vector3(CATWALK_MAX_X, DECK_Y - 2.7, z + 1.3),
      0.24,
    ));
    steel.add(box, memberTransform(
      new Vector3(CATWALK_MAX_X, DECK_Y - 0.6, z),
      new Vector3(CATWALK_MIN_X, DECK_Y - 2.7, z + 1.3),
      0.24,
    ));
  }
  // Local control cabinet, conduit and drain line on the walk: familiar-sized objects
  // keep the surrounding tower from reading as an abstract pile of primitives.
  dark.add(box, boxTransform(9.7, DECK_Y + 0.78, 13.8, 1.15, 1.6, 0.72), 2);
  yellow.add(box, boxTransform(9.7, DECK_Y + 1.62, 13.8, 1.22, 0.09, 0.78), 2);
  dark.add(box, boxTransform(9.72, DECK_Y + 0.84, 13.42, 0.7, 0.78, 0.08), 2);
  addPipeRun(steel, [
    new Vector3(8.05, DECK_Y + 0.22, 7.0),
    new Vector3(8.05, DECK_Y + 0.22, 18.2),
    new Vector3(8.05, DECK_Y + 4.4, 18.2),
  ], 0.24, 5);

  // =================================================================================
  /*
   * THE CRANE TOWER — lowered as one unit.
   *
   * Adam: the tower stopping short of the water makes the sea read as closer than it is,
   * because the eye has nothing continuing down to judge the drop against.
   *
   * Marked and translated rather than edited coordinate by coordinate, for the reason the
   * Power Station established: this span expresses positions as named constants, bare
   * arrays, destructured tuples and inline arguments, and no pattern match catches all
   * four. Applying the offset to finished geometry cannot miss a vertex, and it carries
   * the crane, its jib, the interior and every prop with the structure automatically.
   */
  const CRANE_TOWER_DROP = -18;
  const craneTowerMarks = [steel, plate, yellow, dark, openGrate, machined]
    .map((acc) => [acc, acc.vertexMark] as const);

  // STARBOARD TOWER — the mass that fills the right of frame, running past the top.
  // =================================================================================
  // The tower is deliberately asymmetric in plan. Its left/front process face sits near
  // the portrait frustum while its full lattice continues out through landscape right.
  // That makes it read as a place the player is standing on, not a separate prop parked
  // just outside the camera.
  const towerX = 15;
  const towerZ = 29;
  const halfBase = 8.5;
  const halfTop = 4.5;
  const nearFaceZ = towerZ - halfBase - 0.55;

  const towerHalfAt = (y: number): number => {
    const t = Math.max(0, Math.min(1, (y - MAIN_DECK_Y) / (TOWER_TOP - MAIN_DECK_Y)));
    return halfBase + (halfTop - halfBase) * t;
  };

  const towerCorner = (i: number, y: number): Vector3 => {
    const h = towerHalfAt(y);
    const c = legCorners[i] as [number, number];
    return new Vector3(towerX + c[0] * h, y, towerZ + c[1] * h);
  };

  // Fewer, stronger structural bays survive at wide-view scale. The previous eighteen
  // equal bays turned the hero mass into visually uniform scaffolding.
  const TOWER_BAYS = 11;
  for (let i = 0; i < 4; i++) {
    steel.add(box, memberTransform(towerCorner(i, CELLAR_Y), towerCorner(i, TOWER_TOP), 1.28), 4);
  }
  for (let bay = 0; bay <= TOWER_BAYS; bay++) {
    const y = CELLAR_Y + ((TOWER_TOP - CELLAR_Y) * bay) / TOWER_BAYS;
    for (const [a, b] of [[0, 1], [2, 3], [0, 2], [1, 3]] as const) {
      steel.add(box, memberTransform(towerCorner(a, y), towerCorner(b, y), 0.72));
    }
    if (bay < TOWER_BAYS) {
      const yN = CELLAR_Y + ((TOWER_TOP - CELLAR_Y) * (bay + 1)) / TOWER_BAYS;
      for (const [a, b] of [[0, 1], [2, 3], [0, 2], [1, 3]] as const) {
        if ((bay + a) % 2 === 0) {
          steel.add(box, memberTransform(towerCorner(a, y), towerCorner(b, yN), 0.52));
        } else {
          steel.add(box, memberTransform(towerCorner(b, y), towerCorner(a, yN), 0.52));
        }
      }
    }
  }

  // An asymmetric process spine gives the lattice something heavy to carry. One broad
  // vessel owns the camera-facing corner, a shorter separator sits behind it, and an
  // enclosed service shaft supplies the hard rectangular counter-shape. Unequal heights
  // avoid the old "three organ pipes in a cube" silhouette.
  const processBottom = CELLAR_Y + 1;
  const processTop = TOWER_TOP - 9;
  const processColumns = [
    {
      x: towerX - 6.3,
      z: towerZ - 5.0,
      d: 7.2,
      bottom: processBottom,
      top: processTop,
      // The plate scan's runoff is directional, so reserve it for genuinely upright
      // equipment where those streaks follow gravity instead of turning braces to wood.
      acc: plate,
    },
    {
      x: towerX - 0.8,
      z: towerZ - 5.7,
      d: 4.6,
      bottom: processBottom + 7,
      top: processTop - 17,
      acc: steel,
    },
    {
      x: towerX + 3.2,
      z: towerZ - 4.6,
      d: 3.4,
      bottom: processBottom + 22,
      top: processTop - 7,
      acc: plate,
    },
  ];
  for (let columnIndex = 0; columnIndex < processColumns.length; columnIndex++) {
    const spec = processColumns[columnIndex]!;

    if (columnIndex === 0) {
      // The hero drum passes within metres of the camera. Build its shell as separate
      // fabricated courses over a recessed inner body so its seams create real shadow,
      // rather than relying on a texture across one enormous uninterrupted cylinder.
      dark.add(cylHi, boxTransform(
        spec.x,
        (spec.bottom + spec.top) / 2,
        spec.z,
        spec.d * 0.88,
        spec.top - spec.bottom,
        spec.d * 0.88,
      ), 2.4);
      let courseBottom = spec.bottom;
      for (let seamY = spec.bottom + 4; seamY < spec.top; seamY += 8.8) {
        const courseTop = seamY - 0.28;
        if (courseTop > courseBottom) {
          spec.acc.add(cylHi, boxTransform(
            spec.x,
            (courseBottom + courseTop) / 2,
            spec.z,
            spec.d,
            courseTop - courseBottom,
            spec.d,
          ), 2.4);
        }
        courseBottom = seamY + 0.28;
      }
      if (courseBottom < spec.top) {
        spec.acc.add(cylHi, boxTransform(
          spec.x,
          (courseBottom + spec.top) / 2,
          spec.z,
          spec.d,
          spec.top - courseBottom,
          spec.d,
        ), 2.4);
      }
    } else {
      spec.acc.add(cylHi, boxTransform(
        spec.x,
        (spec.bottom + spec.top) / 2,
        spec.z,
        spec.d,
        spec.top - spec.bottom,
        spec.d,
      ), 2.4);
    }
    for (let y = spec.bottom + 4; y < spec.top; y += 8.8) {
      const bandAcc = columnIndex === 0 ? steel : dark;
      bandAcc.add(cylHi, boxTransform(
        spec.x,
        y,
        spec.z,
        spec.d * 1.13,
        columnIndex === 0 ? 0.52 : 0.34,
        spec.d * 1.13,
      ), 3);
      const supportZ = towerZ - halfBase + 1.0;
      steel.add(box, boxTransform(
        spec.x,
        y - 0.28,
        (spec.z + supportZ) / 2,
        spec.d + 0.9,
        0.24,
        Math.abs(spec.z - supportZ),
      ), 3);
    }
  }

  // Segmented cladding behind the vessels creates dark recesses and an unambiguous core
  // without becoming one featureless monolith.
  const shaftX = towerX + 4.8;
  const shaftZ = towerZ + 0.7;
  const shaftW = 5.8;
  const shaftD = 7.2;
  const shaftSegments = [
    [MAIN_DECK_Y + 3, MAIN_DECK_Y + 19],
    [MAIN_DECK_Y + 21, MAIN_DECK_Y + 38],
    [MAIN_DECK_Y + 40, MAIN_DECK_Y + 57],
    [MAIN_DECK_Y + 59, TOWER_TOP - 7],
  ] as const;
  const shaftLowerRecessZ = shaftZ - shaftD / 2 + 1.05;
  for (let segmentIndex = 0; segmentIndex < shaftSegments.length; segmentIndex++) {
    const [bottom, top] = shaftSegments[segmentIndex]!;
    const segmentHeight = top - bottom;
    if (segmentIndex === 0) {
      // The eye-level shaft is a service bay, not a solid cube. Four deep cladding
      // pieces frame a genuinely recessed equipment wall; the opening gives the lamps,
      // doors and pipe station somewhere physical to belong.
      const jambW = 1.05;
      const lintelH = 1.15;
      dark.add(box, boxTransform(
        shaftX,
        (bottom + top) / 2,
        shaftZ + 0.46,
        shaftW - 0.42,
        segmentHeight - 0.7,
        shaftD - 1.18,
      ), 2.2);
      for (const side of [-1, 1]) {
        plate.add(box, boxTransform(
          shaftX + side * (shaftW - jambW) / 2,
          (bottom + top) / 2,
          shaftZ,
          jambW,
          segmentHeight,
          shaftD,
        ), 2.2);
      }
      for (const y of [bottom + lintelH / 2, top - lintelH / 2]) {
        plate.add(box, boxTransform(
          shaftX,
          y,
          shaftZ,
          shaftW - jambW * 2,
          lintelH,
          shaftD,
        ), 2.2);
      }
      steel.add(box, boxTransform(
        shaftX,
        (bottom + top) / 2,
        shaftLowerRecessZ - 0.15,
        0.18,
        segmentHeight - lintelH * 2.2,
        0.18,
      ), 2);
    } else {
      plate.add(box, boxTransform(
        shaftX,
        (bottom + top) / 2,
        shaftZ,
        shaftW,
        segmentHeight,
        shaftD,
      ), 2.2);
    }
    for (const side of [-1, 1]) {
      steel.add(box, boxTransform(
        shaftX + side * (shaftW / 2 - 0.22),
        (bottom + top) / 2,
        shaftZ - shaftD / 2 - 0.05,
        0.26,
        segmentHeight - 0.5,
        0.22,
      ), 2);
    }
    for (let y = bottom + 3.2; y < top; y += 4.2) {
      steel.add(box, boxTransform(
        shaftX,
        y,
        shaftZ - shaftD / 2 - 0.08,
        shaftW - 0.35,
        0.16,
        0.18,
      ), 2);
    }
  }

  // Five irregularly spaced ring galleries replace seven full square slabs. The open
  // centres reveal the process equipment; projecting front strips and brackets create
  // the deep, stacked balcony rhythm visible in the references.
  const towerLevels = [
    MAIN_DECK_Y + 6,
    MAIN_DECK_Y + 19,
    MAIN_DECK_Y + 35,
    MAIN_DECK_Y + 52,
    MAIN_DECK_Y + 69,
  ];
  for (let level = 0; level < towerLevels.length; level++) {
    const y = towerLevels[level] as number;
    const t = (y - MAIN_DECK_Y) / (TOWER_TOP - MAIN_DECK_Y);
    const h = halfBase + (halfTop - halfBase) * t;
    const outer = h + 1.8;
    const walk = 1.75;
    plate.add(box, boxTransform(towerX, y, towerZ - outer, outer * 2, 0.32, walk), 3);
    plate.add(box, boxTransform(towerX, y, towerZ + outer, outer * 2, 0.32, walk), 3);
    plate.add(box, boxTransform(towerX - outer, y, towerZ, walk, 0.32, outer * 2 - walk * 2), 3);
    plate.add(box, boxTransform(towerX + outer, y, towerZ, walk, 0.32, outer * 2 - walk * 2), 3);
    addRailRun(y, towerX - outer, towerZ - outer - walk / 2, towerX + outer, towerZ - outer - walk / 2);
    addRailRun(y, towerX - outer, towerZ + outer + walk / 2, towerX + outer, towerZ + outer + walk / 2);
    addRailRun(y, towerX - outer - walk / 2, towerZ - outer, towerX - outer - walk / 2, towerZ + outer);
    addRailRun(y, towerX + outer + walk / 2, towerZ - outer, towerX + outer + walk / 2, towerZ + outer);
    for (const xSign of [-0.72, 0, 0.72]) {
      steel.add(box, memberTransform(
        new Vector3(towerX + xSign * outer, y - 0.12, towerZ - outer - walk / 2),
        new Vector3(towerX + xSign * h * 0.72, y - 3.4, towerZ - h + 0.8),
        0.38,
      ));
    }
    if (level === 0) {
      // Keep the eye-level gallery open. A full-width module here previously became a
      // giant flat wall in the hero camera and hid the process vessel and crane base.
      dark.add(box, boxTransform(
        towerX + 4.0,
        y + 2.15,
        towerZ + 2.6,
        4.6,
        3.7,
        4.8,
      ), 2);
      steel.add(box, boxTransform(
        towerX + 4.0,
        y + 2.15,
        towerZ + 0.16,
        3.6,
        2.7,
        0.14,
      ), 2);
    } else if (level % 2 === 0) {
      const moduleW = h * 1.08;
      const moduleD = h * 0.9;
      dark.add(box, boxTransform(towerX + 1.0, y + 2.4, towerZ + 1.5, moduleW, 4.2, moduleD), 2.2);
      // Stiffeners, access doors and louvred faces break the featureless blocks into
      // fabricated modules at a scale people can recognise.
      for (let rib = -2; rib <= 2; rib++) {
        steel.add(box, boxTransform(
          towerX + 1.0 + (rib * moduleW) / 5.2,
          y + 2.4,
          towerZ + 1.5 - moduleD / 2 - 0.06,
          0.13,
          4.0,
          0.13,
        ), 2);
      }
      dark.add(box, boxTransform(
        towerX + 1.0 - moduleW * 0.27,
        y + 2.12,
        towerZ + 1.5 - moduleD / 2 - 0.13,
        1.2,
        2.7,
        0.12,
      ), 2);
      for (let vent = 0; vent < 5; vent++) {
        steel.add(box, boxTransform(
          towerX + 1.0 + moduleW * 0.19,
          y + 1.2 + vent * 0.48,
          towerZ + 1.5 - moduleD / 2 - 0.14,
          moduleW * 0.28,
          0.12,
          0.13,
        ), 2);
      }
    } else {
      // Horizontal separator vessels on the open levels.
      const vesselA = new Vector3(towerX - h * 0.55, y + 2.1, towerZ - 1.6);
      const vesselB = new Vector3(towerX + h * 0.55, y + 2.1, towerZ - 1.6);
      dark.add(cylHi, memberTransform(vesselA, vesselB, 2.05), 5);
      for (const tBand of [0.18, 0.5, 0.82]) {
        const c = Vector3.Lerp(vesselA, vesselB, tBand);
        steel.add(cylHi, memberTransform(
          new Vector3(c.x - 0.16, c.y, c.z),
          new Vector3(c.x + 0.16, c.y, c.z),
          2.28,
        ), 3);
      }
      for (const xSign of [-1, 1]) {
        steel.add(box, boxTransform(
          towerX + xSign * h * 0.31,
          y + 0.95,
          towerZ - 1.6,
          0.46,
          1.8,
          1.55,
        ), 2);
      }
    }
  }

  plate.add(box, boxTransform(towerX, TOWER_TOP, towerZ, halfTop * 2 + 2, 0.3, halfTop * 2 + 2), 3);
  steel.add(cyl, boxTransform(towerX, TOWER_TOP + 6, towerZ, 0.55, 12, 0.55), 2);

  // =================================================================================
  // CRANE — mounted on the exterior front-left face of the tower. The previous pedestal
  // sat inside the lattice and its boom ran through the tower volume, so only the cable
  // survived in the hero view. This arrangement keeps the whole diagonal against sky and
  // water, with the tower providing a heavy counterweight behind it.
  // =================================================================================
  const CRANE_BASE_Y = MAIN_DECK_Y + 1.5;
  const craneX = towerX - 6;
  const craneZ = towerZ - 9.5;

  // Support bracket tying the pedestal back into the tower frame.
  steel.add(box, memberTransform(
    new Vector3(craneX, CRANE_BASE_Y - 1, craneZ),
    new Vector3(towerX - halfBase + 1, CRANE_BASE_Y - 7, towerZ - halfBase + 1), 1.15,
  ), 3);
  steel.add(box, memberTransform(
    new Vector3(craneX, CRANE_BASE_Y - 1, craneZ),
    new Vector3(towerX - halfBase + 1, CRANE_BASE_Y + 3, towerZ - halfBase + 1), 0.9,
  ), 3);
  plate.add(box, boxTransform(craneX, CRANE_BASE_Y - 1.2, craneZ, 8, 0.4, 8), 3);
  addRailRun(CRANE_BASE_Y - 1.0, craneX - 4, craneZ - 4, craneX - 4, craneZ + 4);
  addRailRun(CRANE_BASE_Y - 1.0, craneX - 4, craneZ - 4, craneX + 4, craneZ - 4);

  // The crane sits close enough to touch from the observation deck. Its old pedestal,
  // machinery box and cab were three uninterrupted primitives that filled the close
  // view like blank walls. A segmented slewing column, smaller counterweight housing,
  // framed cab and exposed services now carry the metre-scale detail of a working crane.
  const pedestalTop = CRANE_BASE_Y + 4.8;
  steel.add(cylHi, boxTransform(
    craneX,
    (CRANE_BASE_Y - 1.0 + pedestalTop) / 2,
    craneZ,
    4.35,
    pedestalTop - (CRANE_BASE_Y - 1.0),
    4.35,
  ), 2);
  for (const collarY of [CRANE_BASE_Y - 0.25, CRANE_BASE_Y + 2.1, pedestalTop - 0.18]) {
    dark.add(cylHi, boxTransform(craneX, collarY, craneZ, 4.72, 0.34, 4.72), 3);
  }
  for (let bolt = 0; bolt < 12; bolt++) {
    const angle = (bolt / 12) * Math.PI * 2;
    dark.add(cyl, boxTransform(
      craneX + Math.cos(angle) * 2.18,
      CRANE_BASE_Y - 0.78,
      craneZ + Math.sin(angle) * 2.18,
      0.13,
      0.24,
      0.13,
    ), 2);
  }

  const housingX = craneX + 0.45;
  const housingY = CRANE_BASE_Y + 6.15;
  const housingZ = craneZ + 0.85;
  const housingW = 4.65;
  const housingH = 3.65;
  const housingD = 4.45;
  const housingFrontZ = housingZ - housingD / 2;
  dark.add(box, boxTransform(
    housingX,
    housingY,
    housingZ,
    housingW,
    housingH,
    housingD,
  ), 2);
  plate.add(box, boxTransform(
    housingX,
    housingY + housingH / 2 + 0.13,
    housingZ,
    housingW + 0.34,
    0.22,
    housingD + 0.38,
  ), 2);

  // Fabricated corner seams and a framed service door visibly divide the counterweight.
  for (const x of [housingX - housingW / 2 + 0.13, housingX + housingW / 2 - 0.13]) {
    steel.add(box, boxTransform(x, housingY, housingFrontZ - 0.1, 0.17, housingH - 0.24, 0.18), 2);
  }
  for (const y of [housingY - housingH / 2 + 0.13, housingY + housingH / 2 - 0.13]) {
    steel.add(box, boxTransform(housingX, y, housingFrontZ - 0.1, housingW - 0.25, 0.17, 0.18), 2);
  }
  const serviceDoorX = housingX + 1.05;
  plate.add(box, boxTransform(
    serviceDoorX,
    housingY - 0.16,
    housingFrontZ - 0.17,
    1.55,
    2.72,
    0.16,
  ), 2);
  dark.add(box, boxTransform(
    serviceDoorX,
    housingY - 0.16,
    housingFrontZ - 0.27,
    1.23,
    2.38,
    0.12,
  ), 2);
  steel.add(box, boxTransform(
    serviceDoorX + 0.42,
    housingY - 0.08,
    housingFrontZ - 0.37,
    0.07,
    0.42,
    0.08,
  ), 2);
  for (const hingeY of [housingY - 0.9, housingY + 0.55]) {
    steel.add(cyl, boxTransform(
      serviceDoorX - 0.66,
      hingeY,
      housingFrontZ - 0.34,
      0.11,
      0.24,
      0.11,
    ), 2);
  }

  // Cooling louvres, fan guard and hydraulic manifold occupy the broad front and west
  // faces. They are intentionally large enough to survive both the wide and portrait
  // compositions instead of dissolving into texture noise.
  const louvreX = housingX - 1.05;
  for (let louvre = 0; louvre < 7; louvre++) {
    steel.add(box, boxTransform(
      louvreX,
      housingY - 1.1 + louvre * 0.35,
      housingFrontZ - 0.22,
      1.35,
      0.09,
      0.24,
    ), 2);
  }
  addValveWheel(
    new Vector3(housingX - housingW / 2 - 0.28, housingY + 0.32, housingZ - 0.38),
    0.68,
    'x',
  );
  for (const [zOffset, height] of [[-1.2, 1.15], [-0.55, 1.55], [0.12, 1.28]] as const) {
    const x = housingX - housingW / 2 - 0.35;
    const z = housingZ + zOffset;
    addPipeRun(zOffset === -0.55 ? yellow : steel, [
      new Vector3(x, CRANE_BASE_Y + 0.2, z),
      new Vector3(x, housingY + height, z),
      new Vector3(x + 0.7, housingY + height, z),
    ], zOffset === -0.55 ? 0.16 : 0.13, 6, false);
  }

  // Operator cab: dark recessed glazing surrounded by external mullions, roof lip,
  // lower kick panel, wiper and grab rail. The glazing remains within the existing dark
  // steel batch, preserving the five-material contract.
  const cabX = craneX - 3.05;
  const cabY = CRANE_BASE_Y + 6.2;
  const cabZ = craneZ + 2.15;
  const cabW = 2.25;
  const cabH = 2.9;
  const cabD = 2.35;
  const cabFrontZ = cabZ - cabD / 2;
  dark.add(box, boxTransform(cabX, cabY, cabZ, cabW, cabH, cabD), 2);
  plate.add(box, boxTransform(cabX, cabY - 1.08, cabFrontZ - 0.1, cabW + 0.12, 0.72, 0.18), 2);
  plate.add(box, boxTransform(cabX, cabY + cabH / 2 + 0.12, cabZ, cabW + 0.32, 0.2, cabD + 0.32), 2);
  for (const x of [cabX - cabW / 2 + 0.08, cabX, cabX + cabW / 2 - 0.08]) {
    steel.add(box, boxTransform(x, cabY + 0.15, cabFrontZ - 0.13, 0.12, 2.05, 0.14), 2);
  }
  for (const y of [cabY - 0.48, cabY + 1.16]) {
    steel.add(box, boxTransform(cabX, y, cabFrontZ - 0.13, cabW - 0.12, 0.12, 0.14), 2);
  }
  steel.add(box, memberTransform(
    new Vector3(cabX - 0.55, cabY + 0.92, cabFrontZ - 0.23),
    new Vector3(cabX + 0.48, cabY + 0.24, cabFrontZ - 0.23),
    0.055,
  ), 2);
  addPipeRun(yellow, [
    new Vector3(cabX - cabW / 2 - 0.22, cabY - 0.65, cabFrontZ - 0.18),
    new Vector3(cabX - cabW / 2 - 0.22, cabY + 0.85, cabFrontZ - 0.18),
    new Vector3(cabX - cabW / 2 + 0.35, cabY + 0.85, cabFrontZ - 0.18),
  ], 0.08, 5, false);

  // A narrow ladder and access rungs make the slewing column's height readable.
  const ladderX = craneX - 2.34;
  const ladderZ = craneZ - 0.8;
  for (const x of [ladderX - 0.32, ladderX + 0.32]) {
    steel.add(cyl, boxTransform(
      x,
      CRANE_BASE_Y + 1.3,
      ladderZ,
      0.08,
      4.4,
      0.08,
    ), 2);
  }
  for (let rung = 0; rung < 10; rung++) {
    steel.add(box, boxTransform(
      ladderX,
      CRANE_BASE_Y - 0.65 + rung * 0.45,
      ladderZ - 0.02,
      0.72,
      0.07,
      0.1,
    ), 2);
  }

  // A-frame mast and machinery establish how the boom is actually supported.
  const mastTop = new Vector3(craneX + 1.0, CRANE_BASE_Y + 14.5, craneZ + 1.5);
  for (const xOff of [-1.9, 1.9]) {
    steel.add(box, memberTransform(
      new Vector3(craneX + xOff, CRANE_BASE_Y + 4.0, craneZ + 1.8),
      mastTop,
      0.5,
    ), 3);
  }
  dark.add(cylHi, memberTransform(
    new Vector3(craneX - 1.4, CRANE_BASE_Y + 4.1, craneZ - 1.8),
    new Vector3(craneX + 1.4, CRANE_BASE_Y + 4.1, craneZ - 1.8),
    1.0,
  ), 2);

  // Placement offset for the whole jib, applied at the root so the lattice, pendant
  // stays, hoist fall and hook block all follow from one number. +X is to the player's
  // right as they face the sea, +Y is up.
  const BOOM_OFFSET = new Vector3(5, 10, 3);
  const boomRoot = new Vector3(craneX - 0.5, CRANE_BASE_Y + 3.2, craneZ + 1.5)
    .add(BOOM_OFFSET);
  // The tip runs along the tower's exterior rather than through it. Most of its physical
  // length is depth, while the leftward offset and rise produce the unmistakable screen
  // diagonal shared by the landscape and portrait references.
  // Reach and rise are stated directly instead of being implied by an end point, which is
  // what lets either be changed alone: this jib grew 20% longer WITHOUT any change to its
  // section, because length lives here and gauge lives in the chord offsets below.
  // The horizontal bearing is preserved so the boom still runs clear of the tower face.
  /**
   * Boom gauge — its section, not its length.
   *
   * Separable on purpose, and the comment above already promised it: reach lives in
   * BOOM_REACH, thickness lives in the chord offsets. Thinning the jib to 70% leaves it
   * reaching exactly as far.
   */
  const BOOM_GAUGE = 0.7;
  const BOOM_REACH = 81.6;
  const BOOM_ELEVATION_DEG = 35;
  // Slew, measured at the base. Rotating the BEARING pivots the jib about a vertical
  // axis through its root, so the boom swings like a real crane rather than rotating
  // about its own midpoint (which would drag the heel through the tower).
  const BOOM_YAW_DEG = 6;
  const rawBearing = new Vector3(towerX - 12 - boomRoot.x, 0, towerZ + 13 - boomRoot.z)
    .normalize();
  const boomYaw = (BOOM_YAW_DEG * Math.PI) / 180;
  const boomBearing = new Vector3(
    rawBearing.x * Math.cos(boomYaw) + rawBearing.z * Math.sin(boomYaw),
    0,
    -rawBearing.x * Math.sin(boomYaw) + rawBearing.z * Math.cos(boomYaw),
  );
  const boomElev = (BOOM_ELEVATION_DEG * Math.PI) / 180;
  const boomTip = boomRoot.add(
    new Vector3(
      boomBearing.x * Math.cos(boomElev),
      Math.sin(boomElev),
      boomBearing.z * Math.cos(boomElev),
    ).scale(BOOM_REACH),
  );
  const boomVec = boomTip.subtract(boomRoot);
  const boomLen = boomVec.length();
  const bAxis = boomVec.normalize();
  const bSide = Vector3.Cross(bAxis, new Vector3(0, 1, 0)).normalize().scale(4.8 * BOOM_GAUGE);
  const bUp = Vector3.Cross(bSide.clone().normalize(), bAxis).normalize().scale(3.9 * BOOM_GAUGE);
  const chords = [
    bSide.add(bUp), bSide.scale(-1).add(bUp),
    bSide.add(bUp.scale(-1)), bSide.scale(-1).add(bUp.scale(-1)),
  ];
  for (const off of chords) {
    yellow.add(box, memberTransform(boomRoot.add(off), boomTip.add(off.scale(0.45)), 1.08 * BOOM_GAUGE), 3);
  }
  const BOOM_BAYS = 24;
  for (let i = 0; i < BOOM_BAYS; i++) {
    const p0 = boomRoot.add(bAxis.scale((boomLen * i) / BOOM_BAYS));
    const p1 = boomRoot.add(bAxis.scale((boomLen * (i + 1)) / BOOM_BAYS));
    const s0 = 1 - (i / BOOM_BAYS) * 0.55;
    const s1 = 1 - ((i + 1) / BOOM_BAYS) * 0.55;
    for (let c = 0; c < 4; c++) {
      yellow.add(box, memberTransform(
        p0.add((chords[c] as Vector3).scale(s0)),
        p1.add((chords[(c + 1) % 4] as Vector3).scale(s1)),
        0.51,
      ), 1);
    }
    // Zigzag webs on both visible side planes turn the parallel chords into a crane
    // truss rather than a ladder. Alternate direction by bay, as on a real lattice jib.
    for (const [upper, lower] of [[0, 2], [1, 3]] as const) {
      const from = i % 2 === 0 ? upper : lower;
      const to = i % 2 === 0 ? lower : upper;
      yellow.add(box, memberTransform(
        p0.add((chords[from] as Vector3).scale(s0)),
        p1.add((chords[to] as Vector3).scale(s1)),
        0.45,
      ), 1);
    }
  }
  // Pendant stays back to the A-frame, and the hoist cable with its hook block.
  dark.add(box, memberTransform(
    boomTip, mastTop, 0.34,
  ), 1);
  dark.add(box, memberTransform(
    boomTip,
    new Vector3(craneX - 1.4, CRANE_BASE_Y + 12.4, craneZ - 0.8),
    0.30,
  ), 1);
  const cableLength = 87;
  dark.add(cyl, boxTransform(
    boomTip.x,
    boomTip.y - cableLength / 2,
    boomTip.z,
    0.225,
    cableLength,
    0.225,
  ), 1);
  // Cylindrical hook block, with cheek collars top and bottom so it reads as a machined
  // housing rather than a plain barrel.
  //
  // Every dimension below is expressed through HOOK_SCALE. At 88m from the eye the
  // assembly was reading larger than its distance implied — a scale cue that made the
  // whole jib feel nearer than it is — so the block and hook are halved while the fall
  // length and attachment point stay put. Positions chain downward from blockTop, so
  // shrinking the parts keeps them hanging correctly without moving anything.
  const HOOK_SCALE = 0.5;
  const blockTop = boomTip.y - cableLength;
  const blockH = 5.4 * HOOK_SCALE;
  const blockMid = blockTop - blockH / 2;
  machined.add(cylHi, boxTransform(
    boomTip.x, blockMid, boomTip.z, 3.0 * HOOK_SCALE, blockH, 3.0 * HOOK_SCALE,
  ), 2);
  for (const collarY of [
    blockTop - 0.35 * HOOK_SCALE,
    blockTop - blockH + 0.35 * HOOK_SCALE,
  ]) {
    steel.add(cylHi, boxTransform(
      boomTip.x, collarY, boomTip.z,
      3.35 * HOOK_SCALE, 0.5 * HOOK_SCALE, 3.35 * HOOK_SCALE,
    ), 2);
  }

  // Hook: an industrial lifting hook, not a boat hook. The distinction is section and
  // throat, not shape alone — a crane hook is massively thick at the spine where the load
  // acts, tapers only modestly to a BLUNT point, opens a wide throat, and carries a
  // spring latch across it. The previous version was slender, curled through 222 degrees
  // and ended in a spike, which is precisely a pirate hook.
  const shankTop = blockTop - blockH;
  const shankBottom = shankTop - 2.0 * HOOK_SCALE;
  yellow.add(cyl, memberTransform(
    new Vector3(boomTip.x, shankTop, boomTip.z),
    new Vector3(boomTip.x, shankBottom, boomTip.z),
    1.05 * HOOK_SCALE,
  ), 2);
  // Trunnion nut where the shank enters the block — it swivels there on a real hook.
  machined.add(cylHi, boxTransform(
    boomTip.x, shankTop - 0.3 * HOOK_SCALE, boomTip.z,
    1.85 * HOOK_SCALE, 0.7 * HOOK_SCALE, 1.85 * HOOK_SCALE,
  ), 2);

  // Hook body. The arc is centred ON the shank axis rather than one radius to its right,
  // so the bowl is balanced about the line of pull instead of hanging entirely off one
  // side — that offset was what made the hook sweep so far right. The shank first flares
  // down and slightly LEFT into the start of the bend, as a forged hook does, then the
  // throat curves under and back up to a tip roughly level with where it began.
  const HOOK_RADIUS = 1.6 * HOOK_SCALE;
  const HOOK_SEGMENTS = 14;
  const hookCentreY = shankBottom - 1.3 * HOOK_SCALE;
  const hookStart = (170 * Math.PI) / 180;
  const hookEnd = (370 * Math.PI) / 180;

  const arcPoint = (angle: number): Vector3 => new Vector3(
    boomTip.x + Math.cos(angle) * HOOK_RADIUS,
    hookCentreY + Math.sin(angle) * HOOK_RADIUS,
    boomTip.z,
  );

  // Flare from the shank into the bend — down and to the left.
  yellow.add(cyl, memberTransform(
    new Vector3(boomTip.x, shankBottom, boomTip.z),
    arcPoint(hookStart),
    1.12 * HOOK_SCALE,
  ), 2);

  let hookTipPos = arcPoint(hookStart);
  for (let i = 0; i < HOOK_SEGMENTS; i++) {
    const t0 = hookStart + ((hookEnd - hookStart) * i) / HOOK_SEGMENTS;
    const t1 = hookStart + ((hookEnd - hookStart) * (i + 1)) / HOOK_SEGMENTS;
    // Heavy through the loaded spine at the bottom of the bowl, easing to a blunt point.
    const along = i / (HOOK_SEGMENTS - 1);
    const section = (1.18 - 0.46 * along * along) * HOOK_SCALE;
    yellow.add(cyl, memberTransform(arcPoint(t0), arcPoint(t1), section), 2);
    hookTipPos = arcPoint(t1);
  }
  yellow.add(cylHi, boxTransform(
    hookTipPos.x, hookTipPos.y, hookTipPos.z,
    0.8 * HOOK_SCALE, 0.5 * HOOK_SCALE, 0.8 * HOOK_SCALE,
  ), 2);

  // Throat left open — no safety latch.

  // =================================================================================
  /*
   * End of the Crane Tower's marked span.
   *
   * Ends HERE, not at the detail pass. The span originally ran that far and swept up the
   * port accommodation block with it, so the Barracks sank 18 m with the tower and read
   * as deleted. A mark/translate range is only as good as its end anchor: it must close
   * where the structure does, not at the next convenient comment.
   */
  for (const [acc, mark] of craneTowerMarks) {
    acc.translateFrom(mark, 0, CRANE_TOWER_DROP, 0);
  }

  /*
   * CRANE TOWER LOWER SUPPORT — the tower now carries its load all the way into the sea.
   *
   * This is deliberately built after the marked span has moved. The existing tower keeps
   * its authored local coordinates, while this continuation is expressed once in final
   * world space: no second translation and no risk of dragging the Barracks down with it.
   *
   * The frame stays vertical rather than copying the main jacket's rake. It is an annex
   * to that jacket, not a second platform: four continuous posts, open ring beams and one
   * alternating diagonal per face leave the ocean visible through it. Every member is an
   * unbevelled box even at desktop quality. At this distance a rounded edge cannot be
   * seen, and the saved triangles matter more than decorative geometry.
   */
  const CRANE_SUPPORT_TOP_Y = CELLAR_Y + CRANE_TOWER_DROP;
  const craneSupportCorner = (i: number, y: number): Vector3 => {
    const c = legCorners[i] as [number, number];
    return new Vector3(towerX + c[0] * halfBase, y, towerZ + c[1] * halfBase);
  };
  const addCraneSupportMember = (
    a: Vector3,
    b: Vector3,
    thickness: number,
  ): void => {
    steel.add(box, memberTransform(a, b, thickness), 3, [1, 1, 1], false);
  };

  for (let i = 0; i < 4; i++) {
    addCraneSupportMember(
      craneSupportCorner(i, LEG_BASE_Y),
      craneSupportCorner(i, CRANE_SUPPORT_TOP_Y),
      1.28,
    );
  }

  const craneSupportLevels = [LEG_BASE_Y, 2, 20, 40, 60, CRANE_SUPPORT_TOP_Y];
  const craneSupportFaces = [[0, 1], [2, 3], [0, 2], [1, 3]] as const;
  for (let level = 0; level < craneSupportLevels.length; level++) {
    const y = craneSupportLevels[level] as number;
    for (const [a, b] of craneSupportFaces) {
      addCraneSupportMember(
        craneSupportCorner(a, y),
        craneSupportCorner(b, y),
        0.72,
      );
    }
    if (level >= craneSupportLevels.length - 1) continue;

    const yNext = craneSupportLevels[level + 1] as number;
    for (const [face, [a, b]] of craneSupportFaces.entries()) {
      const low = (level + face) % 2 === 0 ? a : b;
      const high = low === a ? b : a;
      addCraneSupportMember(
        craneSupportCorner(low, y),
        craneSupportCorner(high, yNext),
        0.62,
      );
    }
  }

  // Compact sacrificial jackets make the waterline intentional without repeating the
  // main platform's much larger yellow leg bands.
  for (let i = 0; i < 4; i++) {
    const p = craneSupportCorner(i, 1.5);
    dark.add(box, boxTransform(p.x, p.y, p.z, 1.72, 7, 1.72), 3, [1, 1, 1], false);
  }

  // Port-side accommodation blocks and pipe runs.
  // =================================================================================
  /*
   * The Barracks, its twin conjoined to the west, and a third stacked on that twin.
   *
   * Built as a list of offsets rather than three copied blocks so they stay identical: the
   * group reads as one accommodation wing precisely because nothing distinguishes the
   * units, and duplicated literals would drift the first time any of them is touched.
   * Spacing is exactly one block-width across and one storey up, so walls and floors meet
   * with no seam and no overlap.
   *
   * The flare boom that used to spear out of here is gone at Adam's direction. It read as
   * a crane it was not, and from the Bridge it drew the eye off the platform and out to
   * an empty corner of the sea.
   */
  const BARRACKS_WIDTH = 12;
  const BARRACKS_STOREY = 12;
  for (const [dx, dy] of [
    [0, 0],                             // the Barracks itself
    [-BARRACKS_WIDTH, 0],               // its twin, conjoined to the west
    [-BARRACKS_WIDTH, BARRACKS_STOREY], // and a third stacked on the twin
  ] as const) {
    dark.add(box, boxTransform(
      -13 + dx, MAIN_DECK_Y + 6 + dy, -14, BARRACKS_WIDTH, 12, 14,
    ), 4);
    plate.add(box, boxTransform(
      -13 + dx, MAIN_DECK_Y + 12.2 + dy, -14, 13, 0.4, 15,
    ), 4);
    addRailRun(MAIN_DECK_Y + 12.4 + dy, -19.5 + dx, -6.5, -6.5 + dx, -6.5);
    for (let i = 0; i < 3; i++) {
      yellow.add(box, boxTransform(
        -13 + dx, MAIN_DECK_Y + 13.5 + dy, -20 + i * 5, 11, 0.35, 0.35,
      ), 2);
    }

  }

  for (let i = 0; i < 6; i++) {
    yellow.add(cyl, Matrix.Scaling(0.6, 40, 0.6)
      .multiply(Matrix.RotationZ(Math.PI / 2))
      .multiply(Matrix.Translation(2, MAIN_DECK_Y + 1.4 + (i % 2) * 0.8, -18 + i * 2.6)), 5);
  }


  // =================================================================================
  // DETAIL PASS — stairs, pipe risers, cable trays, equipment and vents.
  // A structure this size reads as a toy without the small stuff; the reference is
  // dense with stair flights, handrails and pipework at every level.
  // =================================================================================

  /** Zigzag stair flight with stringers, treads and a handrail on the open side. */
  const addStairFlight = (
    x: number, zTop: number, yTop: number, yBottom: number, run: number, width = 1.5,
  ): void => {
    const rise = yTop - yBottom;
    const steps = Math.max(4, Math.round(rise / 0.22));
    const zBottom = zTop + run;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      plate.add(box, boxTransform(
        x, yTop - rise * t, zTop + run * t, width, 0.07, 0.34,
      ), 1, [1, 1, 1], false);
    }
    // Stringers either side.
    for (const off of [-width / 2, width / 2]) {
      steel.add(box, memberTransform(
        new Vector3(x + off, yTop + 0.1, zTop),
        new Vector3(x + off, yBottom + 0.1, zBottom), 0.14,
      ));
      // Handrail, one metre above the stringer line.
      yellow.add(box, memberTransform(
        new Vector3(x + off, yTop + 1.05, zTop),
        new Vector3(x + off, yBottom + 1.05, zBottom), 0.07,
      ));
      for (let i = 0; i <= 3; i++) {
        const t = i / 3;
        yellow.add(box, boxTransform(
          x + off, yTop - rise * t + 0.55, zTop + run * t, 0.06, 1.0, 0.06,
        ));
      }
    }
  };

  /**
   * A face-on industrial stair. The ordinary stair helper runs in depth, which is
   * structurally useful but collapses into a vertical line from the hero camera. These
   * switchback flights run across the tower facade so their steps, stringers and handrails
   * remain readable as a human access route.
   */
  const addFacadeStairFlight = (
    xTop: number,
    xBottom: number,
    z: number,
    yTop: number,
    yBottom: number,
    width = 1.35,
  ): void => {
    const rise = yTop - yBottom;
    const steps = Math.max(6, Math.round(rise / 0.24));
    const treadRun = Math.abs(xTop - xBottom) / steps + 0.14;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      plate.add(box, boxTransform(
        xTop + (xBottom - xTop) * t,
        yTop - rise * t,
        z,
        treadRun,
        0.075,
        width,
      ), 1, [1, 1, 1], false);
    }
    for (const zOff of [-width / 2, width / 2]) {
      steel.add(box, memberTransform(
        new Vector3(xTop, yTop + 0.08, z + zOff),
        new Vector3(xBottom, yBottom + 0.08, z + zOff),
        0.14,
      ));
      yellow.add(cyl, memberTransform(
        new Vector3(xTop, yTop + 1.02, z + zOff),
        new Vector3(xBottom, yBottom + 1.02, z + zOff),
        0.065,
      ), 3);
      for (let i = 0; i <= 4; i++) {
        const t = i / 4;
        yellow.add(cyl, boxTransform(
          xTop + (xBottom - xTop) * t,
          yTop - rise * t + 0.53,
          z + zOff,
          0.06,
          1.0,
          0.06,
        ), 2);
      }
    }
  };

  // Stairs linking the observation platform down to the main deck, and on down to cellar.
  addStairFlight(-8.5, OBS_MIN_Z - 0.5, DECK_Y, MAIN_DECK_Y, 6.5);
  addStairFlight(-14, -8, MAIN_DECK_Y, CELLAR_Y, 7.5);
  addStairFlight(19, -4, MAIN_DECK_Y, CELLAR_Y, 7.5);
  // Landing platforms at the bottom of each flight.
  plate.add(box, boxTransform(-8.5, MAIN_DECK_Y, OBS_MIN_Z + 6.5, 3.0, 0.3, 3.0), 2);
  plate.add(box, boxTransform(-14, CELLAR_Y, -0.5, 3.0, 0.3, 3.0), 2);

  /** Caged ladder — the vertical access runs between walkway levels. */
  const addCagedLadder = (x: number, z: number, yBottom: number, yTop: number): void => {
    for (const off of [-0.28, 0.28]) {
      steel.add(box, memberTransform(
        new Vector3(x + off, yBottom, z), new Vector3(x + off, yTop, z), 0.07,
      ));
    }
    for (let y = yBottom; y < yTop; y += 0.3) {
      steel.add(box, boxTransform(x, y, z, 0.62, 0.045, 0.045));
    }
    // Hoops of the safety cage.
    for (let y = yBottom + 2.2; y < yTop; y += 0.85) {
      steel.add(box, boxTransform(x, y, z + 0.38, 0.9, 0.05, 0.05));
      steel.add(box, boxTransform(x - 0.45, y, z + 0.19, 0.05, 0.05, 0.42));
      steel.add(box, boxTransform(x + 0.45, y, z + 0.19, 0.05, 0.05, 0.42));
    }
  };

  addCagedLadder(9.5, OBS_MIN_Z - 1, MAIN_DECK_Y, DECK_Y);
  addCagedLadder(towerX - 9.5, towerZ - 8, (MAIN_DECK_Y + CRANE_TOWER_DROP), (MAIN_DECK_Y + CRANE_TOWER_DROP) + 24);
  // Staggered access continues all the way up the near-left edge. Breaking the run at
  // landings is both safer in reality and visually richer than one implausible ladder.
  addCagedLadder(towerX - 7.2, towerZ - 7.5, (MAIN_DECK_Y + CRANE_TOWER_DROP) + 24, (MAIN_DECK_Y + CRANE_TOWER_DROP) + 50);
  addCagedLadder(towerX - 5.4, towerZ - 5.8, (MAIN_DECK_Y + CRANE_TOWER_DROP) + 50, (TOWER_TOP + CRANE_TOWER_DROP) - 3);

  // Real stair flights between the first process levels replace the floating decorative
  // treads as the dominant access silhouette near the camera.
  addStairFlight(towerX - 6.4, towerZ - 8.3, (MAIN_DECK_Y + CRANE_TOWER_DROP) + 15.1, (MAIN_DECK_Y + CRANE_TOWER_DROP) + 6.0, 6.0, 1.8);
  addStairFlight(towerX - 3.0, towerZ - 7.1, (MAIN_DECK_Y + CRANE_TOWER_DROP) + 24.2, (MAIN_DECK_Y + CRANE_TOWER_DROP) + 15.1, 6.0, 1.8);
  addStairFlight(towerX - 5.2, towerZ - 6.2, (MAIN_DECK_Y + CRANE_TOWER_DROP) + 33.3, (MAIN_DECK_Y + CRANE_TOWER_DROP) + 24.2, 5.6, 1.65);

  /** Bundle of process pipes running vertically, with support clamps. */
  const addPipeRiser = (x: number, z: number, yBottom: number, yTop: number, count: number): void => {
    for (let i = 0; i < count; i++) {
      const ox = (i - (count - 1) / 2) * 0.7;
      const d = i % 2 === 0 ? 0.5 : 0.34;
      // Process pipework is mostly bare or coated steel. Safety yellow is reserved for
      // the one explicit service line and handrails; repeated yellow risers looked like
      // bamboo poles and competed with the crane silhouette.
      const acc = i % 4 === 0 ? dark : steel;
      acc.add(cyl, boxTransform(x + ox, (yBottom + yTop) / 2, z, d, yTop - yBottom, d), 8);
    }
    for (let y = yBottom + 3; y < yTop; y += 5.5) {
      steel.add(box, boxTransform(x, y, z, count * 0.75, 0.22, 0.5));
    }
  };

  addPipeRiser(towerX - 6.8, nearFaceZ - 0.25, CELLAR_Y, MAIN_DECK_Y + 34, 5);
  addPipeRiser(towerX - 3.7, nearFaceZ - 0.45, MAIN_DECK_Y - 4, TOWER_TOP - 15, 4);
  addPipeRiser(towerX + 4.8, nearFaceZ - 0.35, CELLAR_Y, MAIN_DECK_Y + 42, 3);
  addPipeRiser(-6, -18, CELLAR_Y, MAIN_DECK_Y + 2, 4);
  addPipeRiser(6.5, -20, CELLAR_Y - 6, MAIN_DECK_Y + 1, 3);

  // Large process loops connect the catwalk service line to the tower manifold, then
  // turn across the near face. Unequal diameters and elevations avoid the "organ pipes"
  // regularity of the earlier risers.
  addPipeRun(yellow, [
    new Vector3(9.55, DECK_Y + 2.0, 17.5),
    new Vector3(9.55, DECK_Y + 2.0, nearFaceZ - 0.65),
    new Vector3(towerX - 5.0, DECK_Y + 2.0, nearFaceZ - 0.65),
    new Vector3(towerX - 5.0, MAIN_DECK_Y + 48, nearFaceZ - 0.65),
  ], 0.48, 7);
  addPipeRun(steel, [
    new Vector3(towerX - 6.0, MAIN_DECK_Y + 8.0, towerZ - 8.5),
    new Vector3(towerX - 6.0, MAIN_DECK_Y + 27.5, towerZ - 8.5),
    new Vector3(towerX + 5.8, MAIN_DECK_Y + 27.5, towerZ - 8.5),
    new Vector3(towerX + 5.8, MAIN_DECK_Y + 35.0, towerZ - 8.5),
  ], 0.78, 7);
  addPipeRun(dark, [
    new Vector3(towerX - 4.4, MAIN_DECK_Y + 5.0, towerZ - 9.0),
    new Vector3(towerX - 4.4, MAIN_DECK_Y + 19.2, towerZ - 9.0),
    new Vector3(towerX + 4.1, MAIN_DECK_Y + 19.2, towerZ - 9.0),
  ], 0.52, 7);
  addPipeRun(steel, [
    new Vector3(towerX - 2.4, MAIN_DECK_Y + 3.0, towerZ - 9.25),
    new Vector3(towerX - 2.4, MAIN_DECK_Y + 14.4, towerZ - 9.25),
    new Vector3(towerX + 6.3, MAIN_DECK_Y + 14.4, towerZ - 9.25),
  ], 0.33, 8);
  addValveWheel(new Vector3(towerX - 4.4, MAIN_DECK_Y + 13.2, towerZ - 9.35), 0.72);
  addValveWheel(new Vector3(towerX + 1.8, MAIN_DECK_Y + 19.2, towerZ - 9.35), 0.58);

  // A compact pump skid under the manifold gives the pipes somewhere credible to go.
  plate.add(box, boxTransform(
    towerX + 1.2,
    MAIN_DECK_Y + 0.35,
    towerZ - 8.0,
    8.4,
    0.32,
    3.4,
  ), 3);
  for (const x of [towerX - 1.3, towerX + 1.2, towerX + 3.7]) {
    dark.add(cylHi, memberTransform(
      new Vector3(x - 0.9, MAIN_DECK_Y + 1.25, towerZ - 8.0),
      new Vector3(x + 0.9, MAIN_DECK_Y + 1.25, towerZ - 8.0),
      1.35,
    ), 4);
    steel.add(box, boxTransform(x, MAIN_DECK_Y + 0.72, towerZ - 8.0, 1.15, 0.9, 1.7), 3);
  }

  /** Cable tray — a shallow perforated channel on brackets. */
  const addCableTray = (
    x0: number, z0: number, x1: number, z1: number, y: number,
  ): void => {
    const a = new Vector3(x0, y, z0);
    const b = new Vector3(x1, y, z1);
    dark.add(box, memberTransform(a, b, 0.42), 4);
    const len = Math.hypot(x1 - x0, z1 - z0);
    const n = Math.max(2, Math.round(len / 3));
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      steel.add(box, boxTransform(x0 + (x1 - x0) * t, y - 0.35, z0 + (z1 - z0) * t, 0.1, 0.55, 0.1));
    }
  };

  addCableTray(-18, 7, 22, 7, MAIN_DECK_Y + 2.6);
  addCableTray(-18, -20, -18, 7, MAIN_DECK_Y + 2.6);
  addCableTray(towerX - 7, towerZ - 6, towerX - 7, towerZ + 8, MAIN_DECK_Y + 12);
  addCableTray(
    CATWALK_MAX_X - 0.45,
    CATWALK_MIN_Z,
    CATWALK_MAX_X - 0.45,
    CATWALK_MAX_Z,
    DECK_Y - 0.55,
  );
  addCableTray(
    towerX - 7.6,
    towerZ - 7.4,
    towerX - 7.6,
    towerZ + 7.2,
    MAIN_DECK_Y + 38,
  );

  // Equipment: skids, containers and winches scattered over the main deck.
  const deckKit: [number, number, number, number, number][] = [
    [-8, -16, 3.4, 2.6, 6.5],
    [-2, -19, 2.6, 2.4, 2.6],
    [8, -17, 5.0, 2.8, 3.2],
    [16, -12, 3.0, 2.2, 3.0],
    [20, 2, 2.4, 2.0, 4.5],
    [-16, 2, 2.8, 2.4, 2.8],
  ];
  for (const [x, z, w, h, d] of deckKit) {
    dark.add(box, boxTransform(x, MAIN_DECK_Y + h / 2 + 0.3, z, w, h, d), 3);
    yellow.add(box, boxTransform(x, MAIN_DECK_Y + h + 0.35, z, w + 0.15, 0.14, d + 0.15), 2);
  }
  // A couple of bright shipping containers for colour relief.
  yellow.add(box, boxTransform(-11, MAIN_DECK_Y + 1.6, 4, 6.1, 2.6, 2.5), 3);
  dark.add(box, boxTransform(-11, MAIN_DECK_Y + 3.0, 4, 6.2, 0.18, 2.6), 2);

  // Vent stacks and small ducting on the cellar deck.
  for (let i = 0; i < 5; i++) {
    const x = -14 + i * 6.5;
    steel.add(cyl, boxTransform(x, CELLAR_Y + 2.4, -20, 1.5, 4.4, 1.5), 2);
    dark.add(cyl, boxTransform(x, CELLAR_Y + 4.8, -20, 1.75, 0.7, 1.75), 1);
  }

  // Lifeboat davits on the port side — unmistakably offshore.
  for (const lz of [-4, -11]) {
    steel.add(box, memberTransform(
      new Vector3(-19.5, MAIN_DECK_Y, lz), new Vector3(-24.5, MAIN_DECK_Y + 5.5, lz), 0.45,
    ));
    dark.add(box, boxTransform(-24, MAIN_DECK_Y + 1.4, lz, 3.0, 2.0, 6.5), 2);
    yellow.add(box, boxTransform(-24, MAIN_DECK_Y + 2.5, lz, 3.1, 0.5, 6.6), 2);
  }

  // =================================================================================
  // FULL-ARC SERVICE WINGS — inhabited structure at both free-look limits.
  //
  // The hero bearing already opens correctly onto the Atlantic, but turning hard left
  // or right previously exposed two enormous empty observation-deck slabs. Offshore
  // platforms do not end in unused ballroom-sized floors: accommodation access, pipe
  // racks, separator skids and maintenance galleries occupy those protected side arcs.
  // These wings sit behind the hero camera's open-ocean cone, so they add place and scale
  // without closing the reference composition.
  // =================================================================================

  // PORT ACCOMMODATION FACE — three working storeys on the former blank east wall.
  const accommodationFaceX = -6.82;
  const accommodationZMin = -20.3;
  const accommodationZMax = -7.7;
  const accommodationMidZ = (accommodationZMin + accommodationZMax) / 2;
  const accommodationGalleryX = -5.62;
  const accommodationLevels = [
    MAIN_DECK_Y + 3.7,
    MAIN_DECK_Y + 8.1,
    MAIN_DECK_Y + 12.35,
  ];

  for (let level = 0; level < accommodationLevels.length; level++) {
    const y = accommodationLevels[level] as number;
    plate.add(box, boxTransform(
      (accommodationFaceX + accommodationGalleryX) / 2,
      y,
      accommodationMidZ,
      accommodationGalleryX - accommodationFaceX,
      0.24,
      accommodationZMax - accommodationZMin,
    ), 3);
    addRailRun(y, accommodationGalleryX, accommodationZMin, accommodationGalleryX, accommodationZMax, 1.45);
    addRailRun(y, accommodationGalleryX, accommodationZMin, accommodationFaceX, accommodationZMin, 1.15);
    addRailRun(y, accommodationGalleryX, accommodationZMax, accommodationFaceX, accommodationZMax, 1.15);

    for (const z of [accommodationZMin + 1.0, accommodationMidZ, accommodationZMax - 1.0]) {
      steel.add(box, memberTransform(
        new Vector3(accommodationGalleryX, y - 0.18, z),
        new Vector3(accommodationFaceX - 0.22, y - 2.15, z),
        0.22,
      ));
    }
  }

  // Deep panel joints, doors and unequal louvre banks make the wall read as fabricated
  // rooms instead of one shader-covered cube.
  for (const z of [-19.3, -16.0, -12.1, -8.8]) {
    steel.add(box, boxTransform(
      accommodationFaceX + 0.06,
      MAIN_DECK_Y + 6.0,
      z,
      0.16,
      11.45,
      0.16,
    ), 2);
  }
  for (const y of [MAIN_DECK_Y + 3.1, MAIN_DECK_Y + 7.25, MAIN_DECK_Y + 11.25]) {
    steel.add(box, boxTransform(
      accommodationFaceX + 0.07,
      y,
      accommodationMidZ,
      0.17,
      0.17,
      accommodationZMax - accommodationZMin - 0.35,
    ), 2);
  }
  for (const [z, y] of [
    [-18.0, MAIN_DECK_Y + 1.65],
    [-9.65, MAIN_DECK_Y + 5.65],
  ] as const) {
    plate.add(box, boxTransform(
      accommodationFaceX + 0.14,
      y,
      z,
      0.18,
      2.85,
      1.55,
    ), 2);
    dark.add(box, boxTransform(
      accommodationFaceX + 0.25,
      y,
      z,
      0.12,
      2.48,
      1.2,
    ), 2);
    steel.add(box, boxTransform(
      accommodationFaceX + 0.34,
      y,
      z + 0.42,
      0.1,
      0.42,
      0.08,
    ), 2);
  }
  for (let louvre = 0; louvre < 7; louvre++) {
    plate.add(box, boxTransform(
      accommodationFaceX + 0.23,
      MAIN_DECK_Y + 8.8 + louvre * 0.36,
      -14.0,
      0.2,
      0.1,
      2.45,
    ), 2);
  }

  // Two switchback stair flights remain face-on from the left-limit camera.
  const accommodationLandingY = MAIN_DECK_Y + 5.9;
  addStairFlight(
    accommodationGalleryX,
    -18.9,
    accommodationLandingY,
    MAIN_DECK_Y + 1.15,
    5.0,
    1.45,
  );
  plate.add(box, boxTransform(
    accommodationGalleryX,
    accommodationLandingY,
    -13.9,
    2.3,
    0.24,
    2.0,
  ), 3);
  addRailRun(
    accommodationLandingY,
    accommodationGalleryX - 1.15,
    -12.9,
    accommodationGalleryX + 1.15,
    -12.9,
    1.15,
  );
  addStairFlight(
    accommodationGalleryX,
    -8.9,
    MAIN_DECK_Y + 10.75,
    accommodationLandingY,
    -5.0,
    1.45,
  );

  // External utilities and roof machinery add vertical development above eye level.
  for (const [z, diameter, top] of [
    [-19.15, 0.42, MAIN_DECK_Y + 15.7],
    [-17.9, 0.28, MAIN_DECK_Y + 13.7],
    [-8.6, 0.34, MAIN_DECK_Y + 16.4],
  ] as const) {
    steel.add(cyl, boxTransform(
      accommodationFaceX + 0.52,
      (MAIN_DECK_Y + 1.0 + top) / 2,
      z,
      diameter,
      top - (MAIN_DECK_Y + 1.0),
      diameter,
    ), 7);
    for (let y = MAIN_DECK_Y + 3.0; y < top; y += 3.1) {
      dark.add(box, boxTransform(
        accommodationFaceX + 0.5,
        y,
        z,
        0.5,
        0.12,
        diameter + 0.28,
      ), 2);
    }
  }
  for (const z of [-18.0, -13.8, -9.4]) {
    dark.add(cylHi, boxTransform(
      -11.6,
      MAIN_DECK_Y + 15.0,
      z,
      1.3,
      5.0,
      1.3,
    ), 3);
    steel.add(cylHi, boxTransform(
      -11.6,
      MAIN_DECK_Y + 17.55,
      z,
      1.75,
      0.28,
      1.75,
    ), 2);
  }
  addCableTray(
    accommodationGalleryX - 0.2,
    accommodationZMin,
    accommodationGalleryX - 0.2,
    accommodationZMax,
    MAIN_DECK_Y + 11.2,
  );

  // PORT ROOFTOP PROCESS STACK — the accommodation roof used to end in three lonely
  // exhausts and a large patch of sky. This open, three-level plant keeps the outline
  // porous and industrial while giving the player an unmistakable continuation of the
  // rig above the roof. It stays entirely over the accommodation block, away from both
  // protected openings beside the scrubber train and tower bridge.
  const rooftopXMin = -19.15;
  const rooftopXMax = -7.05;
  const rooftopZMin = -20.45;
  const rooftopZMax = -7.55;
  const rooftopBaseY = MAIN_DECK_Y + 12.42;
  const rooftopLevels = [
    rooftopBaseY,
    rooftopBaseY + 5.0,
    rooftopBaseY + 10.0,
    rooftopBaseY + 15.2,
  ];

  // Primary load frame: four corner columns, intermediate columns, edge beams and
  // alternating braces. Unequal bay spacing avoids the sterile "empty cage" rhythm.
  for (const x of [rooftopXMin, -14.75, -10.7, rooftopXMax]) {
    for (const z of [rooftopZMin, rooftopZMax]) {
      steel.add(box, memberTransform(
        new Vector3(x, rooftopBaseY, z),
        new Vector3(x, rooftopLevels[3] as number, z),
        0.46,
      ), 3);
    }
  }
  /*
   * Dark blue profiled cladding on the rooftop stack's southern face.
   *
   * The same sheet as the Power Station's western wall, shared through CLAD_BLUE_SHEET so
   * the two cannot drift apart. This structure sits on the eastern Barracks roof and is
   * the one mass on that side with a clear south elevation, which is why the cladding
   * lands here rather than on the accommodation blocks below — those read better as bare
   * painted steel, and clad they buried their own stair towers.
   */
  {
    const cladBottom = rooftopLevels[0] as number;
    const cladTop = rooftopLevels[3] as number;
    const cladMidY = (cladBottom + cladTop) / 2;
    const cladWidth = (rooftopXMax - rooftopXMin) - 1.2;
    const cladMidX = (rooftopXMin + rooftopXMax) / 2;
    const cladZ = rooftopZMin;

    plate.add(box, boxTransform(
      cladMidX, cladMidY, cladZ - 0.26, cladWidth, cladTop - cladBottom, 0.34,
    ), 3, CLAD_BLUE_SHEET);

    // Fixed rib pitch, so the profile keeps its physical size whatever the wall's width.
    const ribs = Math.max(2, Math.floor(cladWidth / 1.15));
    for (let i = 0; i < ribs; i++) {
      plate.add(box, boxTransform(
        cladMidX - cladWidth / 2 + ((i + 0.5) * cladWidth) / ribs,
        cladMidY, cladZ - 0.52, 0.46, cladTop - cladBottom, 0.3,
      ), 3, CLAD_BLUE_RIB);
    }
    for (const y of [cladBottom + 0.35, cladTop - 0.35]) {
      steel.add(box, boxTransform(cladMidX, y, cladZ - 0.5, cladWidth, 0.42, 0.42), 4);
    }
  }

  for (const y of rooftopLevels.slice(1)) {
    for (const z of [rooftopZMin, rooftopZMax]) {
      steel.add(box, memberTransform(
        new Vector3(rooftopXMin, y, z),
        new Vector3(rooftopXMax, y, z),
        0.42,
      ), 3);
    }
    for (const x of [rooftopXMin, rooftopXMax]) {
      steel.add(box, memberTransform(
        new Vector3(x, y, rooftopZMin),
        new Vector3(x, y, rooftopZMax),
        0.42,
      ), 3);
    }
  }
  for (const z of [rooftopZMin, rooftopZMax]) {
    for (const [x0, x1, reverse] of [
      [rooftopXMin, -14.75, false],
      [-14.75, -10.7, true],
      [-10.7, rooftopXMax, false],
    ] as const) {
      for (let level = 0; level < rooftopLevels.length - 1; level++) {
        const y0 = rooftopLevels[level] as number;
        const y1 = rooftopLevels[level + 1] as number;
        steel.add(box, memberTransform(
          new Vector3(reverse ? x1 : x0, y0 + 0.18, z),
          new Vector3(reverse ? x0 : x1, y1 - 0.18, z),
          0.26,
        ), 3);
      }
    }
  }

  // Three partial service decks leave shafts for pipes and exhausts instead of reading
  // as one solid block. Their exposed edges carry approved yellow access rails.
  for (const [y, x0, x1, z0, z1] of [
    [rooftopBaseY + 4.95, rooftopXMin, rooftopXMax, rooftopZMin, -11.0],
    [rooftopBaseY + 9.95, rooftopXMin + 1.0, rooftopXMax, -18.8, rooftopZMax],
    [rooftopBaseY + 15.15, rooftopXMin, rooftopXMax - 1.4, rooftopZMin, -12.25],
  ] as const) {
    plate.add(box, boxTransform(
      (x0 + x1) / 2,
      y,
      (z0 + z1) / 2,
      x1 - x0,
      0.28,
      z1 - z0,
    ), 3);
    addRailRun(y, x0, z1, x1, z1, 1.35);
    addRailRun(y, x0, z0, x1, z0, 1.35);
  }

  // Large but offset machinery rooms provide visible mass without becoming a blank wall.
  // Recessed dark faces, cap flashings, panel seams and louvres make each box read as a
  // fabricated utility module.
  for (const [x, y, z, w, h, d] of [
    [-15.9, rooftopBaseY + 2.15, -15.55, 5.25, 4.05, 5.8],
    [-10.35, rooftopBaseY + 7.25, -12.35, 4.65, 4.05, 4.9],
    [-15.0, rooftopBaseY + 12.45, -16.35, 5.8, 4.2, 5.45],
  ] as const) {
    plate.add(box, boxTransform(x, y, z, w, h, d), 3);
    dark.add(box, boxTransform(x + w / 2 + 0.07, y - 0.05, z, 0.16, h - 0.45, d - 0.42), 3);
    steel.add(box, boxTransform(x, y + h / 2 + 0.09, z, w + 0.18, 0.18, d + 0.18), 2);
    for (let seam = -1; seam <= 1; seam++) {
      steel.add(box, boxTransform(
        x + w / 2 + 0.16,
        y,
        z + seam * d * 0.26,
        0.09,
        h - 0.55,
        0.08,
      ), 2);
    }
    for (let louvre = 0; louvre < 5; louvre++) {
      steel.add(box, boxTransform(
        x + w / 2 + 0.19,
        y - 0.62 + louvre * 0.3,
        z,
        0.08,
        0.08,
        d * 0.52,
      ), 2);
    }
  }

  // A continuous switchback access route is a strong human-scale cue across the stack.
  addStairFlight(
    rooftopXMax - 1.25,
    rooftopZMin + 1.2,
    rooftopBaseY + 4.9,
    rooftopBaseY + 0.2,
    rooftopZMax - rooftopZMin - 2.4,
    1.35,
  );
  addStairFlight(
    rooftopXMax - 1.25,
    rooftopZMax - 1.2,
    rooftopBaseY + 9.9,
    rooftopBaseY + 5.15,
    -(rooftopZMax - rooftopZMin - 2.4),
    1.35,
  );
  addCagedLadder(
    rooftopXMin + 0.55,
    rooftopZMax - 0.45,
    rooftopBaseY + 9.9,
    rooftopBaseY + 15.0,
  );

  // Colour-coded process and fire-water headers, with believable vertical drops into
  // the rooms below. They create fine silhouette detail that survives around the large
  // masses and stops the new structure feeling like a single crude addition.
  for (const [z, y, diameter, tint] of [
    [-9.15, rooftopBaseY + 11.35, 0.34, [0.16, 0.32, 0.48]],
    [-9.8, rooftopBaseY + 12.05, 0.26, [0.62, 0.12, 0.07]],
    [-10.4, rooftopBaseY + 12.68, 0.3, [0.63, 0.46, 0.08]],
  ] as const) {
    addPipeRun(steel, [
      new Vector3(rooftopXMin + 0.7, y, z),
      new Vector3(rooftopXMax - 0.55, y, z),
    ], diameter, 7, true, tint);
  }
  for (const [x, z, top, tint] of [
    [-18.0, -9.15, rooftopBaseY + 11.35, [0.16, 0.32, 0.48]],
    [-12.65, -9.8, rooftopBaseY + 12.05, [0.62, 0.12, 0.07]],
    [-8.3, -10.4, rooftopBaseY + 12.68, [0.63, 0.46, 0.08]],
  ] as const) {
    addPipeRun(steel, [
      new Vector3(x, rooftopBaseY + 0.35, z),
      new Vector3(x, top, z),
    ], 0.28, 7, true, tint);
    addValveWheel(new Vector3(x, rooftopBaseY + 6.9, z + 0.18), 0.48);
  }

  // Rooftop exhaust fans, paired vent stacks, cable routes and emergency cabinets are
  // intentionally small: these are the props that make the large structure feel used.
  for (const [x, z, diameter, height] of [
    [-17.75, -18.15, 1.25, 3.4],
    [-12.45, -18.75, 0.95, 2.7],
    [-9.0, -14.85, 1.1, 3.1],
  ] as const) {
    dark.add(cylHi, boxTransform(
      x,
      rooftopBaseY + 15.25 + height / 2,
      z,
      diameter,
      height,
      diameter,
    ), 4);
    steel.add(cylHi, boxTransform(
      x,
      rooftopBaseY + 15.25 + height,
      z,
      diameter * 1.35,
      0.22,
      diameter * 1.35,
    ), 3);
  }
  for (const [x, y, z] of [
    [-8.0, rooftopBaseY + 1.15, -8.05],
    [-8.0, rooftopBaseY + 6.15, -8.05],
    [-18.7, rooftopBaseY + 11.1, -19.9],
  ] as const) {
    plate.add(box, boxTransform(
      x,
      y,
      z,
      0.75,
      1.65,
      1.15,
    ), 2, [0.72, 0.22, 0.055]);
    dark.add(box, boxTransform(
      x + 0.39,
      y,
      z,
      0.08,
      1.25,
      0.82,
    ), 2);
  }
  addCableTray(
    rooftopXMin + 0.35,
    rooftopZMax - 0.25,
    rooftopXMax - 0.35,
    rooftopZMax - 0.25,
    rooftopBaseY + 4.25,
  );
  addCableTray(
    rooftopXMin + 0.35,
    rooftopZMin + 0.35,
    rooftopXMin + 0.35,
    rooftopZMax - 0.25,
    rooftopBaseY + 9.25,
  );

  // The accommodation galleries above belong to the lower working decks. A separate
  // utility annex rises from the observation level itself, so the hard-port bearing
  // contains an actual industrial destination rather than an empty foreground slab.
  const portAnnexXMin = -31.0;
  const portAnnexXMax = -12.6;
  const portAnnexZMin = -2.7;
  const portAnnexZMax = 5.2;
  const portAnnexDeckY = DECK_Y - 0.08;
  const portAnnexMidY = DECK_Y + 6.0;
  const portAnnexTopY = DECK_Y + 15.8;

  plate.add(box, boxTransform(
    (portAnnexXMin + portAnnexXMax) / 2,
    portAnnexDeckY,
    (portAnnexZMin + portAnnexZMax) / 2,
    portAnnexXMax - portAnnexXMin,
    0.34,
    portAnnexZMax - portAnnexZMin,
  ), 4);
  for (const z of [portAnnexZMin, portAnnexZMax]) {
    steel.add(box, boxTransform(
      (portAnnexXMin + portAnnexXMax) / 2,
      portAnnexDeckY - 0.72,
      z,
      portAnnexXMax - portAnnexXMin,
      1.08,
      0.58,
    ), 3);
  }

  const portAnnexFrames = [-30.6, -24.6, -18.6, -12.9];
  for (const x of portAnnexFrames) {
    for (const z of [portAnnexZMin + 0.3, portAnnexZMax - 0.3]) {
      steel.add(box, memberTransform(
        new Vector3(x, MAIN_DECK_Y - 0.4, z),
        new Vector3(x, portAnnexTopY, z),
        0.5,
      ), 3);
    }
    for (const y of [portAnnexMidY, portAnnexTopY]) {
      steel.add(box, memberTransform(
        new Vector3(x, y, portAnnexZMin),
        new Vector3(x, y, portAnnexZMax),
        0.42,
      ), 3);
    }
  }
  for (let bay = 0; bay < portAnnexFrames.length - 1; bay++) {
    const x0 = portAnnexFrames[bay] as number;
    const x1 = portAnnexFrames[bay + 1] as number;
    for (const z of [portAnnexZMin + 0.3, portAnnexZMax - 0.3]) {
      steel.add(box, memberTransform(
        new Vector3(x0, portAnnexDeckY + 0.2, z),
        new Vector3(x1, portAnnexMidY, z),
        0.32,
      ), 3);
      steel.add(box, memberTransform(
        new Vector3(x1, portAnnexDeckY + 0.2, z),
        new Vector3(x0, portAnnexMidY, z),
        0.32,
      ), 3);
      steel.add(box, memberTransform(
        new Vector3(x0, portAnnexMidY, z),
        new Vector3(x1, portAnnexTopY, z),
        0.34,
      ), 3);
    }
  }

  // Two staggered access decks allow the ocean and storm light to remain visible
  // through the structure instead of replacing the empty space with a solid box.
  for (const [y, z] of [
    [portAnnexMidY, portAnnexZMax - 0.9],
    [portAnnexTopY - 3.3, portAnnexZMin + 0.9],
  ] as const) {
    plate.add(box, boxTransform(
      (portAnnexXMin + portAnnexXMax) / 2,
      y,
      z,
      portAnnexXMax - portAnnexXMin,
      0.25,
      1.8,
    ), 3);
    addRailRun(y, portAnnexXMin, z + 0.92, portAnnexXMax, z + 0.92, 1.5);
  }

  // A pair of unequal scrubbers gives the annex a readable process function and a
  // strong vertical rhythm from the player-height camera.
  for (const [x, z, radius, height] of [
    [-27.2, 1.2, 2.15, 13.8],
    [-19.8, 0.7, 1.72, 10.7],
  ] as const) {
    const centreY = portAnnexDeckY + 0.2 + height / 2;
    plate.add(cylHi, boxTransform(x, centreY, z, radius * 2, height, radius * 2), 4);
    dark.add(cylHi, boxTransform(
      x,
      portAnnexDeckY + height + 0.35,
      z,
      radius * 2.25,
      0.32,
      radius * 2.25,
    ), 3);
    for (const ringY of [
      portAnnexDeckY + 2.2,
      portAnnexDeckY + height * 0.55,
      portAnnexDeckY + height - 1.0,
    ]) {
      steel.add(cylHi, boxTransform(
        x,
        ringY,
        z,
        radius * 2.22,
        0.25,
        radius * 2.22,
      ), 3);
    }
    steel.add(box, boxTransform(
      x,
      portAnnexDeckY + 0.45,
      z,
      radius * 1.7,
      0.9,
      radius * 1.7,
    ), 3);
  }

  // Layered manifolds, risers and valves break up the lower silhouette at human scale.
  for (let line = 0; line < 5; line++) {
    const z = portAnnexZMin + 0.85 + line * 1.25;
    const y = DECK_Y + 10.9 + (line % 2) * 0.62;
    const acc = line === 1 ? yellow : line % 2 === 0 ? dark : steel;
    acc.add(cyl, memberTransform(
      new Vector3(portAnnexXMin - 0.4, y, z),
      new Vector3(portAnnexXMax + 1.0, y, z),
      line === 2 ? 0.54 : 0.32,
    ), 7);
  }
  for (const x of [-29.5, -23.0, -16.0]) {
    addPipeRun(steel, [
      new Vector3(x, DECK_Y + 1.0, portAnnexZMin + 0.75),
      new Vector3(x, DECK_Y + 8.8, portAnnexZMin + 0.75),
      new Vector3(x + 1.6, DECK_Y + 8.8, portAnnexZMin + 0.75),
    ], 0.38, 7);
    addValveWheel(
      new Vector3(x, DECK_Y + 5.1, portAnnexZMin + 0.28),
      0.52,
    );
  }
  addCagedLadder(
    portAnnexXMax - 0.65,
    portAnnexZMax - 0.3,
    portAnnexDeckY + 0.2,
    portAnnexTopY - 0.45,
  );
  addStairFlight(
    portAnnexXMin + 2.0,
    portAnnexZMin + 0.75,
    portAnnexMidY,
    portAnnexDeckY + 0.2,
    portAnnexZMax - portAnnexZMin - 1.5,
    1.5,
  );

  // PORT ANNEX PROCESS DECK — finish the two visible left bays while leaving the
  // rightmost frame bay and its narrow sea corridor completely untouched. Equipment is
  // layered around the existing scrubbers, not placed across their protected sightline.
  const portDenseXMin = portAnnexXMin + 0.75;
  const portDenseXMax = -19.15;
  const portDenseMidX = (portDenseXMin + portDenseXMax) / 2;
  const portUpperServiceY = DECK_Y + 12.15;

  // A second switchback flight completes access from the mid gallery to the upper deck.
  // Its lower landing aligns with the first stair but reverses direction through the bay.
  addStairFlight(
    portAnnexXMin + 2.0,
    portAnnexZMax - 0.95,
    portUpperServiceY,
    portAnnexMidY + 0.15,
    -(portAnnexZMax - portAnnexZMin - 1.6),
    1.5,
  );
  openGrate.add(box, boxTransform(
    portAnnexXMin + 2.0,
    portAnnexMidY + 0.12,
    portAnnexZMin + 0.95,
    2.4,
    0.22,
    1.9,
  ), 3);
  addRailRun(
    portAnnexMidY + 0.12,
    portAnnexXMin + 0.8,
    portAnnexZMin + 1.9,
    portAnnexXMin + 3.2,
    portAnnexZMin + 1.9,
    1.2,
  );

  // Two unequal ventilation/process housings occupy the upper frame behind the vessels.
  // Their setbacks leave service slots, while intake fins and roof fans read clearly
  // through the steel cage from the player deck.
  const portUpperModules = [
    {
      x: -27.25, z: -0.75, width: 5.2, height: 3.7, depth: 3.2,
    },
    {
      x: -22.15, z: 3.05, width: 4.45, height: 4.8, depth: 3.55,
    },
  ] as const;
  for (const module of portUpperModules) {
    const centreY = portUpperServiceY + module.height / 2 + 0.15;
    const faceX = module.x + module.width / 2;
    plate.add(box, boxTransform(
      module.x,
      centreY,
      module.z,
      module.width,
      module.height,
      module.depth,
    ), 2.5);
    plate.add(box, boxTransform(
      module.x,
      portUpperServiceY + module.height + 0.42,
      module.z,
      module.width + 0.45,
      0.24,
      module.depth + 0.45,
    ), 2.5);

    dark.add(box, boxTransform(
      faceX + 0.08,
      centreY,
      module.z,
      0.14,
      module.height - 0.35,
      module.depth - 0.38,
    ), 1.8);
    for (let fin = 0; fin < 10; fin++) {
      steel.add(box, boxTransform(
        faceX + 0.17,
        centreY - 0.88 + fin * 0.19,
        module.z,
        0.11,
        0.055,
        module.depth * 0.66,
      ), 1.5);
    }
    for (const z of [
      module.z - module.depth / 2 + 0.16,
      module.z,
      module.z + module.depth / 2 - 0.16,
    ]) {
      steel.add(box, boxTransform(
        faceX + 0.19,
        centreY,
        z,
        0.12,
        module.height - 0.22,
        0.12,
      ), 1.7);
    }

    dark.add(cylHi, boxTransform(
      module.x,
      portUpperServiceY + module.height + 0.64,
      module.z,
      Math.min(module.width, module.depth) * 0.72,
      0.20,
      Math.min(module.width, module.depth) * 0.72,
    ), 2.6);
    steel.add(cylHi, boxTransform(
      module.x,
      portUpperServiceY + module.height + 0.78,
      module.z,
      0.58,
      0.32,
      0.58,
    ), 2);
    for (const xOffset of [-module.width * 0.35, module.width * 0.35]) {
      for (const zOffset of [-module.depth * 0.35, module.depth * 0.35]) {
        steel.add(box, memberTransform(
          new Vector3(
            module.x + xOffset,
            portUpperServiceY - 0.1,
            module.z + zOffset,
          ),
          new Vector3(
            module.x + xOffset,
            centreY - module.height / 2,
            module.z + zOffset,
          ),
          0.24,
        ), 2.2);
      }
    }
  }

  // The upper gallery continues only across the dense bays and terminates before the
  // corridor. Railings and diagonal brackets make its endpoint explicit.
  openGrate.add(box, boxTransform(
    portDenseMidX,
    portUpperServiceY,
    portAnnexZMin + 0.92,
    portDenseXMax - portDenseXMin,
    0.22,
    1.65,
  ), 4);
  addRailRun(
    portUpperServiceY,
    portDenseXMin,
    portAnnexZMin + 0.05,
    portDenseXMax,
    portAnnexZMin + 0.05,
    1.4,
  );
  for (const x of [-30.1, -27.0, -23.6, portDenseXMax]) {
    steel.add(box, memberTransform(
      new Vector3(x, portUpperServiceY - 0.1, portAnnexZMin + 0.15),
      new Vector3(x, portUpperServiceY - 2.2, portAnnexZMin + 2.25),
      0.23,
    ), 2.4);
  }

  // A far-side riser rack and three coloured service headers fill the residual sky with
  // genuine plant logic. All runs stop half a metre before the protected third bay.
  const portRiserZ = portAnnexZMin + 0.42;
  for (let riser = 0; riser < 8; riser++) {
    const x = portDenseXMin + 0.35
      + riser * (portDenseXMax - portDenseXMin - 0.7) / 7;
    const radius = riser % 3 === 0 ? 0.31 : riser % 2 === 0 ? 0.23 : 0.17;
    const acc = riser % 2 === 0 ? steel : dark;
    acc.add(cyl, memberTransform(
      new Vector3(x, portAnnexDeckY + 0.35, portRiserZ),
      new Vector3(x, portAnnexTopY - 0.6 - (riser % 3) * 0.55, portRiserZ),
      radius,
    ), 6);
    acc.add(cyl, memberTransform(
      new Vector3(x, portAnnexTopY - 0.85 - (riser % 3) * 0.55, portRiserZ),
      new Vector3(x + 0.85, portAnnexTopY - 0.85 - (riser % 3) * 0.55, portRiserZ),
      radius,
    ), 6);
  }
  const portFireRed = [0.72, 0.18, 0.10] as const;
  const portServiceBlue = [0.16, 0.42, 0.66] as const;
  const portServiceYellow = [0.92, 0.68, 0.12] as const;
  for (const [z, y, radius, tint] of [
    [-1.85, DECK_Y + 9.55, 0.20, portFireRed],
    [-1.35, DECK_Y + 10.25, 0.17, portServiceBlue],
    [-0.82, DECK_Y + 10.92, 0.19, portServiceYellow],
  ] as const) {
    addPipeRun(steel, [
      new Vector3(portDenseXMin, y, z),
      new Vector3(portDenseXMax, y, z),
    ], radius, 5, true, tint);
  }
  for (const [x, tint] of [
    [-29.8, portFireRed],
    [-26.2, portServiceBlue],
    [-22.6, portServiceYellow],
    [-19.75, portServiceBlue],
  ] as const) {
    addPipeRun(steel, [
      new Vector3(x, portAnnexDeckY + 0.4, portRiserZ + 0.28),
      new Vector3(x, DECK_Y + 10.92, portRiserZ + 0.28),
      new Vector3(x + 0.62, DECK_Y + 10.92, portRiserZ + 0.28),
    ], 0.17, 5, true, tint);
    addValveWheel(
      new Vector3(x, DECK_Y + 7.15, portRiserZ + 0.54),
      0.38,
    );
  }

  // Pump and metering skids fill the accessible lower layer underneath the large
  // scrubbers. Their pipe connections lead upward into the new header bank.
  for (const [x, z, length] of [
    [-25.0, -1.1, 2.15],
    [-21.85, -1.2, 1.75],
  ] as const) {
    plate.add(box, boxTransform(
      x,
      portAnnexDeckY + 0.18,
      z,
      2.65,
      0.24,
      length + 1.0,
    ), 2.7);
    dark.add(cylHi, memberTransform(
      new Vector3(x, portAnnexDeckY + 0.9, z - length / 2),
      new Vector3(x, portAnnexDeckY + 0.9, z + length / 2),
      0.68,
    ), 3);
    steel.add(box, boxTransform(
      x + 0.9,
      portAnnexDeckY + 0.75,
      z,
      1.0,
      1.15,
      1.3,
    ), 2);
    addPipeRun(steel, [
      new Vector3(x, portAnnexDeckY + 1.55, z),
      new Vector3(x, DECK_Y + 4.5, z),
      new Vector3(x + 0.72, DECK_Y + 4.5, z),
    ], 0.22, 5);
  }

  // Field boxes, a rescue locker and a cable tray create a maintained human-scale edge.
  for (const x of [-27.15, -24.85, -22.55]) {
    plate.add(box, boxTransform(
      x,
      portAnnexMidY + 1.0,
      portAnnexZMin + 0.1,
      1.15,
      1.55,
      0.46,
    ), 2);
    dark.add(box, boxTransform(
      x,
      portAnnexMidY + 1.0,
      portAnnexZMin - 0.15,
      0.65,
      0.82,
      0.08,
    ), 1.6);
  }
  const portRescueOrange = [0.90, 0.38, 0.10] as const;
  yellow.add(box, boxTransform(
    -20.2,
    portAnnexMidY + 1.0,
    portAnnexZMin + 0.12,
    1.0,
    1.7,
    0.5,
  ), 2.1, portRescueOrange);
  dark.add(box, boxTransform(
    -20.2,
    portAnnexMidY + 1.02,
    portAnnexZMin - 0.16,
    0.58,
    1.0,
    0.08,
  ), 1.6);
  addCableTray(
    portDenseXMin,
    portAnnexZMax - 0.4,
    portDenseXMax,
    portAnnexZMax - 0.4,
    DECK_Y + 8.65,
  );

  // PORT BACKPLANT — the decisive mass behind the visible scrubber cage. Three offset
  // production blocks, external galleries and a switchback stair turn the port bearing
  // into the edge of a much larger installation. This stack sits far behind the annex
  // (x < -31), so perspective keeps it clear of the protected corridor sightline.
  const backplantFaceX = -18.75;
  const backplantZMin = -9.45;
  const backplantZMax = -2.20;
  const backplantMidZ = (backplantZMin + backplantZMax) / 2;
  const backplantLevels = [
    DECK_Y - 0.80,
    DECK_Y + 6.10,
    DECK_Y + 12.00,
  ];
  const backplantModules = [
    {
      x: -22.15,
      y: DECK_Y - 4.45,
      z: -5.85,
      width: 6.8,
      height: 7.0,
      depth: 7.1,
    },
    {
      x: -22.65,
      y: DECK_Y + 2.65,
      z: -6.65,
      width: 7.7,
      height: 6.3,
      depth: 8.35,
    },
    {
      x: -21.75,
      y: DECK_Y + 9.00,
      z: -5.00,
      width: 5.9,
      height: 5.7,
      depth: 4.85,
    },
  ] as const;

  for (let moduleIndex = 0; moduleIndex < backplantModules.length; moduleIndex++) {
    const module = backplantModules[moduleIndex]!;
    const faceX = module.x + module.width / 2;
    plate.add(box, boxTransform(
      module.x,
      module.y,
      module.z,
      module.width,
      module.height,
      module.depth,
    ), 2.8);
    plate.add(box, boxTransform(
      module.x,
      module.y + module.height / 2 + 0.18,
      module.z,
      module.width + 0.48,
      0.28,
      module.depth + 0.48,
    ), 2.7);

    // Deep transport-sized panel bays and horizontal joints stop the mass reading as a
    // single blank wall, even when mist compresses its depth.
    for (let panel = 0; panel <= 4; panel++) {
      const z = module.z - module.depth / 2
        + panel * module.depth / 4;
      steel.add(box, boxTransform(
        faceX + 0.13,
        module.y,
        z,
        0.16,
        module.height - 0.22,
        0.13,
      ), 2);
    }
    for (const yOffset of [-0.28, 0.28]) {
      dark.add(box, boxTransform(
        faceX + 0.14,
        module.y + module.height * yOffset,
        module.z,
        0.16,
        0.14,
        module.depth - 0.18,
      ), 2);
    }

    if (moduleIndex !== 1) {
      for (let louvre = 0; louvre < 9; louvre++) {
        dark.add(box, boxTransform(
          faceX + 0.23,
          module.y - 0.85 + louvre * 0.21,
          module.z + module.depth * 0.15,
          0.13,
          0.065,
          module.depth * 0.42,
        ), 1.6);
      }
    } else {
      // Large recessed maintenance doors on the middle process floor.
      for (const z of [-1.35, 1.8]) {
        dark.add(box, boxTransform(
          faceX + 0.24,
          module.y - 0.45,
          z,
          0.13,
          3.55,
          1.75,
        ), 2);
        steel.add(box, boxTransform(
          faceX + 0.33,
          module.y - 0.1,
          z + 0.55,
          0.08,
          0.48,
          0.12,
        ), 2);
      }
    }
  }

  // The blocks share an external circulation face. Every gallery projects toward the
  // camera with rails, toe boards and diagonal brackets, creating repeated human scale.
  for (let level = 0; level < backplantLevels.length; level++) {
    const y = backplantLevels[level] as number;
    const galleryX = backplantFaceX + 0.85 + level * 0.12;
    openGrate.add(box, boxTransform(
      galleryX,
      y,
      backplantMidZ,
      1.75,
      0.22,
      backplantZMax - backplantZMin,
    ), 4);
    addRailRun(
      y,
      galleryX + 0.88,
      backplantZMin,
      galleryX + 0.88,
      backplantZMax,
      1.4,
    );
    for (const z of [backplantZMin + 0.4, 0.0, backplantZMax - 0.4]) {
      steel.add(box, memberTransform(
        new Vector3(galleryX + 0.65, y - 0.12, z),
        new Vector3(backplantFaceX - 0.4, y - 2.25, z),
        0.24,
      ), 2.5);
    }
  }

  // Three alternating flights form one continuous escape route from the annex deck to
  // the top process floor. The zigzag remains legible through the foreground frame.
  addStairFlight(
    backplantFaceX + 1.05,
    backplantZMin + 0.55,
    backplantLevels[0] as number,
    DECK_Y - 7.80,
    5.45,
    1.5,
  );
  addStairFlight(
    backplantFaceX + 1.05,
    backplantZMax - 0.55,
    backplantLevels[1] as number,
    (backplantLevels[0] as number) + 0.15,
    -5.45,
    1.5,
  );
  addStairFlight(
    backplantFaceX + 1.05,
    backplantZMin + 0.55,
    backplantLevels[2] as number,
    (backplantLevels[1] as number) + 0.15,
    5.45,
    1.5,
  );

  // A full-height utility bank, cross headers and valves make the backplant operational
  // rather than architectural scenery.
  for (let riser = 0; riser < 7; riser++) {
    const z = backplantZMin + 0.65
      + riser * (backplantZMax - backplantZMin - 1.3) / 6;
    const radius = riser % 3 === 0 ? 0.34 : riser % 2 === 0 ? 0.24 : 0.18;
    const acc = riser === 4 ? yellow : riser % 2 === 0 ? steel : dark;
    acc.add(cyl, memberTransform(
      new Vector3(backplantFaceX + 1.95, DECK_Y - 7.65, z),
      new Vector3(
        backplantFaceX + 1.95,
        DECK_Y + 10.30 - (riser % 3) * 0.9,
        z,
      ),
      radius,
    ), 6);
    if (riser % 2 === 0) {
      addValveWheel(
        new Vector3(
          backplantFaceX + 2.25,
          DECK_Y + 1.00 + (riser % 3) * 1.15,
          z,
        ),
        0.42,
        'x',
      );
    }
  }
  for (const y of [DECK_Y + 0.60, DECK_Y + 4.40, DECK_Y + 9.25]) {
    addPipeRun(steel, [
      new Vector3(backplantFaceX + 2.0, y, backplantZMin + 0.4),
      new Vector3(backplantFaceX + 2.0, y, backplantZMax - 0.35),
    ], y === DECK_Y + 4.40 ? 0.32 : 0.22, 5);
  }

  // Rooftop exhausts and air intakes push the silhouette another storey upward.
  for (const [x, z, height, radius] of [
    [-28.90, -0.4, 5.8, 0.42],
    [-27.40, 2.25, 7.1, 0.54],
    [-30.10, 3.8, 4.7, 0.34],
  ] as const) {
    dark.add(cylHi, memberTransform(
      new Vector3(x, DECK_Y + 11.85, z),
      new Vector3(x, DECK_Y + 11.85 + height, z),
      radius,
    ), 5);
    steel.add(cylHi, boxTransform(
      x,
      DECK_Y + 12.05 + height,
      z,
      radius * 2.8,
      0.28,
      radius * 2.8,
    ), 2);
  }

  // Orange rescue and fire cabinets punctuate the galleries without recolouring the
  // approved handrails.
  for (const [y, z] of [
    [backplantLevels[0] as number, 3.55],
    [backplantLevels[1] as number, -1.6],
    [backplantLevels[2] as number, 3.4],
  ] as const) {
    yellow.add(box, boxTransform(
      backplantFaceX + 2.05,
      y + 0.92,
      z,
      0.52,
      1.55,
      1.05,
    ), 2, portRescueOrange);
    dark.add(box, boxTransform(
      backplantFaceX + 2.34,
      y + 0.92,
      z,
      0.08,
      0.9,
      0.62,
    ), 1.6);
  }

  /*
   * THE POWER STATION — relocated as one unit.
   *
   * The offset is applied to the finished geometry rather than to the source
   * coordinates. Editing the coordinates is what failed: the frame moved south while a
   * Z-frame array, two deck tuples and a row of rail posts stayed behind over open
   * water, because they are written in forms a pattern match does not see. Marking the
   * buffers and translating everything appended between cannot miss a vertex.
   */
  const POWER_STATION_OFFSET = { x: 31, y: -6, z: -45 };
  /**
   * Stretches the building about its own centre, in its own axes.
   *
   * Both horizontal axes, so it grows as a footprint rather than a slab: the long side
   * (`x`) went first, and the short side (`z`) follows so the proportions hold. Height is
   * left alone — scaling that too would push the roof through the deck above it.
   */
  const POWER_STATION_WIDTH_SCALE = 1.3;
  const POWER_STATION_DEPTH_SCALE = 1.3;
  /** Radians, clockwise seen from above. */
  const POWER_STATION_ROTATION = (3 * Math.PI) / 2;
  const powerStationMarks = [steel, plate, yellow, dark, openGrate, machined]
    .map((acc) => [acc, acc.vertexMark] as const);

  // PORT OCEAN-EDGE SPINE — the former protected "corridor" occupied half of the
  // hard-port view because nothing framed its far side. This independent utility and
  // accommodation stack begins beyond the slit (z > 9), leaving clear air between it
  // and the scrubber/backplant edge at z=4.8. From the player, the sea remains visible
  // as one deliberate vertical breathing slot instead of a generally unfinished side.
  const portSpineXMin = -38.0;
  const portSpineXMax = -14.2;
  const portSpineZMin = 9.1;
  const portSpineZMax = 24.2;
  const portSpineLevels = [
    DECK_Y - 0.12,
    DECK_Y + 6.45,
    DECK_Y + 13.15,
    DECK_Y + 20.05,
    DECK_Y + 27.2,
  ];
  const portSpineXFrames = [-37.5, -29.7, -21.8, -14.7];
  const portSpineZFrames = [9.55, 16.7, 23.7];

  // Offset floor plates create a stepped ocean-side profile rather than another cage.
  for (const [y, x0, x1, z0, z1] of [
    [portSpineLevels[0], portSpineXMin, portSpineXMax, portSpineZMin, portSpineZMax],
    [portSpineLevels[1], portSpineXMin, portSpineXMax, portSpineZMin, portSpineZMax],
    [portSpineLevels[2], -35.6, portSpineXMax, portSpineZMin, portSpineZMax],
    [portSpineLevels[3], portSpineXMin, -17.0, 11.0, portSpineZMax],
    [portSpineLevels[4], -34.8, -18.8, 12.5, 22.6],
  ] as const) {
    openGrate.add(box, boxTransform(
      (x0 + x1) / 2,
      y,
      (z0 + z1) / 2,
      x1 - x0,
      0.28,
      z1 - z0,
    ), 4);
    addRailRun(y, x0, z1, x1, z1, 1.5);
  }

  // Full-height columns, edge girders and alternating braces establish real load paths.
  for (const x of portSpineXFrames) {
    for (const z of portSpineZFrames) {
      steel.add(box, memberTransform(
        new Vector3(x, MAIN_DECK_Y - 1.1, z),
        new Vector3(x, (portSpineLevels[4] as number) + 0.2, z),
        0.52,
      ), 3);
    }
  }
  for (let level = 1; level < portSpineLevels.length; level++) {
    const y = portSpineLevels[level] as number;
    for (const z of [portSpineZFrames[0], portSpineZFrames[2]]) {
      steel.add(box, memberTransform(
        new Vector3(portSpineXFrames[0] as number, y, z as number),
        new Vector3(portSpineXFrames[3] as number, y, z as number),
        0.46,
      ), 3);
    }
    for (const x of portSpineXFrames) {
      steel.add(box, memberTransform(
        new Vector3(x, y, portSpineZFrames[0] as number),
        new Vector3(x, y, portSpineZFrames[2] as number),
        0.4,
      ), 3);
    }
  }
  for (let bay = 0; bay < portSpineXFrames.length - 1; bay++) {
    const x0 = portSpineXFrames[bay] as number;
    const x1 = portSpineXFrames[bay + 1] as number;
    for (let level = 0; level < 3; level++) {
      const y0 = portSpineLevels[level] as number;
      const y1 = portSpineLevels[level + 1] as number;
      steel.add(box, memberTransform(
        new Vector3(x0, y0 + 0.2, portSpineZMax - 0.5),
        new Vector3(x1, y1 - 0.2, portSpineZMax - 0.5),
        0.3,
      ), 3);
      steel.add(box, memberTransform(
        new Vector3(x1, y0 + 0.2, portSpineZMax - 0.5),
        new Vector3(x0, y1 - 0.2, portSpineZMax - 0.5),
        0.3,
      ), 3);
    }
  }

  // A weathered off-white accommodation/service block gives the far side a broad,
  // readable mass. Its upper control room steps toward the ocean and carries blue
  // ventilation banks, so the silhouette and colour hierarchy differ from the process
  // cage opposite it.
  const portSpineHouseTint = [2.15, 2.2, 2.12] as const;
  const portSpineHouseX = -29.2;
  const portSpineHouseZ = 17.5;
  plate.add(box, boxTransform(
    portSpineHouseX,
    DECK_Y + 15.9,
    portSpineHouseZ,
    14.8,
    18.4,
    10.6,
  ), 3, portSpineHouseTint);
  plate.add(box, boxTransform(
    -25.5,
    DECK_Y + 28.65,
    18.5,
    10.0,
    7.1,
    8.1,
  ), 3, [1.82, 1.9, 1.96]);
  steel.add(box, boxTransform(
    -25.5,
    DECK_Y + 32.35,
    18.5,
    11.2,
    0.32,
    9.2,
  ), 2);

  const portSpineNearZ = portSpineHouseZ - 5.3;
  for (let floor = 0; floor < 6; floor++) {
    const y = DECK_Y + 8.65 + floor * 2.85;
    for (let window = 0; window < 7; window++) {
      const x = -35.0 + window * 1.9;
      if ((floor + window) % 9 === 4) continue;
      dark.add(box, boxTransform(
        x,
        y,
        portSpineNearZ - 0.12,
        0.88,
        1.0,
        0.13,
      ), 1.7);
    }
  }
  for (const [x, y, width, height] of [
    [-33.3, DECK_Y + 11.1, 3.4, 3.7],
    [-25.2, DECK_Y + 17.6, 4.1, 4.0],
    [-31.2, DECK_Y + 24.0, 3.6, 3.4],
  ] as const) {
    plate.add(box, boxTransform(
      x,
      y,
      portSpineNearZ - 0.2,
      width,
      height,
      0.18,
    ), 2, [0.14, 0.5, 0.86]);
    for (let slat = 0; slat < 7; slat++) {
      steel.add(box, boxTransform(
        x,
        y - height * 0.34 + slat * height * 0.68 / 6,
        portSpineNearZ - 0.31,
        width - 0.34,
        0.065,
        0.08,
      ), 1.4);
    }
  }

  // A teal cantilevered control pod creates a memorable asymmetric shoulder over the
  // corridor-facing edge, supported by visible knees rather than floating decoration.
  const portSpinePodTint = [0.1, 0.67, 0.82] as const;
  plate.add(box, boxTransform(
    -17.5,
    DECK_Y + 17.0,
    16.0,
    6.1,
    5.0,
    7.8,
  ), 3, portSpinePodTint);
  for (const z of [13.2, 16.0, 18.8]) {
    steel.add(box, memberTransform(
      new Vector3(-14.4, DECK_Y + 14.5, z),
      new Vector3(-20.6, DECK_Y + 11.6, z),
      0.31,
    ), 2.5);
  }

  // Connected fire-water, air and utility lines run the full lower plant before rising
  // into the accommodation block. Their colour denotes function instead of decoration.
  for (const [z, y, tint, diameter] of [
    [10.5, DECK_Y + 2.8, [0.64, 0.085, 0.05], 0.34],
    [11.45, DECK_Y + 4.0, [0.06, 0.34, 0.62], 0.3],
    [12.32, DECK_Y + 5.15, [0.72, 0.55, 0.055], 0.27],
  ] as const) {
    addPipeRun(steel, [
      new Vector3(portSpineXMax + 0.5, y, z),
      new Vector3(portSpineXMin + 0.8, y, z),
      new Vector3(portSpineXMin + 0.8, DECK_Y + 12.5, z),
      new Vector3(-34.2, DECK_Y + 12.5, z),
    ], diameter, 7, true, tint);
  }

  // Lower-deck pump and valve wall gives the new mass a working process layer at the
  // player's height while preserving a clear central maintenance aisle.
  for (let skid = 0; skid < 7; skid++) {
    const x = -35.7 + skid * 2.95;
    const tint = skid % 3 === 0
      ? [0.08, 0.32, 0.58] as const
      : [0.66, 0.69, 0.67] as const;
    dark.add(cyl, memberTransform(
      new Vector3(x - 0.72, DECK_Y + 1.05, 14.3),
      new Vector3(x + 0.72, DECK_Y + 1.05, 14.3),
      0.78,
    ), 3, tint);
    plate.add(box, boxTransform(
      x,
      DECK_Y + 0.35,
      14.3,
      2.3,
      0.5,
      1.8,
    ), 2, tint);
    addPipeRun(steel, [
      new Vector3(x, DECK_Y + 1.6, 14.3),
      new Vector3(x, DECK_Y + 7.9 - (skid % 2) * 0.7, 14.3),
      new Vector3(x + 0.7, DECK_Y + 7.9 - (skid % 2) * 0.7, 14.3),
    ], 0.18 + (skid % 2) * 0.05, 6, true, tint);
    if (skid % 2 === 0) {
      addValveWheel(new Vector3(x + 0.28, DECK_Y + 4.7, 14.05), 0.38);
    }
  }

  // A complete switchback escape route makes all five decks plausibly accessible.
  for (let flight = 0; flight < 4; flight++) {
    const bottomY = portSpineLevels[flight] as number;
    const topY = portSpineLevels[flight + 1] as number;
    addStairFlight(
      -15.8,
      flight % 2 === 0 ? 20.8 : 13.0,
      topY,
      bottomY + 0.2,
      flight % 2 === 0 ? -6.4 : 6.4,
      1.5,
    );
  }

  // Dark-orange enclosed survival craft sits on its own launch gallery. Davit arms and
  // falls make the accent functional and visible from the deck.
  const portSpineRescueTint = [0.92, 0.31, 0.06] as const;
  yellow.add(cylHi, memberTransform(
    new Vector3(-21.8, DECK_Y + 9.65, 10.45),
    new Vector3(-27.4, DECK_Y + 9.65, 10.45),
    1.25,
  ), 2.5, portSpineRescueTint);
  yellow.add(box, boxTransform(
    -24.6,
    DECK_Y + 9.25,
    10.45,
    5.4,
    1.55,
    2.3,
  ), 2.2, portSpineRescueTint);
  for (const x of [-22.2, -27.0]) {
    yellow.add(box, memberTransform(
      new Vector3(x, DECK_Y + 13.5, 9.8),
      new Vector3(x, DECK_Y + 10.2, 7.4),
      0.25,
    ), 2.2, portSpineRescueTint);
    steel.add(cyl, memberTransform(
      new Vector3(x, DECK_Y + 10.2, 7.4),
      new Vector3(x, DECK_Y + 9.4, 10.45),
      0.05,
    ), 1.5);
  }

  // Exhaust crown: unequal heights and a shared yellow service ring finish the stepped
  // skyline with a vertical system rather than another flat roof.
  for (const [x, z, height, radius] of [
    [-31.8, 17.0, 7.2, 0.52],
    [-28.9, 18.6, 9.1, 0.62],
    [-25.8, 16.6, 6.0, 0.44],
  ] as const) {
    dark.add(cylHi, memberTransform(
      new Vector3(x, DECK_Y + 32.2, z),
      new Vector3(x, DECK_Y + 32.2 + height, z),
      radius,
    ), 4);
    yellow.add(cylHi, boxTransform(
      x,
      DECK_Y + 32.35 + height,
      z,
      radius * 2.7,
      0.28,
      radius * 2.7,
    ), 2);
  }

  /*
   * Cladding panel on the Power Station's southern face.
   *
   * Built in the building's OWN axes and inside the marked region, so it goes through the
   * same rotate/stretch/move as everything else and stays welded to the wall no matter
   * how the building is later turned. Placed on local -X because the 270-degree rotation
   * swings that face to world south — hardcoding a world-facing panel would come adrift
   * the moment POWER_STATION_ROTATION changed.
   *
   * Set slightly proud of the frame line and inset at its ends so it reads as a panel
   * bolted onto the structure rather than a face of it.
   */
  {
    const panelInset = 1.1;
    const panelBottom = portSpineLevels[0] as number;
    const panelTop = portSpineLevels[4] as number;
    plate.add(box, boxTransform(
      portSpineXMin - 0.28,
      (panelBottom + panelTop) / 2,
      (portSpineZMin + portSpineZMax) / 2,
      0.42,
      panelTop - panelBottom,
      (portSpineZMax - portSpineZMin) - panelInset * 2,
    ), 3);
    // A pair of vertical stiffeners break the slab up at this scale.
    for (const t of [0.3, 0.7]) {
      steel.add(box, boxTransform(
        portSpineXMin - 0.54,
        (panelBottom + panelTop) / 2,
        portSpineZMin + (portSpineZMax - portSpineZMin) * t,
        0.22,
        panelTop - panelBottom,
        0.55,
      ), 4);
    }
  }

  /*
   * Dark blue corrugated cladding on the Power Station's western face.
   *
   * Local +Z, because the 270-degree rotation swings that face to world west — same
   * reasoning as the southern panel, and the same reason both are built inside the marked
   * region: they must follow the building rather than sit at a world position it has
   * moved away from.
   *
   * The corrugation is real geometry rather than a texture. At the ranges this face is
   * seen from — the Bridge is close, and the flycam closer — a flat plate with a pattern
   * on it reads as a flat plate, and the whole point of profiled sheet is the way raking
   * light breaks across the ribs.
   */
  {
    /*
     * Strongly blue-dominant, because the tint multiplies rather than replaces.
     *
     * It lands on a grey plate scan under a grey base colour, so a tint that merely leans
     * blue — the first attempt was [0.13, 0.21, 0.36] — comes out as cold grey with the
     * colour washed out of it. Crushing red and green while holding blue up is what
     * survives that chain and the ACES grade after it. The ribs are lifted slightly so
     * the profile still catches light against the sheet behind it.
     */
    const bluePanel = CLAD_BLUE_SHEET;
    const blueRib = CLAD_BLUE_RIB;
    const westBottom = portSpineLevels[0] as number;
    const westTop = portSpineLevels[4] as number;
    const westHeight = westTop - westBottom;
    const westMidY = (westBottom + westTop) / 2;
    const westInset = 0.9;
    const westX0 = portSpineXMin + westInset;
    const westX1 = portSpineXMax - westInset;

    // Backing sheet.
    plate.add(box, boxTransform(
      (westX0 + westX1) / 2, westMidY, portSpineZMax + 0.26,
      westX1 - westX0, westHeight, 0.34,
    ), 3, bluePanel);

    // Ribs. Spaced by a fixed pitch rather than a fixed count, so the profile stays the
    // same physical size if the building is stretched again.
    const ribPitch = 1.15;
    const ribCount = Math.max(2, Math.floor((westX1 - westX0) / ribPitch));
    for (let i = 0; i < ribCount; i++) {
      const x = westX0 + ((i + 0.5) * (westX1 - westX0)) / ribCount;
      plate.add(box, boxTransform(
        x, westMidY, portSpineZMax + 0.52,
        0.46, westHeight, 0.3,
      ), 3, blueRib);
    }

    // A capping rail top and bottom so the sheet terminates rather than just stopping.
    for (const y of [westBottom + 0.35, westTop - 0.35]) {
      steel.add(box, boxTransform(
        (westX0 + westX1) / 2, y, portSpineZMax + 0.5,
        westX1 - westX0, 0.42, 0.42,
      ), 4);
    }
  }

  /*
   * Matching cladding on the Power Station's eastern face.
   *
   * Local -Z, the opposite face to the western panel: the 270-degree rotation swings +Z
   * to west, so -Z lands east. Built inside the marked region like the others, so it
   * follows the building through the rotate/stretch/move rather than sitting at a world
   * position the building has left.
   */
  {
    const eastBottom = portSpineLevels[0] as number;
    const eastTop = portSpineLevels[4] as number;
    const eastHeight = eastTop - eastBottom;
    const eastMidY = (eastBottom + eastTop) / 2;
    const eastInset = 0.9;
    const eastX0 = portSpineXMin + eastInset;
    const eastX1 = portSpineXMax - eastInset;
    const eastWidth = eastX1 - eastX0;
    const eastMidX = (eastX0 + eastX1) / 2;

    plate.add(box, boxTransform(
      eastMidX, eastMidY, portSpineZMin - 0.26, eastWidth, eastHeight, 0.34,
    ), 3, CLAD_BLUE_SHEET);

    const ribs = Math.max(2, Math.floor(eastWidth / 1.15));
    for (let i = 0; i < ribs; i++) {
      plate.add(box, boxTransform(
        eastX0 + ((i + 0.5) * eastWidth) / ribs,
        eastMidY, portSpineZMin - 0.52, 0.46, eastHeight, 0.3,
      ), 3, CLAD_BLUE_RIB);
    }
    for (const y of [eastBottom + 0.35, eastTop - 0.35]) {
      steel.add(box, boxTransform(
        eastMidX, y, portSpineZMin - 0.5, eastWidth, 0.42, 0.42,
      ), 4);
    }
  }

  /*
   * Rotate about the building's own centre, then move it.
   *
   * The pivot is measured from the geometry rather than guessed, so the building turns on
   * the spot instead of swinging away from wherever a hardcoded centre happened to be.
   * Every accumulator shares one pivot — taken from the largest, which carries the
   * primary steel — or the parts would each spin about their own middle and fly apart.
   */
  const powerStationPivot = powerStationMarks
    .reduce((best, [acc, mark]) => {
      const span = acc.vertexMark - mark;
      return span > best.span ? { span, centre: acc.centreFrom(mark) } : best;
    }, { span: -1, centre: Vector3.Zero() }).centre;

  const powerStationTransform = Matrix.Translation(
    -powerStationPivot.x, -powerStationPivot.y, -powerStationPivot.z,
  )
    // Stretch first, in the building's OWN axes — after the rotation its local width
    // would have swung onto a world axis and 'wider' would mean something else.
    .multiply(Matrix.Scaling(POWER_STATION_WIDTH_SCALE, 1, POWER_STATION_DEPTH_SCALE))
    // Clockwise seen from above: +Z swings to +X.
    .multiply(Matrix.RotationY(POWER_STATION_ROTATION))
    .multiply(Matrix.Translation(
      powerStationPivot.x + POWER_STATION_OFFSET.x,
      powerStationPivot.y + POWER_STATION_OFFSET.y,
      powerStationPivot.z + POWER_STATION_OFFSET.z,
    ));

  for (const [acc, mark] of powerStationMarks) {
    acc.transformFrom(mark, powerStationTransform);
  }

  /*
   * THE REFINERY — the whole starboard mass, moved as one unit.
   *
   * Spans four sections: the process wing, the high compression plant, the utility
   * backbone and the outboard works. They are one building to the player, so the mark
   * opens here and closes at the service skin, which is a separate layer.
   */
  const REFINERY_OFFSET = { x: 0, y: -0.5, z: 0 };
  const refineryMarks = [steel, plate, yellow, dark, openGrate, machined]
    .map((acc) => [acc, acc.vertexMark] as const);

  // STARBOARD PROCESS WING — a two-storey separator and overhead pipe-rack bay.
  const sideBayXMin = 13.2;
  const sideBayXMax = 31.2;
  const sideBayZMin = -15.2;
  const sideBayZMax = 2.1;
  const sideBayMidZ = (sideBayZMin + sideBayZMax) / 2;
  const sideBayDeckY = DECK_Y - 0.08;
  const sideBayGalleryY = DECK_Y + 5.0;
  const sideBayTopY = DECK_Y + 14.2;

  plate.add(box, boxTransform(
    (sideBayXMin + sideBayXMax) / 2,
    sideBayDeckY,
    sideBayMidZ,
    sideBayXMax - sideBayXMin,
    0.34,
    sideBayZMax - sideBayZMin,
  ), 4);
  for (const z of [sideBayZMin, sideBayZMax]) {
    steel.add(box, boxTransform(
      (sideBayXMin + sideBayXMax) / 2,
      sideBayDeckY - 0.7,
      z,
      sideBayXMax - sideBayXMin,
      1.05,
      0.55,
    ), 3);
  }
  for (const x of [sideBayXMin, 19.2, 25.2, sideBayXMax]) {
    for (const z of [sideBayZMin + 0.25, sideBayZMax - 0.25]) {
      steel.add(box, memberTransform(
        new Vector3(x, MAIN_DECK_Y - 0.5, z),
        new Vector3(x, sideBayTopY, z),
        0.48,
      ), 3);
    }
    steel.add(box, memberTransform(
      new Vector3(x, sideBayTopY, sideBayZMin),
      new Vector3(x, sideBayTopY, sideBayZMax),
      0.46,
    ), 3);
    steel.add(box, memberTransform(
      new Vector3(x, sideBayGalleryY, sideBayZMin),
      new Vector3(x, sideBayGalleryY, sideBayZMax),
      0.38,
    ), 3);
  }

  // Cross-bracing turns the silhouette into load-bearing structure rather than a fence.
  for (let bay = 0; bay < 3; bay++) {
    const x0 = sideBayXMin + bay * 6.0;
    const x1 = Math.min(sideBayXMax, x0 + 6.0);
    for (const z of [sideBayZMin + 0.25, sideBayZMax - 0.25]) {
      steel.add(box, memberTransform(
        new Vector3(x0, sideBayDeckY + 0.1, z),
        new Vector3(x1, sideBayGalleryY, z),
        0.31,
      ), 3);
      steel.add(box, memberTransform(
        new Vector3(x1, sideBayDeckY + 0.1, z),
        new Vector3(x0, sideBayGalleryY, z),
        0.31,
      ), 3);
      steel.add(box, memberTransform(
        new Vector3(x0, sideBayGalleryY, z),
        new Vector3(x1, sideBayTopY, z),
        0.34,
      ), 3);
    }
  }

  // Mid-level access gallery and the safety rail language approved on the hero deck.
  plate.add(box, boxTransform(
    (sideBayXMin + sideBayXMax) / 2,
    sideBayGalleryY,
    sideBayZMax - 0.8,
    sideBayXMax - sideBayXMin,
    0.25,
    1.6,
  ), 3);
  addRailRun(
    sideBayGalleryY,
    sideBayXMin,
    sideBayZMax + 0.05,
    sideBayXMax,
    sideBayZMax + 0.05,
    1.5,
  );
  addRailRun(
    sideBayDeckY + 0.18,
    sideBayXMin,
    sideBayZMin,
    sideBayXMax,
    sideBayZMin,
    1.5,
  );

  // A horizontal knockout drum, bands, saddle feet and service nozzles form the heavy
  // centrepiece; machinery remains visible through the open structural frame.
  const sideDrumA = new Vector3(16.0, DECK_Y + 3.1, sideBayMidZ);
  const sideDrumB = new Vector3(27.8, DECK_Y + 3.1, sideBayMidZ);
  dark.add(cylHi, memberTransform(sideDrumA, sideDrumB, 2.65), 4);
  for (const bandT of [0.12, 0.42, 0.72, 0.94]) {
    const band = Vector3.Lerp(sideDrumA, sideDrumB, bandT);
    steel.add(cylHi, memberTransform(
      new Vector3(band.x - 0.12, band.y, band.z),
      new Vector3(band.x + 0.12, band.y, band.z),
      2.9,
    ), 3);
  }
  for (const x of [18.0, 25.7]) {
    steel.add(box, boxTransform(
      x,
      DECK_Y + 1.15,
      sideBayMidZ,
      0.65,
      2.7,
      1.75,
    ), 2);
    plate.add(box, boxTransform(
      x,
      DECK_Y + 0.1,
      sideBayMidZ,
      2.2,
      0.22,
      2.15,
    ), 2);
  }
  addPipeRun(steel, [
    new Vector3(19.0, DECK_Y + 4.45, sideBayMidZ),
    new Vector3(19.0, DECK_Y + 8.6, sideBayMidZ),
    new Vector3(24.0, DECK_Y + 8.6, sideBayMidZ),
  ], 0.44, 7);
  addValveWheel(new Vector3(19.0, DECK_Y + 7.0, sideBayMidZ - 0.48), 0.6);

  // Five unequal overhead process lines make the high frame useful and give the limit
  // view long perspective runs instead of an empty sky rectangle.
  const overheadPipeZ = [-14.0, -11.1, -7.6, -3.4, 0.8];
  for (let pipe = 0; pipe < overheadPipeZ.length; pipe++) {
    const z = overheadPipeZ[pipe] as number;
    const diameter = pipe === 1 ? 0.58 : pipe === 3 ? 0.42 : 0.3;
    const acc = pipe === 3 ? yellow : pipe % 2 === 0 ? dark : steel;
    acc.add(cyl, memberTransform(
      new Vector3(sideBayXMin - 0.4, sideBayTopY - 1.05 - (pipe % 2) * 0.5, z),
      new Vector3(sideBayXMax + 0.8, sideBayTopY - 1.05 - (pipe % 2) * 0.5, z),
      diameter,
    ), 7);
    for (const x of [15.0, 21.0, 27.0, 30.4]) {
      steel.add(box, boxTransform(
        x,
        sideBayTopY - 1.85,
        z,
        diameter + 0.3,
        0.22,
        diameter + 0.34,
      ), 2);
    }
  }

  // Pumps, an upright filter vessel and a switchback stair complete the human-scale
  // working layer below the pipe rack.
  for (const x of [15.3, 18.3, 28.7]) {
    dark.add(cylHi, memberTransform(
      new Vector3(x - 0.8, DECK_Y + 0.8, sideBayZMin + 1.2),
      new Vector3(x + 0.8, DECK_Y + 0.8, sideBayZMin + 1.2),
      1.15,
    ), 3);
    steel.add(box, boxTransform(
      x,
      DECK_Y + 0.32,
      sideBayZMin + 1.2,
      1.1,
      0.65,
      1.5,
    ), 2);
  }
  plate.add(cylHi, boxTransform(
    29.2,
    DECK_Y + 6.0,
    sideBayZMax - 1.5,
    3.0,
    10.5,
    3.0,
  ), 4);
  for (const y of [DECK_Y + 2.4, DECK_Y + 6.0, DECK_Y + 9.5]) {
    steel.add(cylHi, boxTransform(
      29.2,
      y,
      sideBayZMax - 1.5,
      3.35,
      0.26,
      3.35,
    ), 3);
  }
  addCagedLadder(27.35, sideBayZMax - 1.55, sideBayDeckY + 0.2, sideBayTopY - 0.4);
  addStairFlight(
    14.55,
    sideBayZMin + 0.7,
    sideBayGalleryY,
    sideBayDeckY + 0.2,
    sideBayZMax - sideBayZMin - 1.6,
    1.45,
  );

  // The marked starboard bay is not an empty skeleton: it is a complete process module.
  // Keep every part inside the existing side-bay frame so the separate tower-bridge void
  // remains open. Layered equipment, access and pipework replace the sky seen through
  // the cage without turning it into one flat wall.
  const sideBayUpperGalleryY = DECK_Y + 10.15;
  const sideBayUpperFrontZ = sideBayZMax - 0.95;

  // A second grated gallery establishes a real third working level. It is deliberately
  // narrow: storm light can still pass between the machinery and the rear frame.
  openGrate.add(box, boxTransform(
    (sideBayXMin + sideBayXMax) / 2,
    sideBayUpperGalleryY,
    sideBayUpperFrontZ,
    sideBayXMax - sideBayXMin,
    0.22,
    1.85,
  ), 4);
  steel.add(box, boxTransform(
    (sideBayXMin + sideBayXMax) / 2,
    sideBayUpperGalleryY - 0.25,
    sideBayUpperFrontZ + 0.86,
    sideBayXMax - sideBayXMin,
    0.42,
    0.26,
  ), 3);
  addRailRun(
    sideBayUpperGalleryY,
    sideBayXMin,
    sideBayZMax,
    sideBayXMax,
    sideBayZMax,
    1.45,
  );
  for (const x of [sideBayXMin, 19.2, 25.2, sideBayXMax]) {
    steel.add(box, memberTransform(
      new Vector3(x, sideBayUpperGalleryY - 0.1, sideBayUpperFrontZ - 0.72),
      new Vector3(x, sideBayUpperGalleryY - 2.35, sideBayZMax - 2.8),
      0.25,
    ), 3);
  }
  addStairFlight(
    sideBayXMax - 2.15,
    sideBayZMax - 1.15,
    sideBayUpperGalleryY,
    sideBayGalleryY + 0.15,
    -6.15,
    1.35,
  );

  // Two air-cooler cells occupy the high centre of the frame. Their louvred intake
  // faces, fan drums and tapered plenums read as machinery from below, not as buildings.
  const coolerCells = [
    { x: 17.15, z: -8.4, width: 5.05, depth: 5.4 },
    { x: 22.75, z: -8.4, width: 5.05, depth: 5.4 },
  ] as const;
  const coolerCentreY = DECK_Y + 10.85;
  for (const cooler of coolerCells) {
    const coolerFrontZ = cooler.z + cooler.depth / 2;
    plate.add(box, boxTransform(
      cooler.x,
      coolerCentreY,
      cooler.z,
      cooler.width,
      2.35,
      cooler.depth,
    ), 2.7);

    // Recessed intake, closely spaced fins and perimeter angle frame.
    dark.add(box, boxTransform(
      cooler.x,
      coolerCentreY,
      coolerFrontZ + 0.08,
      cooler.width - 0.48,
      1.78,
      0.14,
    ), 2);
    for (let fin = 0; fin < 9; fin++) {
      steel.add(box, boxTransform(
        cooler.x,
        coolerCentreY - 0.72 + fin * 0.18,
        coolerFrontZ + 0.17,
        cooler.width - 0.68,
        0.055,
        0.12,
      ), 1.5);
    }
    for (const x of [
      cooler.x - cooler.width / 2 + 0.18,
      cooler.x,
      cooler.x + cooler.width / 2 - 0.18,
    ]) {
      steel.add(box, boxTransform(
        x,
        coolerCentreY,
        coolerFrontZ + 0.2,
        0.13,
        2.08,
        0.13,
      ), 2);
    }

    // Fan rings and hubs on the roof survive the high-angle diagnostic view.
    dark.add(cylHi, boxTransform(
      cooler.x,
      coolerCentreY + 1.28,
      cooler.z,
      3.45,
      0.24,
      3.45,
    ), 3);
    steel.add(cylHi, boxTransform(
      cooler.x,
      coolerCentreY + 1.43,
      cooler.z,
      0.72,
      0.38,
      0.72,
    ), 2);
    for (let blade = 0; blade < 6; blade++) {
      const angle = (blade / 6) * Math.PI * 2;
      steel.add(box, boxYawTransform(
        cooler.x + Math.cos(angle) * 0.8,
        coolerCentreY + 1.43,
        cooler.z + Math.sin(angle) * 0.8,
        1.65,
        0.09,
        0.34,
        -angle,
      ), 1.5);
    }

    // Four legs and a funnelled underside make the cooler's load path legible.
    for (const xOffset of [-1.85, 1.85]) {
      for (const zOffset of [-1.75, 1.75]) {
        steel.add(box, memberTransform(
          new Vector3(
            cooler.x + xOffset,
            sideBayGalleryY + 0.15,
            cooler.z + zOffset,
          ),
          new Vector3(
            cooler.x + xOffset,
            coolerCentreY - 1.1,
            cooler.z + zOffset,
          ),
          0.27,
        ), 2.5);
      }
    }
    dark.add(box, boxTransform(
      cooler.x,
      coolerCentreY - 1.52,
      cooler.z,
      cooler.width - 0.9,
      0.55,
      cooler.depth - 0.9,
    ), 2.3);
  }

  // A staggered utility/MCC enclosure occupies the hard-starboard rear corner. Its
  // setbacks leave visible passages on three sides, while panel seams, doors and vents
  // prevent the volume from reading as an anonymous grey cube.
  const utilityHouseX = 28.35;
  const utilityHouseY = DECK_Y + 8.7;
  const utilityHouseZ = -8.6;
  const utilityHouseWidth = 4.7;
  const utilityHouseHeight = 5.9;
  const utilityHouseDepth = 5.6;
  plate.add(box, boxTransform(
    utilityHouseX,
    utilityHouseY,
    utilityHouseZ,
    utilityHouseWidth,
    utilityHouseHeight,
    utilityHouseDepth,
  ), 2.5);
  plate.add(box, boxTransform(
    utilityHouseX,
    utilityHouseY + utilityHouseHeight / 2 + 0.2,
    utilityHouseZ,
    utilityHouseWidth + 0.5,
    0.28,
    utilityHouseDepth + 0.5,
  ), 2.5);
  const utilityFrontZ = utilityHouseZ + utilityHouseDepth / 2;
  for (const x of [
    utilityHouseX - utilityHouseWidth / 2,
    utilityHouseX,
    utilityHouseX + utilityHouseWidth / 2,
  ]) {
    dark.add(box, boxTransform(
      x,
      utilityHouseY,
      utilityFrontZ + 0.09,
      0.12,
      utilityHouseHeight - 0.35,
      0.14,
    ), 1.8);
  }
  for (let seam = 0; seam < 4; seam++) {
    dark.add(box, boxTransform(
      utilityHouseX,
      utilityHouseY - 2.0 + seam * 1.28,
      utilityFrontZ + 0.1,
      utilityHouseWidth - 0.2,
      0.10,
      0.15,
    ), 1.8);
  }
  dark.add(box, boxTransform(
    utilityHouseX - 1.18,
    utilityHouseY - 0.45,
    utilityFrontZ + 0.18,
    1.45,
    3.95,
    0.16,
  ), 2);
  steel.add(box, boxTransform(
    utilityHouseX - 0.72,
    utilityHouseY - 0.25,
    utilityFrontZ + 0.29,
    0.13,
    0.48,
    0.10,
  ), 2);
  for (let louvre = 0; louvre < 7; louvre++) {
    dark.add(box, boxTransform(
      utilityHouseX + 1.05,
      utilityHouseY - 0.62 + louvre * 0.24,
      utilityFrontZ + 0.2,
      1.35,
      0.07,
      0.13,
    ), 1.5);
  }

  // Twin polishing vessels fill the rear bay with vertical process rhythm. Unequal
  // heights and dense nozzle connections stop them looking like duplicated props.
  for (const [x, z, height, radius] of [
    [15.25, sideBayZMin + 2.0, 8.8, 1.25],
    [18.35, sideBayZMin + 1.8, 7.1, 1.05],
  ] as const) {
    const centreY = sideBayDeckY + 0.2 + height / 2;
    dark.add(cylHi, boxTransform(
      x,
      centreY,
      z,
      radius * 2,
      height,
      radius * 2,
    ), 3);
    for (const ringY of [
      sideBayDeckY + 1.35,
      sideBayDeckY + height * 0.56,
      sideBayDeckY + height - 0.55,
    ]) {
      steel.add(cylHi, boxTransform(
        x,
        ringY,
        z,
        radius * 2.18,
        0.18,
        radius * 2.18,
      ), 2);
    }
    steel.add(cyl, memberTransform(
      new Vector3(x, sideBayDeckY + height, z),
      new Vector3(x, sideBayTopY - 0.8, z),
      0.26,
    ), 4);
  }

  // A far-side manifold wall fills the remaining sky leaks with real depth. It is a
  // rack of independent members and equipment skids, never a scenery panel: gaps remain
  // visible between the pipes, and operators can reach every valve from the rear gallery.
  const rearRackZ = sideBayZMin + 0.62;
  const rearRackLevels = [
    DECK_Y + 3.65,
    DECK_Y + 7.55,
    DECK_Y + 11.45,
  ];
  for (const y of rearRackLevels) {
    steel.add(box, boxTransform(
      (sideBayXMin + sideBayXMax) / 2,
      y,
      rearRackZ,
      sideBayXMax - sideBayXMin - 0.9,
      0.34,
      0.42,
    ), 2.5);
  }
  openGrate.add(box, boxTransform(
    (sideBayXMin + sideBayXMax) / 2,
    rearRackLevels[1] as number,
    rearRackZ + 0.78,
    sideBayXMax - sideBayXMin - 1.15,
    0.20,
    1.55,
  ), 3);
  addRailRun(
    rearRackLevels[1] as number,
    sideBayXMin + 0.55,
    rearRackZ + 1.55,
    sideBayXMax - 0.55,
    rearRackZ + 1.55,
    1.4,
  );

  for (let riser = 0; riser < 9; riser++) {
    const x = sideBayXMin + 1.0 + riser * 1.95;
    const radius = riser % 4 === 0 ? 0.38 : riser % 3 === 0 ? 0.28 : 0.18;
    const acc = riser === 5 ? yellow : riser % 2 === 0 ? steel : dark;
    acc.add(cyl, memberTransform(
      new Vector3(x, sideBayDeckY + 0.35, rearRackZ),
      new Vector3(x, sideBayTopY - 0.75 - (riser % 3) * 0.5, rearRackZ),
      radius,
    ), 6);
    acc.add(cyl, memberTransform(
      new Vector3(x, sideBayTopY - 1.0 - (riser % 3) * 0.5, rearRackZ),
      new Vector3(x + 1.25, sideBayTopY - 1.0 - (riser % 3) * 0.5, rearRackZ),
      radius,
    ), 6);
    if (riser % 2 === 0) {
      addValveWheel(
        new Vector3(x, DECK_Y + 6.15 + (riser % 3) * 0.72, rearRackZ + 0.34),
        0.35 + radius * 0.45,
      );
    }
  }

  // Cartridge-filter skids sit behind the knockout drum and remain visible through its
  // saddle gap. Offset doors, top headers and lifting lugs give each skid its own logic.
  for (const [x, width, height] of [
    [22.1, 3.1, 4.8],
    [26.0, 3.5, 5.7],
  ] as const) {
    const centreY = sideBayDeckY + 0.2 + height / 2;
    const z = rearRackZ + 1.25;
    plate.add(box, boxTransform(
      x,
      centreY,
      z,
      width,
      height,
      2.0,
    ), 2.4);
    dark.add(box, boxTransform(
      x - width * 0.17,
      centreY - 0.25,
      z + 1.08,
      width * 0.46,
      height * 0.65,
      0.13,
    ), 1.7);
    for (const xOffset of [-width * 0.28, width * 0.28]) {
      steel.add(box, boxTransform(
        x + xOffset,
        centreY,
        z + 1.12,
        0.12,
        height - 0.25,
        0.12,
      ), 1.8);
      yellow.add(box, memberTransform(
        new Vector3(x + xOffset - 0.28, sideBayDeckY + height + 0.35, z),
        new Vector3(x + xOffset + 0.28, sideBayDeckY + height + 0.35, z),
        0.10,
      ), 1.5);
    }
    steel.add(cyl, memberTransform(
      new Vector3(x, sideBayDeckY + height + 0.2, z),
      new Vector3(x, sideBayDeckY + height + 1.25, z),
      0.22,
    ), 4);
  }

  // A proper header-and-riser system ties the new equipment into the existing drum.
  // Different diameters and routes create foreground/background overlap from the player.
  for (let line = 0; line < 6; line++) {
    const z = -13.7 + line * 2.15;
    const y = DECK_Y + 12.45 - (line % 3) * 0.55;
    const radius = line === 1 ? 0.34 : line === 4 ? 0.27 : 0.19;
    const acc = line === 2 ? yellow : line % 2 === 0 ? steel : dark;
    acc.add(cyl, memberTransform(
      new Vector3(sideBayXMin + 0.65, y, z),
      new Vector3(sideBayXMax - 0.65, y, z),
      radius,
    ), 6);
  }
  for (const [x, z, topY] of [
    [16.2, -13.7, DECK_Y + 12.45],
    [20.8, -9.4, DECK_Y + 11.9],
    [25.5, -5.1, DECK_Y + 11.35],
    [30.2, -3.0, DECK_Y + 12.45],
  ] as const) {
    addPipeRun(steel, [
      new Vector3(x, DECK_Y + 1.0, z),
      new Vector3(x, topY, z),
      new Vector3(x + 1.15, topY, z),
    ], 0.24, 6);
    addValveWheel(new Vector3(x, DECK_Y + 6.45, z - 0.34), 0.42);
  }

  // An elevated cable tray and clustered field boxes finish the working layer at a
  // scale the player can read from the outer deck.
  const trayY = DECK_Y + 8.05;
  const trayZ = sideBayZMax - 2.75;
  for (const xOffset of [-0.55, 0.55]) {
    steel.add(box, boxTransform(
      (sideBayXMin + sideBayXMax) / 2,
      trayY,
      trayZ + xOffset,
      sideBayXMax - sideBayXMin - 1.3,
      0.18,
      0.14,
    ), 2);
  }
  for (let rung = 0; rung <= 18; rung++) {
    steel.add(box, boxTransform(
      sideBayXMin + 0.65 + rung * (sideBayXMax - sideBayXMin - 1.3) / 18,
      trayY,
      trayZ,
      0.12,
      0.16,
      1.18,
    ), 1.5);
  }
  for (const x of [20.0, 22.0, 24.0]) {
    plate.add(box, boxTransform(
      x,
      sideBayGalleryY + 1.35,
      sideBayZMax - 0.55,
      1.25,
      1.7,
      0.52,
    ), 2);
    dark.add(box, boxTransform(
      x,
      sideBayGalleryY + 1.35,
      sideBayZMax - 0.84,
      0.72,
      0.88,
      0.08,
    ), 1.5);
  }

  // Enclosed survival craft and their gravity-launch davits bring a controlled safety
  // colour to the outer face. A vertex tint keeps them in the existing painted-metal
  // accumulator, so the approved yellow railings and the five-material draw-call budget
  // remain unchanged.
  const safetyOrange = [0.96, 0.45, 0.12] as const;
  const survivalCraftX = sideBayXMax + 2.25;
  const survivalCraftY = DECK_Y + 4.05;
  for (const craftZ of [-11.25, -4.3]) {
    // Stepped cylindrical sections give the hull a blunt enclosed-lifeboat profile.
    yellow.add(cylHi, memberTransform(
      new Vector3(survivalCraftX, survivalCraftY, craftZ - 2.15),
      new Vector3(survivalCraftX, survivalCraftY, craftZ + 2.15),
      1.18,
    ), 2.4, safetyOrange);
    yellow.add(cylHi, memberTransform(
      new Vector3(survivalCraftX, survivalCraftY, craftZ - 2.85),
      new Vector3(survivalCraftX, survivalCraftY, craftZ - 2.05),
      0.82,
    ), 2.4, safetyOrange);
    yellow.add(cylHi, memberTransform(
      new Vector3(survivalCraftX, survivalCraftY, craftZ + 2.05),
      new Vector3(survivalCraftX, survivalCraftY, craftZ + 2.72),
      0.98,
    ), 2.4, safetyOrange);
    yellow.add(box, boxTransform(
      survivalCraftX,
      survivalCraftY - 0.18,
      craftZ,
      2.08,
      1.58,
      4.62,
    ), 2.2, safetyOrange);
    yellow.add(box, boxTransform(
      survivalCraftX,
      survivalCraftY + 0.86,
      craftZ,
      1.8,
      0.82,
      2.95,
    ), 2.1, safetyOrange);
    yellow.add(box, boxTransform(
      survivalCraftX,
      survivalCraftY - 1.08,
      craftZ,
      0.5,
      0.28,
      4.65,
    ), 2.1, safetyOrange);

    // Dark wraparound glazing, steel hull bands and side/stern service hatches.
    for (const side of [-1, 1]) {
      dark.add(box, boxTransform(
        survivalCraftX + side * 1.06,
        survivalCraftY + 0.72,
        craftZ,
        0.08,
        0.46,
        2.45,
      ), 2);
      dark.add(box, boxTransform(
        survivalCraftX + side * 1.07,
        survivalCraftY - 0.18,
        craftZ - 1.35,
        0.08,
        0.82,
        0.72,
      ), 2);
    }
    for (const zOffset of [-1.62, 1.62]) {
      steel.add(cylHi, memberTransform(
        new Vector3(survivalCraftX, survivalCraftY, craftZ + zOffset - 0.08),
        new Vector3(survivalCraftX, survivalCraftY, craftZ + zOffset + 0.08),
        1.24,
      ), 2.5);
    }
    dark.add(box, boxTransform(
      survivalCraftX,
      survivalCraftY,
      craftZ - 2.87,
      1.2,
      1.18,
      0.08,
    ), 2);

    // The davit is anchored to the process frame and leans outboard over the craft.
    for (const zOffset of [-2.0, 2.0]) {
      const anchor = new Vector3(
        sideBayXMax - 0.1,
        sideBayUpperGalleryY + 1.65,
        craftZ + zOffset,
      );
      const head = new Vector3(
        survivalCraftX + 1.2,
        sideBayUpperGalleryY + 0.65,
        craftZ + zOffset,
      );
      yellow.add(box, memberTransform(anchor, head, 0.24), 2.2, safetyOrange);
      steel.add(cyl, memberTransform(
        head,
        new Vector3(
          survivalCraftX + 0.7,
          survivalCraftY + 1.15,
          craftZ + zOffset * 0.78,
        ),
        0.055,
      ), 1.5);
      steel.add(box, memberTransform(
        new Vector3(
          sideBayXMax + 0.15,
          sideBayGalleryY + 0.2,
          craftZ + zOffset,
        ),
        new Vector3(
          survivalCraftX - 0.45,
          survivalCraftY - 0.7,
          craftZ + zOffset * 0.8,
        ),
        0.16,
      ), 1.8);
    }

    // Boarding grate and guard rail connect each craft to the module's middle gallery.
    openGrate.add(box, boxTransform(
      sideBayXMax + 0.85,
      sideBayGalleryY,
      craftZ,
      2.25,
      0.20,
      5.15,
    ), 3);
    addRailRun(
      sideBayGalleryY,
      sideBayXMax + 0.05,
      craftZ - 2.5,
      sideBayXMax + 0.05,
      craftZ + 2.5,
      1.4,
    );
  }

  // STARBOARD HIGH COMPRESSION PLANT — continue the separator module upward instead of
  // letting its frame stop after two storeys. The extension remains inside the existing
  // starboard footprint, well clear of the protected tower-bridge opening to its port
  // side. Heavy rooms fill most of each level; galleries, process vessels and pipe gaps
  // preserve real depth and maintenance access.
  const highBayBaseY = sideBayTopY;
  const highBayMidY = DECK_Y + 20.55;
  const highBayTopY = DECK_Y + 27.3;
  const highBayFrames = [sideBayXMin, 19.2, 25.2, sideBayXMax];

  for (const x of highBayFrames) {
    for (const z of [sideBayZMin + 0.25, sideBayZMax - 0.25]) {
      steel.add(box, memberTransform(
        new Vector3(x, highBayBaseY - 0.1, z),
        new Vector3(x, highBayTopY, z),
        0.5,
      ), 3);
    }
    for (const y of [highBayMidY, highBayTopY]) {
      steel.add(box, memberTransform(
        new Vector3(x, y, sideBayZMin),
        new Vector3(x, y, sideBayZMax),
        0.44,
      ), 3);
    }
  }
  for (const y of [highBayMidY, highBayTopY]) {
    for (const z of [sideBayZMin + 0.25, sideBayZMax - 0.25]) {
      steel.add(box, memberTransform(
        new Vector3(sideBayXMin, y, z),
        new Vector3(sideBayXMax, y, z),
        0.45,
      ), 3);
    }
  }

  // Alternating face braces give both new storeys a continuous load path.
  for (let bay = 0; bay < highBayFrames.length - 1; bay++) {
    const x0 = highBayFrames[bay] as number;
    const x1 = highBayFrames[bay + 1] as number;
    for (const z of [sideBayZMin + 0.25, sideBayZMax - 0.25]) {
      steel.add(box, memberTransform(
        new Vector3(x0, highBayBaseY + 0.15, z),
        new Vector3(x1, highBayMidY - 0.15, z),
        0.3,
      ), 3);
      steel.add(box, memberTransform(
        new Vector3(x1, highBayMidY + 0.15, z),
        new Vector3(x0, highBayTopY - 0.15, z),
        0.32,
      ), 3);
    }
  }

  // Two full-depth working decks make the vertical addition usable. Large machinery
  // penetrations are left around the absorbers rather than faking a solid floor.
  openGrate.add(box, boxTransform(
    (sideBayXMin + sideBayXMax) / 2,
    highBayMidY,
    sideBayMidZ,
    sideBayXMax - sideBayXMin,
    0.25,
    sideBayZMax - sideBayZMin,
  ), 4);
  openGrate.add(box, boxTransform(
    (sideBayXMin + sideBayXMax) / 2,
    highBayTopY,
    sideBayMidZ - 1.4,
    sideBayXMax - sideBayXMin,
    0.25,
    sideBayZMax - sideBayZMin - 2.8,
  ), 4);
  for (const y of [highBayMidY, highBayTopY]) {
    addRailRun(y, sideBayXMin, sideBayZMax, sideBayXMax, sideBayZMax, 1.5);
    addRailRun(y, sideBayXMin, sideBayZMin, sideBayXMax, sideBayZMin, 1.5);
  }

  // A compressor house and an electrical/control room are staggered across the two
  // levels. Deep intake recesses, removable panels and roof cowls keep their mass from
  // becoming featureless cladding.
  for (const [x, y, z, width, height, depth] of [
    [20.05, highBayBaseY + 3.0, -8.6, 10.2, 5.65, 8.0],
    [27.75, highBayMidY + 3.0, -7.2, 5.25, 5.55, 6.8],
  ] as const) {
    plate.add(box, boxTransform(x, y, z, width, height, depth), 3);
    steel.add(box, boxTransform(
      x,
      y + height / 2 + 0.13,
      z,
      width + 0.3,
      0.24,
      depth + 0.3,
    ), 2);
    const frontZ = z + depth / 2 + 0.09;
    dark.add(box, boxTransform(
      x,
      y + 0.25,
      frontZ,
      width - 0.65,
      height - 1.05,
      0.16,
    ), 2.2);
    for (let panel = 0; panel < Math.max(3, Math.round(width / 1.5)); panel++) {
      const panelX = x - width / 2 + 0.65
        + panel * (width - 1.3) / Math.max(1, Math.round(width / 1.5) - 1);
      steel.add(box, boxTransform(
        panelX,
        y + 0.2,
        frontZ + 0.1,
        0.1,
        height - 1.3,
        0.1,
      ), 1.7);
    }
    for (let louvre = 0; louvre < 7; louvre++) {
      steel.add(box, boxTransform(
        x,
        y - 0.85 + louvre * 0.28,
        frontZ + 0.13,
        width * 0.58,
        0.07,
        0.1,
      ), 1.5);
    }
    for (const xOffset of [-width * 0.31, width * 0.31]) {
      dark.add(box, boxTransform(
        x + xOffset,
        y + height / 2 + 0.48,
        z,
        Math.min(1.7, width * 0.22),
        0.7,
        Math.min(2.2, depth * 0.32),
      ), 2);
    }
  }

  // Three unequal absorber/surge vessels make the upper plant legible as process
  // equipment rather than another building. Nozzles connect each vessel into the
  // header bank running along the rear frame.
  for (const [x, z, radius, height] of [
    [15.6, -12.5, 1.35, 11.4],
    [24.7, -12.25, 1.12, 9.5],
    [29.45, -12.4, 0.92, 8.15],
  ] as const) {
    const bottomY = highBayBaseY + 0.35;
    dark.add(cylHi, boxTransform(
      x,
      bottomY + height / 2,
      z,
      radius * 2,
      height,
      radius * 2,
    ), 3.5);
    for (const ringY of [
      bottomY + 1.0,
      bottomY + height * 0.52,
      bottomY + height - 0.65,
    ]) {
      steel.add(cylHi, boxTransform(
        x,
        ringY,
        z,
        radius * 2.18,
        0.2,
        radius * 2.18,
      ), 2.5);
    }
    steel.add(box, boxTransform(
      x,
      bottomY - 0.12,
      z,
      radius * 1.4,
      0.8,
      radius * 1.4,
    ), 2);
    addPipeRun(steel, [
      new Vector3(x, bottomY + height, z),
      new Vector3(x, highBayTopY - 1.0, z),
      new Vector3(x + 1.05, highBayTopY - 1.0, z),
    ], radius * 0.24, 6);
    addValveWheel(
      new Vector3(x, bottomY + Math.min(height * 0.62, 6.5), z + radius + 0.18),
      Math.max(0.4, radius * 0.42),
    );
  }

  // Paired switchback stairs complete a continuous route from the existing upper
  // gallery to the roof. Their landings project on the visible outer face.
  addStairFlight(
    sideBayXMax - 1.55,
    sideBayZMin + 1.1,
    highBayMidY,
    highBayBaseY + 0.2,
    sideBayZMax - sideBayZMin - 2.2,
    1.45,
  );
  addStairFlight(
    sideBayXMax - 1.55,
    sideBayZMax - 1.1,
    highBayTopY,
    highBayMidY + 0.2,
    -(sideBayZMax - sideBayZMin - 2.2),
    1.45,
  );
  addCagedLadder(
    sideBayXMin + 0.65,
    sideBayZMax - 0.45,
    highBayMidY + 0.2,
    highBayTopY - 0.35,
  );

  // A dense rear header bank ties the high plant to the separator below. The three
  // restrained safety colours communicate function without recolouring the railings.
  for (let line = 0; line < 7; line++) {
    const y = highBayBaseY + 1.3 + line * 1.65;
    const z = sideBayZMin + 0.58;
    const tint = line === 1
      ? [0.54, 0.12, 0.075] as const
      : line === 4
        ? [0.12, 0.29, 0.48] as const
        : line === 6
          ? [0.62, 0.44, 0.07] as const
          : [0.78, 0.8, 0.79] as const;
    addPipeRun(steel, [
      new Vector3(sideBayXMin + 0.65, y, z),
      new Vector3(sideBayXMax - 0.7, y, z),
    ], line % 3 === 0 ? 0.32 : 0.22, 6, true, tint);
  }
  for (const [x, z, topY] of [
    [17.1, -13.55, highBayMidY + 2.5],
    [21.9, -13.45, highBayTopY - 1.0],
    [27.25, -13.6, highBayMidY + 4.0],
  ] as const) {
    addPipeRun(steel, [
      new Vector3(x, highBayBaseY + 0.4, z),
      new Vector3(x, topY, z),
      new Vector3(x + 1.15, topY, z),
    ], 0.27, 7);
  }

  // Cable ladders, field boxes, emergency cabinets and roof exhausts provide the
  // close-range operational detail that should accompany—not replace—the large mass.
  addCableTray(
    sideBayXMin + 0.55,
    sideBayZMax - 0.55,
    sideBayXMax - 0.55,
    sideBayZMax - 0.55,
    highBayMidY - 0.72,
  );
  addCableTray(
    sideBayXMin + 0.55,
    sideBayZMax - 0.85,
    sideBayXMin + 0.55,
    sideBayZMin + 0.65,
    highBayTopY - 0.75,
  );
  for (const [x, y, z, orange] of [
    [17.2, highBayMidY + 1.1, sideBayZMax - 0.3, false],
    [20.0, highBayMidY + 1.1, sideBayZMax - 0.3, false],
    [23.0, highBayTopY + 1.0, sideBayZMax - 0.3, true],
    [29.65, highBayMidY + 1.0, sideBayZMax - 0.3, true],
  ] as const) {
    const tint = orange
      ? [0.76, 0.24, 0.055] as const
      : [0.66, 0.7, 0.69] as const;
    plate.add(box, boxTransform(x, y, z, 1.15, 1.7, 0.55), 2, tint);
    dark.add(box, boxTransform(x, y, z + 0.31, 0.72, 0.92, 0.08), 1.5);
  }
  for (const [x, z, diameter, height] of [
    [18.0, -6.7, 0.95, 3.0],
    [21.25, -7.2, 1.2, 4.1],
    [27.8, -9.9, 0.8, 2.6],
  ] as const) {
    dark.add(cylHi, boxTransform(
      x,
      highBayTopY + height / 2,
      z,
      diameter,
      height,
      diameter,
    ), 3);
    steel.add(cylHi, boxTransform(
      x,
      highBayTopY + height,
      z,
      diameter * 1.35,
      0.22,
      diameter * 1.35,
    ), 2);
  }

  // STARBOARD ACCOMMODATION / UTILITY BACKBONE — the process bay now has a real body
  // behind it. Three setback masses rise from the rear edge of the module, like the
  // occupied accommodation and service towers in the rendered massing references. The
  // nearest face stays behind the rear pipe rack, so existing vessels, galleries and the
  // protected bridge opening retain their depth and silhouettes.
  const starboardHouseNearZ = -14.72;
  const starboardHouseCentreX = 23.5;
  const starboardHouseBlocks = [
    {
      x: starboardHouseCentreX,
      z: -19.35,
      width: 18.0,
      depth: 9.25,
      bottom: DECK_Y - 0.25,
      height: 17.2,
    },
    {
      x: starboardHouseCentreX - 0.25,
      z: -19.85,
      width: 16.0,
      depth: 8.2,
      bottom: DECK_Y + 16.75,
      height: 13.15,
    },
    {
      x: starboardHouseCentreX - 0.7,
      z: -20.55,
      width: 13.5,
      depth: 6.75,
      bottom: DECK_Y + 29.65,
      height: 10.6,
    },
  ] as const;

  for (const blockSpec of starboardHouseBlocks) {
    const centreY = blockSpec.bottom + blockSpec.height / 2;
    const frontZ = blockSpec.z + blockSpec.depth / 2;
    plate.add(box, boxTransform(
      blockSpec.x,
      centreY,
      blockSpec.z,
      blockSpec.width,
      blockSpec.height,
      blockSpec.depth,
    ), 3, [1.18, 1.2, 1.18]);
    steel.add(box, boxTransform(
      blockSpec.x,
      blockSpec.bottom + blockSpec.height + 0.14,
      blockSpec.z,
      blockSpec.width + 0.35,
      0.28,
      blockSpec.depth + 0.35,
    ), 2);

    // Deep horizontal and vertical joints break the cladding into replaceable panels.
    const seamRows = Math.max(2, Math.round(blockSpec.height / 3.1));
    for (let row = 1; row < seamRows; row++) {
      dark.add(box, boxTransform(
        blockSpec.x,
        blockSpec.bottom + row * blockSpec.height / seamRows,
        frontZ + 0.09,
        blockSpec.width - 0.25,
        0.09,
        0.12,
      ), 1.8);
    }
    const seamColumns = Math.max(3, Math.round(blockSpec.width / 2.1));
    for (let column = 1; column < seamColumns; column++) {
      dark.add(box, boxTransform(
        blockSpec.x - blockSpec.width / 2 + column * blockSpec.width / seamColumns,
        centreY,
        frontZ + 0.1,
        0.09,
        blockSpec.height - 0.3,
        0.12,
      ), 1.8);
    }

    // Narrow recessed windows follow occupied floor levels. Some bays are intentionally
    // blank or louvred, avoiding the regular grid of a generic office building.
    const floorCount = Math.max(2, Math.floor(blockSpec.height / 3.15));
    const windowColumns = Math.max(4, Math.floor(blockSpec.width / 1.75));
    for (let floor = 0; floor < floorCount; floor++) {
      const y = blockSpec.bottom + 1.65 + floor * 3.05;
      for (let column = 0; column < windowColumns; column++) {
        if ((floor * 3 + column * 2) % 7 === 2) continue;
        const x = blockSpec.x - blockSpec.width * 0.4
          + column * blockSpec.width * 0.8 / Math.max(1, windowColumns - 1);
        if ((floor + column) % 5 === 0) {
          for (let slat = 0; slat < 4; slat++) {
            dark.add(box, boxTransform(
              x,
              y - 0.35 + slat * 0.23,
              frontZ + 0.18,
              0.92,
              0.07,
              0.1,
            ), 1.5);
          }
        } else {
          dark.add(box, boxTransform(
            x,
            y,
            frontZ + 0.17,
            0.88,
            1.18,
            0.12,
          ), 1.7);
          steel.add(box, boxTransform(
            x,
            y,
            frontZ + 0.25,
            0.08,
            1.08,
            0.08,
          ), 1.4);
        }
      }
    }
  }

  // Six external galleries turn the façade into repeated human-scale working floors.
  // Their widths track the setbacks above instead of floating past the building edges.
  const starboardHouseGalleryLevels = [
    DECK_Y + 4.9,
    DECK_Y + 10.8,
    DECK_Y + 16.8,
    DECK_Y + 23.2,
    DECK_Y + 29.75,
    DECK_Y + 36.1,
  ];
  for (let level = 0; level < starboardHouseGalleryLevels.length; level++) {
    const y = starboardHouseGalleryLevels[level] as number;
    const width = level < 3 ? 18.25 : level < 5 ? 16.25 : 13.65;
    const centreX = level < 3
      ? starboardHouseCentreX
      : level < 5
        ? starboardHouseCentreX - 0.25
        : starboardHouseCentreX - 0.7;
    const faceZ = level < 3
      ? starboardHouseNearZ
      : level < 5
        ? -15.75
        : -17.15;
    openGrate.add(box, boxTransform(
      centreX,
      y,
      faceZ + 0.85,
      width,
      0.24,
      1.7,
    ), 4);
    addRailRun(
      y,
      centreX - width / 2,
      faceZ + 1.72,
      centreX + width / 2,
      faceZ + 1.72,
      1.5,
    );
    for (const x of [
      centreX - width / 2 + 0.45,
      centreX - width * 0.18,
      centreX + width * 0.18,
      centreX + width / 2 - 0.45,
    ]) {
      steel.add(box, memberTransform(
        new Vector3(x, y - 0.15, faceZ + 1.45),
        new Vector3(x, y - 2.4, faceZ + 0.05),
        0.22,
      ), 2.5);
    }
  }

  // The player reaches this tower from its west side, so that face receives its own
  // windows and full-depth galleries. Without this layer the large backbone existed but
  // appeared only as a dark edge from the actual free-look camera.
  for (const blockSpec of starboardHouseBlocks) {
    const westFaceX = blockSpec.x - blockSpec.width / 2;
    const floorCount = Math.max(2, Math.floor(blockSpec.height / 3.15));
    const windowColumns = Math.max(3, Math.floor(blockSpec.depth / 1.55));
    for (let floor = 0; floor < floorCount; floor++) {
      const y = blockSpec.bottom + 1.65 + floor * 3.05;
      for (let column = 0; column < windowColumns; column++) {
        if ((floor * 2 + column * 3) % 8 === 3) continue;
        const z = blockSpec.z - blockSpec.depth * 0.38
          + column * blockSpec.depth * 0.76 / Math.max(1, windowColumns - 1);
        dark.add(box, boxTransform(
          westFaceX - 0.1,
          y,
          z,
          0.12,
          1.16,
          0.9,
        ), 1.8);
        steel.add(box, boxTransform(
          westFaceX - 0.18,
          y,
          z,
          0.08,
          1.06,
          0.08,
        ), 1.4);
      }
    }
  }
  for (let level = 0; level < starboardHouseGalleryLevels.length; level++) {
    const y = starboardHouseGalleryLevels[level] as number;
    const blockSpec = level < 3
      ? starboardHouseBlocks[0]
      : level < 5
        ? starboardHouseBlocks[1]
        : starboardHouseBlocks[2];
    const westFaceX = blockSpec.x - blockSpec.width / 2;
    const galleryX = westFaceX - 0.82;
    const galleryDepth = blockSpec.depth + 0.35;
    openGrate.add(box, boxTransform(
      galleryX,
      y,
      blockSpec.z,
      1.65,
      0.24,
      galleryDepth,
    ), 4);
    addRailRun(
      y,
      galleryX - 0.85,
      blockSpec.z - galleryDepth / 2,
      galleryX - 0.85,
      blockSpec.z + galleryDepth / 2,
      1.45,
    );
    for (const z of [
      blockSpec.z - galleryDepth * 0.38,
      blockSpec.z,
      blockSpec.z + galleryDepth * 0.38,
    ]) {
      steel.add(box, memberTransform(
        new Vector3(galleryX - 0.65, y - 0.15, z),
        new Vector3(westFaceX + 0.1, y - 2.35, z),
        0.22,
      ), 2.5);
    }
  }

  // Two visible switchback stair towers and a west-face utility rack keep circulation
  // legible along the side the player actually sees.
  for (let flight = 0; flight < starboardHouseGalleryLevels.length - 1; flight++) {
    const bottomY = starboardHouseGalleryLevels[flight] as number;
    const topY = starboardHouseGalleryLevels[flight + 1] as number;
    const blockSpec = flight < 2
      ? starboardHouseBlocks[0]
      : flight < 4
        ? starboardHouseBlocks[1]
        : starboardHouseBlocks[2];
    const westFaceX = blockSpec.x - blockSpec.width / 2 - 0.82;
    const depthRun = blockSpec.depth - 2.0;
    addStairFlight(
      westFaceX,
      flight % 2 === 0
        ? blockSpec.z - depthRun / 2
        : blockSpec.z + depthRun / 2,
      topY,
      bottomY + 0.15,
      flight % 2 === 0 ? depthRun : -depthRun,
      1.35,
    );
  }
  for (const [z, tint] of [
    [-22.4, [0.56, 0.13, 0.075]],
    [-20.85, [0.12, 0.3, 0.48]],
    [-19.25, [0.76, 0.78, 0.76]],
    [-17.6, [0.62, 0.45, 0.07]],
  ] as const) {
    addPipeRun(steel, [
      new Vector3(14.05, DECK_Y + 0.35, z),
      new Vector3(14.05, DECK_Y + 28.3, z),
      new Vector3(15.3, DECK_Y + 28.3, z),
    ], 0.24, 7, true, tint);
  }

  // Face-on switchback stairs link every gallery and remain readable from the player's
  // starboard look limit. Alternating directions form a continuous emergency route.
  for (let flight = 0; flight < starboardHouseGalleryLevels.length - 1; flight++) {
    const bottomY = starboardHouseGalleryLevels[flight] as number;
    const topY = starboardHouseGalleryLevels[flight + 1] as number;
    const z = flight < 2 ? -12.82 : flight < 4 ? -14.0 : -15.45;
    const xLeft = flight < 3 ? 20.4 : 20.8;
    const xRight = flight < 3 ? 26.2 : 25.8;
    addFacadeStairFlight(
      flight % 2 === 0 ? xRight : xLeft,
      flight % 2 === 0 ? xLeft : xRight,
      z,
      topY,
      bottomY + 0.15,
      1.35,
    );
  }

  // A full-height service riser wall and manifold banks make the tower visibly part of
  // the process system. Coloured lines are functional accents, not decorative stripes.
  for (let riser = 0; riser < 8; riser++) {
    const x = 23.4 + riser * 0.58;
    const z = -14.05 - (riser % 2) * 0.18;
    const top = DECK_Y + 31.5 - (riser % 3) * 1.4;
    const tint = riser === 1
      ? [0.55, 0.12, 0.07] as const
      : riser === 5
        ? [0.12, 0.3, 0.48] as const
        : riser === 7
          ? [0.62, 0.45, 0.07] as const
          : [0.78, 0.79, 0.77] as const;
    addPipeRun(steel, [
      new Vector3(x, DECK_Y + 0.35, z),
      new Vector3(x, top, z),
      new Vector3(x + 1.25, top, z),
    ], riser % 3 === 0 ? 0.3 : 0.21, 7, true, tint);
    if (riser % 2 === 0) {
      addValveWheel(new Vector3(x, DECK_Y + 8.1 + riser * 1.6, z + 0.35), 0.42);
    }
  }
  for (const y of [DECK_Y + 8.4, DECK_Y + 15.0, DECK_Y + 21.5, DECK_Y + 28.0]) {
    steel.add(box, boxTransform(
      25.45,
      y,
      -14.25,
      5.2,
      0.28,
      0.55,
    ), 2.5);
  }

  // Ventilation trunks and rooftop exhausts give the accommodation stack a plausible
  // plant-room crown instead of a clean rectangular termination.
  for (const [x, bottomY, z, width, depth, height] of [
    [30.4, DECK_Y + 1.0, -14.85, 1.4, 1.2, 19.8],
    [28.7, DECK_Y + 18.0, -16.05, 1.1, 1.0, 14.0],
    [23.2, DECK_Y + 30.0, -17.45, 1.25, 1.1, 9.2],
  ] as const) {
    dark.add(box, boxTransform(
      x,
      bottomY + height / 2,
      z,
      width,
      height,
      depth,
    ), 3);
    for (let collarY = bottomY + 2.5; collarY < bottomY + height; collarY += 4.0) {
      steel.add(box, boxTransform(
        x,
        collarY,
        z,
        width + 0.18,
        0.16,
        depth + 0.18,
      ), 2);
    }
  }
  for (const [x, z, diameter, height] of [
    [21.8, -20.6, 1.15, 4.6],
    [24.7, -20.2, 0.92, 3.7],
    [27.6, -20.8, 1.3, 5.25],
  ] as const) {
    dark.add(cylHi, boxTransform(
      x,
      DECK_Y + 40.35 + height / 2,
      z,
      diameter,
      height,
      diameter,
    ), 3);
    steel.add(cylHi, boxTransform(
      x,
      DECK_Y + 40.35 + height,
      z,
      diameter * 1.4,
      0.24,
      diameter * 1.4,
    ), 2);
  }

  // Equipment doors, rescue lockers and cable routes finish the accessible outer skin.
  const accommodationOrange = [0.76, 0.24, 0.055] as const;
  for (const [x, y, z, orange] of [
    [31.8, DECK_Y + 2.0, -14.55, false],
    [29.8, DECK_Y + 12.7, -14.55, true],
    [30.6, DECK_Y + 19.1, -15.65, false],
    [27.8, DECK_Y + 31.7, -17.05, true],
  ] as const) {
    plate.add(box, boxTransform(
      x,
      y,
      z,
      1.25,
      2.15,
      0.18,
    ), 2, orange ? accommodationOrange : [0.82, 0.84, 0.82]);
    dark.add(box, boxTransform(
      x + 0.42,
      y,
      z + 0.12,
      0.1,
      1.6,
      0.08,
    ), 1.5);
  }
  addCableTray(17.95, -13.78, 32.55, -13.78, DECK_Y + 14.25);
  addCableTray(18.15, -15.25, 31.95, -15.25, DECK_Y + 27.3);

  // STARBOARD OUTBOARD WORKS — fill the marked sky beyond the process cage's outer
  // right leg. This is a connected, inhabited wing rather than another repeated cage:
  // staggered decks carry an accommodation/service block, a cantilevered blue utility
  // pod, pipe walls, rescue equipment, stairs and a crown of ventilation stacks.
  // Everything grows toward negative Z, the screen-right direction at the player's
  // starboard look limit, and therefore stays clear of the protected bridge void.
  const outboardXMin = 14.0;
  const outboardXMax = 36.0;
  const outboardZNear = -12.65;
  const outboardZFar = -35.00;
  const outboardBaseY = DECK_Y - 0.18;
  const outboardLevels = [
    outboardBaseY,
    DECK_Y + 6.25,
    DECK_Y + 13.0,
    DECK_Y + 19.9,
    DECK_Y + 27.0,
    DECK_Y + 34.15,
  ];
  const outboardXFrames = [outboardXMin, 21.5, 28.5, outboardXMax];
  const outboardZFrames = [outboardZNear, -19.60, -27.10, outboardZFar];

  // Staggered plates create terraces and overhangs instead of a stack of identical
  // rectangles. The lower three floors connect directly to the existing process bay.
  for (const [y, x0, x1, z0, z1] of [
    [outboardLevels[0], outboardXMin, outboardXMax, outboardZFar, outboardZNear],
    [outboardLevels[1], outboardXMin, outboardXMax, outboardZFar, outboardZNear],
    [outboardLevels[2], outboardXMin, outboardXMax, outboardZFar, outboardZNear],
    [outboardLevels[3], 17.2, outboardXMax, outboardZFar, -16.50],
    [outboardLevels[4], outboardXMin, 31.2, outboardZFar, -19.40],
    [outboardLevels[5], 19.4, outboardXMax, -33.40, -20.80],
  ] as const) {
    openGrate.add(box, boxTransform(
      (x0 + x1) / 2,
      y,
      (z0 + z1) / 2,
      x1 - x0,
      0.3,
      z1 - z0,
    ), 4);
    addRailRun(y, x0, z0, x1, z0, 1.45);
    addRailRun(y, x1, z0, x1, z1, 1.45);
  }

  // Primary steel continues the load path down through every terrace. Columns at the
  // far edge rise higher than the near edge, giving the wing a stepped skyline.
  for (const x of outboardXFrames) {
    for (let zIndex = 0; zIndex < outboardZFrames.length; zIndex++) {
      const z = outboardZFrames[zIndex] as number;
      const topY = zIndex >= 2
        ? outboardLevels[5] as number
        : outboardLevels[3] as number;
      steel.add(box, memberTransform(
        new Vector3(x, MAIN_DECK_Y - 0.5, z),
        new Vector3(x, topY, z),
        zIndex === 3 ? 0.62 : 0.5,
      ), 3);
    }
  }
  for (let level = 1; level < outboardLevels.length; level++) {
    const y = outboardLevels[level] as number;
    const x0 = level === 4 ? outboardXMin : level === 5 ? 19.4 : outboardXMin;
    const x1 = level === 4 ? 31.2 : outboardXMax;
    const z0 = level === 5 ? -33.40 : outboardZFar;
    const z1 = level === 3 ? -16.50 : level === 4 ? -19.40 : level === 5 ? -20.80 : outboardZNear;
    for (const z of [z0, z1]) {
      steel.add(box, memberTransform(
        new Vector3(x0, y, z),
        new Vector3(x1, y, z),
        0.46,
      ), 3);
    }
    for (const x of [x0, x1]) {
      steel.add(box, memberTransform(
        new Vector3(x, y, z0),
        new Vector3(x, y, z1),
        0.46,
      ), 3);
    }
  }

  // Alternating X braces close the visible far and west faces with believable secondary
  // steel while leaving enough openings for galleries, ducts and colored services.
  for (let bay = 0; bay < outboardZFrames.length - 1; bay++) {
    const z0 = outboardZFrames[bay] as number;
    const z1 = outboardZFrames[bay + 1] as number;
    for (let level = 0; level < 4; level++) {
      const y0 = outboardLevels[level] as number;
      const y1 = outboardLevels[level + 1] as number;
      for (const x of [outboardXMin, outboardXMax]) {
        steel.add(box, memberTransform(
          new Vector3(x, y0 + 0.2, z0),
          new Vector3(x, y1 - 0.2, z1),
          0.3,
        ), 3);
        if ((bay + level) % 2 === 0) {
          steel.add(box, memberTransform(
            new Vector3(x, y0 + 0.2, z1),
            new Vector3(x, y1 - 0.2, z0),
            0.3,
          ), 3);
        }
      }
    }
  }

  // A light accommodation/service block supplies the broad readable mass missing from
  // the marked region. Its offset upper block and cantilevered roof avoid a plain cuboid.
  const outboardHouseTint = [1.22, 1.24, 1.22] as const;
  const outboardHouseX = 27.0;
  const outboardHouseZ = -20.50;
  const outboardHouseWidth = 15.0;
  const outboardHouseDepth = 12.0;
  const outboardHouseBottom = DECK_Y + 6.45;
  const outboardHouseHeight = 19.4;
  plate.add(box, boxTransform(
    outboardHouseX,
    outboardHouseBottom + outboardHouseHeight / 2,
    outboardHouseZ,
    outboardHouseWidth,
    outboardHouseHeight,
    outboardHouseDepth,
  ), 3, outboardHouseTint);
  plate.add(box, boxTransform(
    outboardHouseX - 2.35,
    DECK_Y + 30.3,
    outboardHouseZ - 1.3,
    10.3,
    8.3,
    8.8,
  ), 3, [1.12, 1.16, 1.18]);
  steel.add(box, boxTransform(
    outboardHouseX - 1.8,
    DECK_Y + 34.65,
    outboardHouseZ - 1.25,
    13.5,
    0.38,
    11.5,
  ), 2);

  // Windows, ventilation banks and blue service panels articulate both faces visible
  // from the player camera.
  const houseNearZ = outboardHouseZ + outboardHouseDepth / 2;
  const houseWestX = outboardHouseX - outboardHouseWidth / 2;
  for (let floor = 0; floor < 6; floor++) {
    const y = outboardHouseBottom + 1.7 + floor * 3.0;
    for (let column = 0; column < 7; column++) {
      if ((floor + column * 2) % 8 === 3) continue;
      const x = outboardHouseX - 5.8 + column * 1.92;
      dark.add(box, boxTransform(x, y, houseNearZ + 0.12, 0.9, 1.15, 0.13), 1.7);
      steel.add(box, boxTransform(x, y, houseNearZ + 0.2, 0.08, 1.05, 0.08), 1.4);
    }
    for (let column = 0; column < 5; column++) {
      const z = outboardHouseZ - 4.4 + column * 2.2;
      dark.add(box, boxTransform(houseWestX - 0.12, y, z, 0.13, 1.15, 0.9), 1.7);
      steel.add(box, boxTransform(houseWestX - 0.2, y, z, 0.08, 1.05, 0.08), 1.4);
    }
  }
  for (const [x, y, width, height] of [
    [22.3, DECK_Y + 10.1, 3.3, 3.7],
    [30.2, DECK_Y + 16.4, 4.0, 4.2],
    [24.4, DECK_Y + 23.0, 3.7, 3.4],
  ] as const) {
    plate.add(box, boxTransform(
      x,
      y,
      houseNearZ + 0.18,
      width,
      height,
      0.18,
    ), 2, [0.12, 0.34, 0.58]);
    for (let slat = 0; slat < 7; slat++) {
      steel.add(box, boxTransform(
        x,
        y - height * 0.35 + slat * height * 0.7 / 6,
        houseNearZ + 0.3,
        width - 0.35,
        0.07,
        0.08,
      ), 1.4);
    }
  }

  // A turquoise cantilevered utility pod hangs off the west side with visible knee
  // braces. This strong colored overhang gives the wing a shape unlike the existing bays.
  const utilityPodTint = [0.08, 0.42, 0.52] as const;
  plate.add(box, boxTransform(
    15.4,
    DECK_Y + 16.35,
    -20.90,
    6.6,
    5.2,
    8.4,
  ), 3, utilityPodTint);
  steel.add(box, boxTransform(
    15.4,
    DECK_Y + 19.05,
    -20.90,
    7.2,
    0.24,
    9.0,
  ), 2);
  for (const z of [-24.20, -20.90, -17.60]) {
    steel.add(box, memberTransform(
      new Vector3(12.05, DECK_Y + 13.75, z),
      new Vector3(16.6, DECK_Y + 10.9, z),
      0.32,
    ), 2.5);
  }
  for (let slat = 0; slat < 8; slat++) {
    dark.add(box, boxTransform(
      12.04,
      DECK_Y + 14.55 + slat * 0.48,
      -20.90,
      0.12,
      0.08,
      6.2,
    ), 1.5);
  }

  // Three main process colors run as complete systems through the wing. They turn up
  // into equipment rather than ending as decorative stripes.
  const outboardServiceLines = [
    { x: 15.25, tint: [0.62, 0.1, 0.055] as const, diameter: 0.34, y: DECK_Y + 3.1 },
    { x: 16.25, tint: [0.08, 0.32, 0.62] as const, diameter: 0.3, y: DECK_Y + 4.25 },
    { x: 17.2, tint: [0.74, 0.55, 0.06] as const, diameter: 0.27, y: DECK_Y + 5.35 },
  ];
  for (const line of outboardServiceLines) {
    addPipeRun(steel, [
      new Vector3(line.x, line.y, outboardZNear + 0.4),
      new Vector3(line.x, line.y, outboardZFar + 1.0),
      new Vector3(line.x, DECK_Y + 12.2, outboardZFar + 1.0),
      new Vector3(line.x + 3.4, DECK_Y + 12.2, outboardZFar + 1.0),
    ], line.diameter, 7, true, line.tint);
  }

  // Dense lower-deck pipe wall: unequal risers, cross headers, pumps and valves occupy
  // the space below the accommodation block while leaving a central maintenance aisle.
  for (let riser = 0; riser < 11; riser++) {
    const x = 19.4 + riser * 1.34;
    const z = -17.20 - (riser % 3) * 0.72;
    const topY = DECK_Y + 11.5 - (riser % 4) * 0.55;
    const tint = riser % 5 === 1
      ? [0.1, 0.36, 0.62] as const
      : riser % 5 === 3
        ? [0.62, 0.1, 0.055] as const
        : [0.72, 0.74, 0.72] as const;
    addPipeRun(steel, [
      new Vector3(x, DECK_Y + 0.4, z),
      new Vector3(x, topY, z),
      new Vector3(x + 0.9, topY, z),
    ], riser % 4 === 0 ? 0.32 : 0.22, 6, true, tint);
    if (riser % 2 === 0) {
      addValveWheel(new Vector3(x, DECK_Y + 4.8 + (riser % 3) * 0.8, z + 0.32), 0.42);
    }
  }
  for (const x of [21.0, 25.6, 31.7]) {
    dark.add(cylHi, memberTransform(
      new Vector3(x - 0.85, DECK_Y + 1.0, -15.10),
      new Vector3(x + 0.85, DECK_Y + 1.0, -15.10),
      1.2,
    ), 3);
    plate.add(box, boxTransform(
      x,
      DECK_Y + 0.35,
      -15.10,
      2.7,
      0.55,
      2.2,
    ), 2, [0.16, 0.38, 0.48]);
  }

  // An enclosed orange survival craft and paired davit arms sit on the far terrace.
  const outboardRescueTint = [0.93, 0.34, 0.075] as const;
  const rescueA = new Vector3(32.9, DECK_Y + 9.15, -32.00);
  const rescueB = new Vector3(32.9, DECK_Y + 9.15, -26.80);
  yellow.add(cylHi, memberTransform(rescueA, rescueB, 1.32), 2.5, outboardRescueTint);
  yellow.add(box, boxTransform(
    32.9,
    DECK_Y + 8.75,
    -29.40,
    2.4,
    1.7,
    5.1,
  ), 2.2, outboardRescueTint);
  dark.add(box, boxTransform(
    31.65,
    DECK_Y + 9.55,
    -29.40,
    0.1,
    0.5,
    3.1,
  ), 1.8);
  for (const z of [-31.70, -27.10]) {
    yellow.add(box, memberTransform(
      new Vector3(34.7, DECK_Y + 13.2, z),
      new Vector3(37.7, DECK_Y + 10.4, z),
      0.26,
    ), 2.2, outboardRescueTint);
    steel.add(cyl, memberTransform(
      new Vector3(37.7, DECK_Y + 10.4, z),
      new Vector3(33.7, DECK_Y + 10.2, z),
      0.055,
    ), 1.5);
  }

  // Two independent stair systems make the wing traversable and visually emphasize its
  // depth. A covered diagonal bridge links back to the existing compression plant.
  for (let flight = 0; flight < 4; flight++) {
    const bottomY = outboardLevels[flight] as number;
    const topY = outboardLevels[flight + 1] as number;
    addStairFlight(
      35.0,
      flight % 2 === 0 ? -14.30 : -21.80,
      topY,
      bottomY + 0.2,
      flight % 2 === 0 ? -7.0 : 7.0,
      1.45,
    );
  }
  openGrate.add(box, memberTransform(
    new Vector3(17.0, DECK_Y + 19.9, -13.20),
    new Vector3(24.5, DECK_Y + 27.0, -22.00),
    1.65,
  ), 4);
  for (const offset of [-0.9, 0.9]) {
    yellow.add(cyl, memberTransform(
      new Vector3(17.0 + offset, DECK_Y + 20.9, -13.20),
      new Vector3(24.5 + offset, DECK_Y + 28.0, -22.00),
      0.075,
    ), 2);
  }

  // Roof ventilation crown: paired rectangular trunks, cowls and unequal exhaust stacks
  // create a recognizable skyline rather than ending on another flat slab.
  for (const [x, z, width, depth, height] of [
    [21.6, -30.50, 2.4, 2.0, 7.0],
    [27.0, -31.00, 3.0, 2.2, 9.2],
    [33.0, -28.20, 2.1, 1.8, 6.1],
  ] as const) {
    dark.add(box, boxTransform(
      x,
      DECK_Y + 34.3 + height / 2,
      z,
      width,
      height,
      depth,
    ), 3);
    plate.add(box, boxTransform(
      x + 0.65,
      DECK_Y + 34.3 + height,
      z,
      width + 1.3,
      0.45,
      depth + 0.6,
    ), 2, [1.08, 1.1, 1.08]);
  }
  for (const [x, z, diameter, height] of [
    [20.0, -24.20, 1.0, 5.2],
    [24.1, -26.00, 1.35, 7.4],
    [30.6, -33.00, 0.9, 4.6],
    [34.0, -23.60, 1.15, 6.3],
  ] as const) {
    dark.add(cylHi, boxTransform(
      x,
      DECK_Y + 34.3 + height / 2,
      z,
      diameter,
      height,
      diameter,
    ), 3);
    steel.add(cylHi, boxTransform(
      x,
      DECK_Y + 34.3 + height,
      z,
      diameter * 1.4,
      0.24,
      diameter * 1.4,
    ), 2);
  }

  // Cable ladders and colored emergency/utility boxes complete the visible circulation
  // layer without blocking the central walkways.
  addCableTray(outboardXMin + 0.5, -14.10, outboardXMax - 0.5, -14.10, DECK_Y + 8.2);
  addCableTray(outboardXMin + 0.5, -22.40, outboardXMax - 0.5, -22.40, DECK_Y + 15.0);
  for (const [x, y, z, tint] of [
    [18.8, DECK_Y + 7.25, -13.10, [0.08, 0.38, 0.58]],
    [23.0, DECK_Y + 14.1, -19.80, [0.68, 0.1, 0.055]],
    [34.4, DECK_Y + 20.9, -26.40, [0.88, 0.3, 0.06]],
    [29.4, DECK_Y + 28.1, -33.80, [0.16, 0.42, 0.3]],
  ] as const) {
    plate.add(box, boxTransform(x, y, z, 1.25, 1.85, 0.65), 2, tint);
    dark.add(box, boxTransform(x, y, z + 0.36, 0.72, 0.95, 0.08), 1.5);
  }

  // =================================================================================
  for (const [acc, mark] of refineryMarks) {
    acc.translateFrom(mark, REFINERY_OFFSET.x, REFINERY_OFFSET.y, REFINERY_OFFSET.z);
  }

  // SOUTH-WEST SERVICE SKIN — the camera-facing working layer.
  //
  // The tower already had the correct overall height, but the hero cameras mostly saw
  // the uninterrupted vessel and shaft surfaces behind it. Real offshore structures put
  // access, manifolds, junction boxes and escape routes outside that mass. Concentrating
  // those layers on the south and west faces gives both wide compositions recognisable
  // human scale without wasting geometry on the rear of the tower.
  // =================================================================================

  const facadeGalleryLevels = [
    MAIN_DECK_Y + 12,
    MAIN_DECK_Y + 27,
    MAIN_DECK_Y + 42,
  ];
  for (let level = 0; level < facadeGalleryLevels.length; level++) {
    const y = facadeGalleryLevels[level] as number;
    const half = towerHalfAt(y);
    const southFace = towerZ - half;
    const frontZ = southFace - 2.55;
    const backZ = southFace + 0.08;
    const xMin = towerX - half * (level === 0 ? 0.58 : 0.48);
    const xMax = towerX + half + 0.45;

    plate.add(box, boxTransform(
      (xMin + xMax) / 2,
      y,
      (frontZ + backZ) / 2,
      xMax - xMin,
      0.28,
      backZ - frontZ,
    ), 4);
    addRailRun(y, xMin, frontZ, xMax, frontZ, 1.45);
    addRailRun(y, xMax, frontZ, xMax, backZ, 1.35);
    if (level > 0) addRailRun(y, xMin, frontZ, xMin, backZ, 1.35);

    const bracketCount = Math.max(3, Math.round((xMax - xMin) / 3.2));
    for (let bracket = 0; bracket <= bracketCount; bracket++) {
      const x = xMin + ((xMax - xMin) * bracket) / bracketCount;
      steel.add(box, memberTransform(
        new Vector3(x, y - 0.18, frontZ + 0.08),
        new Vector3(x, y - 3.1, southFace + 0.28),
        0.3,
      ));
    }
  }

  const lowerFacadeY = facadeGalleryLevels[0] as number;
  const middleFacadeY = facadeGalleryLevels[1] as number;
  const upperFacadeY = facadeGalleryLevels[2] as number;
  const lowerSouthFace = towerZ - towerHalfAt(lowerFacadeY);
  const stairZ = lowerSouthFace - 2.72;
  const stairLandingY = (lowerFacadeY + middleFacadeY) / 2;

  // A face-on switchback route makes the blank module read as several storeys instead of
  // one giant block. The intermediate landing is hung from visible knee braces.
  addFacadeStairFlight(15.35, 10.85, stairZ, stairLandingY, lowerFacadeY);
  plate.add(box, boxTransform(15.35, stairLandingY, stairZ, 3.0, 0.24, 2.15), 3);
  addRailRun(stairLandingY, 13.85, stairZ - 1.08, 16.85, stairZ - 1.08, 1.2);
  for (const x of [13.95, 16.75]) {
    steel.add(box, memberTransform(
      new Vector3(x, stairLandingY - 0.18, stairZ),
      new Vector3(x, stairLandingY - 2.8, lowerSouthFace + 0.15),
      0.26,
    ));
  }
  addFacadeStairFlight(10.95, 15.35, stairZ, middleFacadeY, stairLandingY);

  // Horizontal separator on the lowest facade gallery, including saddle feet, bands,
  // nozzles and a drain. Rounded equipment is the simplest counter-shape to the cladding.
  const separatorY = lowerFacadeY + 3.5;
  const separatorZ = lowerSouthFace - 1.18;
  const separatorA = new Vector3(16.1, separatorY, separatorZ);
  const separatorB = new Vector3(22.3, separatorY, separatorZ);
  dark.add(cylHi, memberTransform(separatorA, separatorB, 2.55), 4);
  for (const bandT of [0.16, 0.5, 0.84]) {
    const band = Vector3.Lerp(separatorA, separatorB, bandT);
    steel.add(cylHi, memberTransform(
      new Vector3(band.x - 0.14, band.y, band.z),
      new Vector3(band.x + 0.14, band.y, band.z),
      2.78,
    ), 3);
  }
  for (const x of [17.45, 20.95]) {
    steel.add(box, boxTransform(
      x,
      (lowerFacadeY + separatorY - 1.1) / 2,
      separatorZ,
      0.46,
      separatorY - lowerFacadeY - 1.1,
      1.55,
    ), 2);
  }
  addPipeRun(steel, [
    new Vector3(18.0, separatorY + 1.3, separatorZ),
    new Vector3(18.0, separatorY + 3.8, separatorZ),
    new Vector3(20.4, separatorY + 3.8, separatorZ),
  ], 0.38, 7);
  addPipeRun(dark, [
    new Vector3(21.6, separatorY - 1.3, separatorZ),
    new Vector3(21.6, lowerFacadeY + 0.35, separatorZ),
    new Vector3(22.8, lowerFacadeY + 0.35, separatorZ),
  ], 0.31, 7);

  // Unequal process routes and a cable tray cross the facade instead of disappearing
  // behind it. Flange collars and valves turn the lines into working machinery.
  const middlePipeZ = towerZ - towerHalfAt(middleFacadeY + 3) - 2.78;
  addPipeRun(steel, [
    new Vector3(11.45, middleFacadeY + 2.4, middlePipeZ),
    new Vector3(21.7, middleFacadeY + 2.4, middlePipeZ),
    new Vector3(21.7, upperFacadeY - 1.6, middlePipeZ),
  ], 0.58, 7);
  addPipeRun(dark, [
    new Vector3(12.65, lowerFacadeY + 0.7, middlePipeZ - 0.42),
    new Vector3(12.65, middleFacadeY + 5.1, middlePipeZ - 0.42),
    new Vector3(18.4, middleFacadeY + 5.1, middlePipeZ - 0.42),
  ], 0.37, 8);
  addPipeRun(yellow, [
    new Vector3(14.1, middleFacadeY + 0.4, middlePipeZ - 0.78),
    new Vector3(14.1, upperFacadeY + 4.5, middlePipeZ - 0.78),
  ], 0.28, 8);
  addValveWheel(
    new Vector3(17.2, middleFacadeY + 2.4, middlePipeZ - 0.34),
    0.63,
  );
  addValveWheel(
    new Vector3(18.4, middleFacadeY + 5.1, middlePipeZ - 0.76),
    0.49,
  );
  addCableTray(
    12.0,
    middlePipeZ + 0.62,
    22.15,
    middlePipeZ + 0.62,
    middleFacadeY + 7.1,
  );

  // Recessed louvre and access panels turn the upper grey slab into fabricated modules.
  const louvreY = middleFacadeY + 7.3;
  const louvreSouthFace = towerZ - towerHalfAt(louvreY);
  dark.add(box, boxTransform(19.8, louvreY, louvreSouthFace - 0.16, 4.6, 6.4, 0.28), 2);
  for (const x of [17.55, 22.05]) {
    steel.add(box, boxTransform(x, louvreY, louvreSouthFace - 0.34, 0.16, 6.35, 0.16), 2);
  }
  for (const y of [louvreY - 3.1, louvreY + 3.1]) {
    steel.add(box, boxTransform(19.8, y, louvreSouthFace - 0.34, 4.65, 0.16, 0.16), 2);
  }
  for (let louvre = 0; louvre < 8; louvre++) {
    plate.add(box, boxTransform(
      19.8,
      louvreY - 2.3 + louvre * 0.62,
      louvreSouthFace - 0.47,
      3.7,
      0.12,
      0.16,
    ), 2);
  }
  for (const [x, y] of [[13.2, middleFacadeY + 1.0], [15.0, middleFacadeY + 1.3]] as const) {
    dark.add(box, boxTransform(x, y, middlePipeZ + 0.36, 1.15, 1.6, 0.65), 2);
    yellow.add(box, boxTransform(x, y + 0.84, middlePipeZ + 0.36, 1.22, 0.08, 0.7), 2);
  }

  // West-face vessel service landings. These project toward the landscape and portrait
  // cameras, so metre-scale manways and rails survive even when the south facade crops.
  const heroColumn = processColumns[0] as {
    x: number;
    z: number;
    d: number;
  };
  const columnWestX = heroColumn.x - heroColumn.d / 2;
  const columnPlatformZMin = heroColumn.z - heroColumn.d * 0.45;
  const columnPlatformZMax = heroColumn.z + heroColumn.d * 0.48;
  const westLandingLevels = [DECK_Y + 1.0, MAIN_DECK_Y + 21.0];
  for (let level = 0; level < westLandingLevels.length; level++) {
    const y = westLandingLevels[level] as number;
    const outerX = columnWestX - 1.55;
    plate.add(box, boxTransform(
      (outerX + columnWestX + 0.1) / 2,
      y,
      (columnPlatformZMin + columnPlatformZMax) / 2,
      columnWestX + 0.1 - outerX,
      0.26,
      columnPlatformZMax - columnPlatformZMin,
    ), 3);
    addRailRun(y, outerX, columnPlatformZMin, outerX, columnPlatformZMax, 1.35);
    addRailRun(y, outerX, columnPlatformZMax, columnWestX, columnPlatformZMax, 1.2);
    for (const z of [columnPlatformZMin + 1.0, columnPlatformZMax - 1.0]) {
      steel.add(box, memberTransform(
        new Vector3(outerX, y - 0.15, z),
        new Vector3(columnWestX + 0.2, y - 2.6, z),
        0.27,
      ));
    }
  }

  const manwayY = DECK_Y + 7.5;
  const manwayZ = heroColumn.z + 0.3;
  dark.add(cylHi, memberTransform(
    new Vector3(columnWestX - 1.15, manwayY, manwayZ),
    new Vector3(columnWestX + 0.18, manwayY, manwayZ),
    1.35,
  ), 4);
  steel.add(cylHi, memberTransform(
    new Vector3(columnWestX - 1.27, manwayY, manwayZ),
    new Vector3(columnWestX - 1.05, manwayY, manwayZ),
    1.58,
  ), 3);
  addValveWheel(
    new Vector3(columnWestX - 1.42, manwayY, manwayZ),
    0.58,
    'x',
  );
  addPipeRun(steel, [
    new Vector3(columnWestX - 1.65, DECK_Y + 2.0, columnPlatformZMin + 0.35),
    new Vector3(columnWestX - 1.65, MAIN_DECK_Y + 29, columnPlatformZMin + 0.35),
    new Vector3(columnWestX - 1.65, MAIN_DECK_Y + 29, columnPlatformZMax - 0.15),
  ], 0.43, 7);
  addPipeRun(dark, [
    new Vector3(columnWestX - 1.95, DECK_Y + 3.2, columnPlatformZMax - 0.45),
    new Vector3(columnWestX - 1.95, MAIN_DECK_Y + 16, columnPlatformZMax - 0.45),
    new Vector3(columnWestX + 0.1, MAIN_DECK_Y + 16, columnPlatformZMax - 0.45),
  ], 0.3, 8);

  // A shallow west-face louvre bank and wrap landing occupy the other formerly blank
  // slab without adding another solid mass.
  const westPanelY = DECK_Y + 3.0;
  const westFaceX = towerX - towerHalfAt(westPanelY);
  const westPanelZ = towerZ + 3.0;
  dark.add(box, boxTransform(westFaceX - 0.16, westPanelY, westPanelZ, 0.28, 6.2, 6.4), 2);
  for (const z of [westPanelZ - 3.05, westPanelZ + 3.05]) {
    steel.add(box, boxTransform(westFaceX - 0.34, westPanelY, z, 0.17, 6.2, 0.16), 2);
  }
  for (const y of [westPanelY - 3.0, westPanelY + 3.0]) {
    steel.add(box, boxTransform(westFaceX - 0.34, y, westPanelZ, 0.17, 0.17, 6.2), 2);
  }
  for (let louvre = 0; louvre < 7; louvre++) {
    plate.add(box, boxTransform(
      westFaceX - 0.47,
      westPanelY - 2.2 + louvre * 0.68,
      westPanelZ,
      0.16,
      0.12,
      4.9,
    ), 2);
  }
  const westWrapY = DECK_Y + 6.7;
  const westWrapFaceX = towerX - towerHalfAt(westWrapY);
  const westWrapOuterX = westWrapFaceX - 2.0;
  plate.add(box, boxTransform(
    (westWrapOuterX + westWrapFaceX) / 2,
    westWrapY,
    westPanelZ,
    westWrapFaceX - westWrapOuterX,
    0.27,
    7.4,
  ), 3);
  addRailRun(westWrapY, westWrapOuterX, westPanelZ - 3.7, westWrapOuterX, westPanelZ + 3.7, 1.35);
  addRailRun(westWrapY, westWrapOuterX, westPanelZ + 3.7, westWrapFaceX, westPanelZ + 3.7, 1.2);
  for (const z of [westPanelZ - 2.8, westPanelZ + 2.8]) {
    steel.add(box, memberTransform(
      new Vector3(westWrapOuterX, westWrapY - 0.16, z),
      new Vector3(westWrapFaceX + 0.2, westWrapY - 2.8, z),
      0.28,
    ));
  }
  addValveWheel(
    new Vector3(westWrapOuterX - 0.32, westPanelY + 0.4, westPanelZ + 1.2),
    0.52,
    'x',
  );

  // Put the process drum's inspection hardware on its true camera-facing tangent. The
  // earlier pure-west accessories sat around the silhouette, leaving the enormous
  // south-west face itself visually untouched in the user's close crop.
  const heroNormal = new Vector3(-heroColumn.x, 0, EYE_Z - heroColumn.z).normalize();
  const heroTangent = new Vector3(heroNormal.z, 0, -heroNormal.x).normalize();
  const heroYaw = Math.atan2(heroNormal.x, heroNormal.z);
  const heroSurface = new Vector3(heroColumn.x, 0, heroColumn.z)
    .add(heroNormal.scale(heroColumn.d / 2 + 0.08));
  const inspectionY = DECK_Y + 0.2;
  const inspectionCentre = new Vector3(heroSurface.x, inspectionY, heroSurface.z)
    .add(heroNormal.scale(0.18));
  dark.add(box, boxYawTransform(
    inspectionCentre.x,
    inspectionCentre.y,
    inspectionCentre.z,
    3.75,
    5.7,
    0.24,
    heroYaw,
  ), 2);
  for (const tangentOffset of [-1.78, 1.78]) {
    const p = inspectionCentre.add(heroTangent.scale(tangentOffset));
    steel.add(box, boxYawTransform(
      p.x,
      inspectionY,
      p.z,
      0.16,
      5.6,
      0.15,
      heroYaw,
    ), 2);
  }
  for (const y of [inspectionY - 2.68, inspectionY + 2.68]) {
    steel.add(box, boxYawTransform(
      inspectionCentre.x,
      y,
      inspectionCentre.z,
      3.7,
      0.16,
      0.15,
      heroYaw,
    ), 2);
  }
  for (let louvre = 0; louvre < 6; louvre++) {
    plate.add(box, boxYawTransform(
      inspectionCentre.x,
      inspectionY - 1.8 + louvre * 0.66,
      inspectionCentre.z,
      3.05,
      0.11,
      0.18,
      heroYaw,
    ), 2);
  }

  const drumPlatformY = DECK_Y + 3.45;
  const drumPlatformCentre = new Vector3(heroSurface.x, drumPlatformY, heroSurface.z)
    .add(heroNormal.scale(0.95));
  plate.add(box, boxYawTransform(
    drumPlatformCentre.x,
    drumPlatformCentre.y,
    drumPlatformCentre.z,
    5.1,
    0.26,
    1.8,
    heroYaw,
  ), 3);
  const drumOuter = new Vector3(heroSurface.x, drumPlatformY, heroSurface.z)
    .add(heroNormal.scale(1.84));
  addRailRun(
    drumPlatformY,
    drumOuter.x - heroTangent.x * 2.55,
    drumOuter.z - heroTangent.z * 2.55,
    drumOuter.x + heroTangent.x * 2.55,
    drumOuter.z + heroTangent.z * 2.55,
    1.25,
  );
  for (const tangentOffset of [-1.85, 1.85]) {
    const outer = drumOuter.add(heroTangent.scale(tangentOffset));
    const inner = new Vector3(heroSurface.x, drumPlatformY - 2.7, heroSurface.z)
      .add(heroTangent.scale(tangentOffset));
    steel.add(box, memberTransform(
      new Vector3(outer.x, drumPlatformY - 0.16, outer.z),
      inner,
      0.28,
    ));
  }

  const tangentManwayY = DECK_Y + 8.8;
  const tangentManwaySurface = new Vector3(heroSurface.x, tangentManwayY, heroSurface.z);
  dark.add(cylHi, memberTransform(
    tangentManwaySurface.add(heroNormal.scale(0.08)),
    tangentManwaySurface.add(heroNormal.scale(1.18)),
    1.28,
  ), 4);
  steel.add(cylHi, memberTransform(
    tangentManwaySurface.add(heroNormal.scale(1.02)),
    tangentManwaySurface.add(heroNormal.scale(1.28)),
    1.48,
  ), 3);
  addValveWheel(
    tangentManwaySurface.add(heroNormal.scale(1.43)),
    0.57,
    heroNormal,
  );
  const tangentPipeBase = new Vector3(heroSurface.x, DECK_Y + 1.0, heroSurface.z)
    .add(heroNormal.scale(1.25))
    .add(heroTangent.scale(-2.25));
  addPipeRun(steel, [
    tangentPipeBase,
    new Vector3(tangentPipeBase.x, MAIN_DECK_Y + 27.5, tangentPipeBase.z),
    new Vector3(tangentPipeBase.x, MAIN_DECK_Y + 27.5, tangentPipeBase.z)
      .add(heroTangent.scale(4.2)),
  ], 0.36, 7);

  // Directly articulate the first enclosed shaft segment. Its south face is the second
  // large blank rectangle in the close crop, so raised door framing, louvres and a pipe
  // station live in front of the cladding rather than being implied by its texture.
  const shaftLowerBottom = MAIN_DECK_Y + 3;
  const shaftLowerTop = MAIN_DECK_Y + 19;
  const shaftLowerMid = (shaftLowerBottom + shaftLowerTop) / 2;
  const shaftFrontZ = shaftLowerRecessZ - 0.2;
  plate.add(box, boxTransform(
    shaftX - 1.35,
    shaftLowerMid - 1.2,
    shaftFrontZ,
    1.75,
    5.8,
    0.24,
  ), 2);
  for (const x of [shaftX - 2.2, shaftX - 0.5]) {
    steel.add(box, boxTransform(
      x,
      shaftLowerMid - 1.2,
      shaftFrontZ - 0.16,
      0.14,
      5.75,
      0.14,
    ), 2);
  }
  for (const y of [shaftLowerMid - 4.0, shaftLowerMid + 1.6]) {
    steel.add(box, boxTransform(
      shaftX - 1.35,
      y,
      shaftFrontZ - 0.16,
      1.82,
      0.14,
      0.14,
    ), 2);
  }
  dark.add(box, boxTransform(
    shaftX + 1.15,
    shaftLowerMid + 1.3,
    shaftFrontZ - 0.02,
    2.1,
    5.0,
    0.23,
  ), 2);
  for (let louvre = 0; louvre < 7; louvre++) {
    steel.add(box, boxTransform(
      shaftX + 1.15,
      shaftLowerMid - 0.45 + louvre * 0.56,
      shaftFrontZ - 0.22,
      1.7,
      0.11,
      0.15,
    ), 2);
  }
  addPipeRun(steel, [
    new Vector3(shaftX - 2.55, shaftLowerBottom + 0.5, shaftFrontZ - 0.48),
    new Vector3(shaftX - 2.55, shaftLowerTop + 2.0, shaftFrontZ - 0.48),
    new Vector3(shaftX + 0.2, shaftLowerTop + 2.0, shaftFrontZ - 0.48),
  ], 0.34, 8);
  addValveWheel(
    new Vector3(shaftX - 2.55, shaftLowerMid + 2.0, shaftFrontZ - 0.68),
    0.47,
    new Vector3(0, 0, -1),
  );
  for (const [x, y] of [
    [shaftX + 0.25, shaftLowerMid - 3.0],
    [shaftX + 1.9, shaftLowerMid - 2.6],
  ] as const) {
    dark.add(box, boxTransform(x, y, shaftFrontZ - 0.42, 1.0, 1.25, 0.55), 2);
    yellow.add(box, boxTransform(x, y + 0.67, shaftFrontZ - 0.42, 1.06, 0.08, 0.6), 2);
  }

  // The camera-side rail now terminates in recognisable working props instead of empty
  // black floor: local controls, a conduit drop and a grated drain channel.
  dark.add(box, boxTransform(8.15, DECK_Y + 0.72, 2.85, 1.15, 1.42, 0.78), 2);
  yellow.add(box, boxTransform(8.15, DECK_Y + 1.47, 2.85, 1.22, 0.09, 0.84), 2);
  plate.add(box, boxTransform(8.15, DECK_Y + 0.77, 2.44, 0.7, 0.82, 0.08), 2);
  addPipeRun(dark, [
    new Vector3(8.58, DECK_Y + 0.18, 2.85),
    new Vector3(8.58, DECK_Y + 1.15, 2.85),
    new Vector3(9.7, DECK_Y + 1.15, 2.85),
  ], 0.1, 8, false);
  dark.add(box, boxTransform(7.2, DECK_Y + 0.08, 3.65, 4.0, 0.08, 0.5), 5);

  // A utility riser follows the visible south-west jacket leg. Clamp bridges tie it to
  // the structure and make the vertigo shot feel maintained rather than skeletal.
  const jacketServiceLevels = [35, 47, 59, 71, 83];
  const jacketServiceOffset = new Vector3(1.55, 0, -1.15);
  const jacketServicePoints = jacketServiceLevels.map(
    (y) => legAt(2, y).add(jacketServiceOffset),
  );
  addPipeRun(dark, jacketServicePoints, 0.34, 7, false);
  for (const y of jacketServiceLevels) {
    steel.add(box, memberTransform(
      legAt(2, y),
      legAt(2, y).add(jacketServiceOffset),
      0.14,
    ));
  }

  // Crane turntable and exposed winch pair make the boom visibly originate in machinery
  // rather than disappearing into the tower.
  dark.add(cylHi, boxTransform(
    craneX,
    pedestalTop + 0.12,
    craneZ,
    5.05,
    0.42,
    5.05,
  ), 3);
  for (const zOff of [-0.95, 0.95]) {
    dark.add(cylHi, memberTransform(
      new Vector3(housingX - 1.18, housingY + housingH / 2 + 0.68, housingZ + zOff),
      new Vector3(housingX + 1.18, housingY + housingH / 2 + 0.68, housingZ + zOff),
      0.72,
    ), 3);
  }
  // =================================================================================
  // LIGHTING
  // =================================================================================
  const spots: SpotLight[] = [];
  const points: PointLight[] = [];
  const lampMeshes: Mesh[] = [];
  // Offshore work lights read as warm-white rectangles, not orange pinpricks. Keep a
  // little tungsten warmth in the lens while giving every repeated fixture enough
  // apparent output to remain legible through overcast daylight and sea haze.
  /*
   * Emissive faces lifted with the lights they represent.
   *
   * These are the only part of a lamp that did NOT scale when the flood power went up
   * fourfold — they are materials, not lights. Left behind, every fixture cast a strong
   * pool while its own face read as dull, which is backwards: the lens should be the
   * brightest thing in frame.
   */
  /*
   * Tungsten, not daylight.
   *
   * A halogen flood runs around 2900 K, so its face is markedly orange — the previous
   * (1.0, 0.86, 0.62) read as a cool white panel. Dropping green and blue further is what
   * makes it a warm lamp rather than a bright rectangle, and it separates the fixtures
   * from the cold searchlight, which is the one thing on the rig that IS daylight-white.
   */
  /*
   * Kept BELOW clipping, deliberately.
   *
   * `createLampMaterial` multiplies colour by intensity, so anything much above 1 pushes
   * green and blue past 1.0 too and ACES resolves the lot to white — which is why the
   * lens read as a plain white square at every value tried, however warm the colour was.
   * At 1.15 only red clips; green lands near 0.7 and blue near 0.3, so the face resolves
   * as hot orange with the tungsten hue intact.
   */
  const lampMat = createLampMaterial(scene, new Color3(1.0, 0.58, 0.24), 0.22);
  /*
   * The filament tube itself: hotter and whiter than the reflector around it.
   *
   * A real halogen flood reads as a dim dimpled dish with one blinding line across the
   * middle, and that line is most of what identifies the fixture at a glance.
   *
   * The gap between the two is what makes it read at all. Both were previously bright
   * enough to clip to white through ACES and bloom, so a hotter bar on a hot lens was
   * white on white and invisible. The reflector is now well below clipping and only the
   * filament blows out — which is exactly the relationship in the reference photos.
   */
  /*
   * The filament, and the ONE part that should blow out.
   *
   * Well past clipping on every channel, so it resolves to white-hot against the orange
   * dish behind it. That contrast is the whole read: without it the fixture is a flat
   * panel, and with the dish also clipping it was white on white.
   */
  const bulbMat = createLampMaterial(scene, new Color3(1.0, 0.94, 0.80), 1.3);

  const floodSpecs: {
    pos: Vector3;
    dir: Vector3;
    active: boolean;
    ocean: boolean;
    oceanDir?: Vector3;
    /**
     * Marks a fixture as a high-power flood.
     *
     * Carried as a flag rather than a number because the actual intensity is owned by
     * `environment.ts` — it has to scale with time of day, and a value baked in here
     * would blaze at noon. The flag only selects which multiplier that authority uses.
     */
    strong?: boolean;
    /**
     * Deck floods: the rail-mounted set that lights the water directly below the player.
     *
     * Their own class rather than `strong` because they differ in two ways at once —
     * an order of magnitude more power, and a colder colour. They are the only fixtures
     * aimed at the sea rather than at the structure, and the sea is what has to read.
     */
    deck?: boolean;
  }[] = [
    // The two real light pairs sit on the new facade galleries and rake across them.
    // Their opposing directions reveal pipes, stairs and louvres as alternating value
    // bands instead of washing the whole tower uniformly.
    {
      pos: new Vector3(11.45, lowerFacadeY + 2.25, lowerSouthFace - 2.32),
      dir: new Vector3(0.84, -0.42, 0.34),
      active: true,
      ocean: false,
    },
    {
      pos: new Vector3(
        21.3,
        middleFacadeY + 2.45,
        towerZ - towerHalfAt(middleFacadeY) - 2.28,
      ),
      dir: new Vector3(-0.82, -0.47, 0.32),
      active: true,
      ocean: false,
    },
    // The new compression plant carries a vertical chain of work lights. Besides
    // making its access route readable at night, the emissive lenses provide human-scale
    // repetition in overcast daylight like the close offshore references.
    {
      pos: new Vector3(15.45, sideBayUpperGalleryY + 2.25, sideBayZMax + 0.18),
      dir: new Vector3(0.15, -0.58, 0.8),
      active: true,
      ocean: false,
    },
    {
      pos: new Vector3(22.1, highBayBaseY + 3.35, sideBayZMax + 0.18),
      dir: new Vector3(-0.18, -0.56, 0.81),
      active: true,
      ocean: false,
    },
    {
      pos: new Vector3(28.7, highBayMidY + 2.45, sideBayZMax + 0.18),
      dir: new Vector3(-0.25, -0.58, 0.77),
      active: false,
      ocean: false,
    },
    {
      pos: new Vector3(18.2, highBayTopY + 1.85, sideBayZMax - 0.15),
      dir: new Vector3(0.12, -0.62, 0.78),
      active: false,
      ocean: false,
    },
    // Repeated accommodation-gallery fixtures use emissive lenses and the standard
    // rounded pale-metal frames. They remain non-casting to preserve the active-light
    // budget while still producing the occupied-floor rhythm seen in the references.
    {
      pos: new Vector3(31.4, DECK_Y + 7.1, -13.78),
      dir: new Vector3(-0.1, -0.5, 0.86),
      active: false,
      ocean: false,
    },
    {
      pos: new Vector3(27.5, DECK_Y + 13.0, -13.78),
      dir: new Vector3(0.08, -0.48, 0.87),
      active: false,
      ocean: false,
    },
    {
      pos: new Vector3(30.6, DECK_Y + 19.1, -14.9),
      dir: new Vector3(-0.1, -0.5, 0.86),
      active: false,
      ocean: false,
    },
    {
      pos: new Vector3(26.7, DECK_Y + 25.4, -14.9),
      dir: new Vector3(0.08, -0.48, 0.87),
      active: false,
      ocean: false,
    },
    {
      pos: new Vector3(27.8, DECK_Y + 32.0, -16.3),
      dir: new Vector3(-0.08, -0.5, 0.86),
      active: false,
      ocean: false,
    },
    {
      pos: new Vector3(craneX - 2.6, MAIN_DECK_Y + 11.5, craneZ - 1.8),
      // Dedicated sea-facing floods throw long, steep pools beyond the jacket. Their
      // scene lights remain analytical in the ocean shader, avoiding two more Babylon
      // lights and preserving the six-light material budget.
      dir: new Vector3(-0.25, -0.72, -0.55),
      active: false,
      ocean: true,
      oceanDir: new Vector3(-0.18, -0.40, 0.90),
    },
    {
      pos: new Vector3(CATWALK_MAX_X - 0.3, DECK_Y + 3.0, 14.5),
      dir: new Vector3(-0.20, -0.70, -0.60),
      active: false,
      ocean: true,
      oceanDir: new Vector3(-0.08, -0.36, 0.93),
    },
  ];

  /*
   * The Refinery's western wall carries no floods. Removed 2026-07-28 (Adam).
   *
   * Fifteen fixtures stood proud of the outboard works' outer columns at
   * `outboardXMin - 1.1` (x 12.90) — five storeys by three bays, every one aimed due
   * west. They were the far side of the same 2.4 m slot the Power Station's eastern wall
   * fired into, and they went on lighting a blank wall once that wall's twelve were
   * removed. The slot is now dark from both sides, which is what it should have been:
   * you cannot see into it from anywhere the player stands.
   *
   * The outboard works still reads as occupied — its wing fixtures and the house near
   * face are untouched.
   */
  for (const y of [
    DECK_Y + 9.0,
    DECK_Y + 15.1,
    DECK_Y + 21.2,
    DECK_Y + 27.4,
  ]) {
    for (const x of [20.5, 24.7, 28.9, 33.1]) {
      floodSpecs.push({
        pos: new Vector3(x, y, houseNearZ + 0.35),
        dir: new Vector3(-0.075, -0.530, 0.845),   // 32 deg
        active: false,
        ocean: false,
      });
    }
  }
  /*
   * Six fixtures on the outboard wing, keyed to its frame rather than to fixed numbers.
   *
   * These were hardcoded to the wing's original position and were left hanging in open
   * air the moment it moved 11 m inboard — the one part of that relocation done by
   * editing coordinates rather than by mark/translate, which is exactly the failure mode
   * that motivated building mark/translate in the first place.
   *
   * Deriving them from `outboardZNear` and the frame spacing means they cannot come
   * adrift again: move the wing and the lights are already on it.
   */
  for (const [xFrac, y, zRef] of [
    [0.10, DECK_Y + 5.7, outboardZNear - 4.75],
    [0.45, DECK_Y + 5.7, outboardZNear - 4.75],
    [0.80, DECK_Y + 5.7, outboardZNear - 4.75],
    [0.17, DECK_Y + 12.45, outboardZNear - 13.45],
    [0.50, DECK_Y + 12.45, outboardZNear - 13.45],
    [0.85, DECK_Y + 12.45, outboardZNear - 13.45],
  ] as const) {
    floodSpecs.push({
      pos: new Vector3(
        outboardXMin + (outboardXMax - outboardXMin) * xFrac,
        y,
        zRef,
      ),
      // Aimed inboard and down, back toward the platform.
      dir: new Vector3(-0.084, -0.850, 0.521),   // 58 deg
      active: false,
      ocean: false,
    });
  }
  /*
   * The Power Station's eastern wall carries no floods. Removed 2026-07-28 (Adam).
   *
   * There were twelve here — four storeys by three bays, aimed east at 32 degrees below
   * horizontal. Measuring the scene rather than reading the source is what condemned
   * them: after the building's move the wall ended up 2.4 m from the Refinery's outboard
   * cage (x 10.46 against x 12.90), and the cage carries fifteen of its own fixtures
   * aimed back west. Twenty-seven lights were firing into a slot too narrow to see into,
   * and with a 12-lights-per-mesh cap they were displacing fixtures elsewhere on the rig
   * that had something to light.
   */
  /*
   * The Power Station's remaining six fixtures — the ones Adam kept finding in mid-air.
   *
   * Hardcoded against the building's ORIGINAL north-west position. It has since moved
   * 31 m east and 45 m south, rotated 270 degrees and stretched 30%, and these never
   * followed: they sat north-east of the Silo lighting empty water, while the wall they
   * belong to was on the far side of the platform.
   *
   * Routed through `powerStationTransform` like the rest, so they move with the building
   * from here on. Positions transform as coordinates, aim vectors as normals — a
   * direction must be rotated but never translated, or it points at the origin.
   */
  for (const [x, y, z] of [
    [-16.0, DECK_Y + 5.9, 20.4],
    [-16.0, DECK_Y + 12.5, 13.4],
    [-16.0, DECK_Y + 19.2, 20.4],
    [-16.0, DECK_Y + 26.15, 13.4],
    // Two of this group moved to the Barracks roof; four remain on the Power Station.
  ] as const) {
    floodSpecs.push({
      pos: Vector3.TransformCoordinates(new Vector3(x, y, z), powerStationTransform),
      dir: Vector3.TransformNormal(
        new Vector3(0.590, -0.530, -0.609), powerStationTransform,   // 32 deg
      ).normalize(),
      active: false,
      ocean: false,
    });
  }

  /*
   * Two floods on the eastern Barracks' northern ledge, looking back at the Platform.
   *
   * Derived from the Barracks' own constants rather than typed as world coordinates —
   * that is the mistake this whole hunt was about. `-13` is the eastern block's centre,
   * its north face is half its depth forward of that, and the ledge is the roof line.
   * Move the Barracks and these follow.
   *
   * Aimed back and down at the observation deck, so they light the walkway a player
   * actually crosses rather than the sea behind them.
   */
  {
    const ledgeY = MAIN_DECK_Y + 12.4;
    const northFaceZ = -14 + 7 + 0.45;
    for (const x of [-17.0, -9.0]) {
      floodSpecs.push({
        pos: new Vector3(x, ledgeY, northFaceZ),
        dir: new Vector3(0.574, -0.669, 0.472).normalize(),   // 42 deg
        // Live, and on the high-power class: these are the two that light the walkway
        // the player actually crosses, so they are worth real scene lights.
        active: true,
        strong: true,
        ocean: false,
      });
    }
  }

  /*
   * The sea's entire lighting, and it is exactly two lamps.
   *
   * `setRigFloods` reads floods[0] and floods[1] and discards the rest — the water is a
   * custom ShaderMaterial that no scene light can reach. Anything beyond the first two
   * entries here lights nothing, however bright it is or wherever it is aimed.
   */
  const oceanFloods: RigOceanFlood[] = floodSpecs
    .filter((spec) => spec.ocean)
    .map((spec) => ({
      position: spec.pos.clone(),
      direction: (spec.oceanDir ?? spec.dir).clone().normalize(),
    }));

  /*
   * How many scene lights the rig may still create — measured, not assumed.
   *
   * Babylon gives each mesh the FIRST `maxSimultaneousLights` entries of `scene.lights`,
   * in creation order: `AbstractMesh._resyncLightSources` walks the scene list and pushes
   * anything `canAffectMesh` accepts, and `BindLights` then truncates with
   * `Math.min(mesh.lightSources.length, maxSimultaneousLights)`. There is no distance
   * sort anywhere in that path, and `canAffectMesh` returns true for every light here
   * because nothing sets an include list, an exclude list or a layer mask. So every rig
   * mesh receives the same twelve lights, and every light created after the twelfth is
   * inert — it costs a matrix update per frame and lights nothing.
   *
   * The rig used to create 136 of them. Five fixtures lit the entire structure and the
   * other sixty-three were glowing stickers, which is why the Crane Tower stayed dark
   * however many lamps were bolted near it and why raising intensity never helped.
   *
   * Reading the live scene length rather than hardcoding "three for sun, ambient and
   * searchlight" means this stays correct if anything is ever created before the rig.
   */
  // Budgeted from the GPU's real uniform-block limit (see lightBudget.ts): the same
  // number the materials bind, so a lamp is never created into a slot that cannot exist.
  let lightSlots = Math.max(0, maxSimultaneousLightsFor(scene) - scene.lights.length);

  for (let i = 0; i < floodSpecs.length; i++) {
    // Typed from the array itself rather than re-declared inline: a duplicated shape
    // silently drops any field added to the source, which is exactly what happened when
    // `strong` was introduced — the array had it, this cast did not.
    const spec = floodSpecs[i] as (typeof floodSpecs)[number];
    /*
     * Lens and bezel only.
     *
     * The dark housing box and its mounting strut are gone at Adam's direction. They read
     * as a black lump behind every lamp, and at the distances these are seen from — the
     * far side of the platform, or the Bridge — the fixture is legible from its lit face
     * and frame alone.
     */
    const lens = new Mesh(`floodLens${i}`, scene);
    CreatePlaneVertexData({
      width: 0.7,
      height: 0.7,
      sideOrientation: Mesh.DOUBLESIDE,
    }).applyToMesh(lens);
    const d = spec.dir.clone().normalize();
    // Seat the emissive face just beyond the housing. A former 0.9m offset made it
    // float, while offsets below 0.62m clip against the axis-aligned casing.
    lens.position.copyFrom(spec.pos.add(d.scale(0.66)));
    lens.lookAt(spec.pos.add(d.scale(4)));

    /*
     * The halogen tube, laid across the lens on its horizontal axis.
     *
     * Sits just proud of the emissive face so it is never z-fighting with it, and is
     * built from the same right-vector the bezel uses, so it stays level with the frame
     * however the fixture is aimed.
     */
    const bulbRight = Vector3.Cross(Vector3.Up(), d).normalize();
    const bulb = new Mesh(`floodBulb${i}`, scene);
    CreatePlaneVertexData({
      width: 0.48,
      height: 0.075,
      sideOrientation: Mesh.DOUBLESIDE,
    }).applyToMesh(bulb);
    bulb.position.copyFrom(spec.pos.add(d.scale(0.70)));
    bulb.lookAt(bulb.position.add(d));
    bulb.rotate(d, Math.atan2(bulbRight.y, 1));
    bulb.material = bulbMat;
    bulb.isPickable = false;
    lampMeshes.push(bulb);

    // Every floodlight uses the same pale galvanized bezel. Four slim beveled members
    // create a slightly rounded outer rectangle around the emissive face while remaining
    // part of the shared steel accumulator rather than adding four meshes per lamp.
    const frameRight = Vector3.Cross(Vector3.Up(), d).normalize();
    const frameUp = Vector3.Cross(d, frameRight).normalize();
    const frameCentre = lens.position.add(d.scale(0.018));
    const frameHalfSpan = 0.5;
    const frameOffset = 0.445;
    const floodFrameTint = [1.38, 1.4, 1.38] as const;
    for (const offset of [-frameOffset, frameOffset]) {
      steel.add(floodFrameMember, memberTransform(
        frameCentre.add(frameUp.scale(offset)).subtract(frameRight.scale(frameHalfSpan)),
        frameCentre.add(frameUp.scale(offset)).add(frameRight.scale(frameHalfSpan)),
        0.095,
      ), 1.5, floodFrameTint);
      steel.add(floodFrameMember, memberTransform(
        frameCentre.add(frameRight.scale(offset)).subtract(frameUp.scale(frameHalfSpan)),
        frameCentre.add(frameRight.scale(offset)).add(frameUp.scale(frameHalfSpan)),
        0.095,
      ), 1.5, floodFrameTint);
    }

    lens.material = lampMat;
    lens.isPickable = false;
    lampMeshes.push(lens);

    /*
     * Every fixture is lit, on Adam's direction.
     *
     * This was previously six. The materials cap at `maxSimultaneousLights = 6`, so the
     * shader still only evaluates the nearest handful per mesh — but Babylon now sorts
     * and uploads every one of them each frame, so the cost is real and lands on the CPU
     * rather than showing up as a draw-call figure. Measured after the change; see the
     * perf note in the completion report.
     */

    /*
     * The fixture's geometry is always built; its scene lights only while slots remain.
     *
     * Past the cap a `SpotLight` is not a dim light, it is no light at all — so skipping
     * it is not a downgrade, it removes an object that was never sampled. The lens, bezel
     * and filament above are untouched, which is why the rig looks identical: what a
     * player reads as "a lit lamp" at these distances is the emissive face, not the pool
     * it casts. Every fixture still glows; only the ones that were already inert stop
     * pretending to be lights.
     */
    if (lightSlots > 0) {
      const spot = new SpotLight(
        spec.deck ? `floodDeck${i}` : spec.strong ? `floodStrong${i}` : `flood${i}`,
        spec.pos.clone(), d, spec.deck ? 1.15 : 1.4, 10, scene,
      );
      // Deck floods run colder — a bluer arc lamp against the rig's warm sodium, which is
      // also what stops the sea below reading as the same orange as the steel above it.
      spot.diffuse = spec.deck
        ? new Color3(0.86, 0.92, 1.0)
        : new Color3(1.0, 0.85, 0.62);
      spot.intensity = 0;
      spot.range = 220;
      spots.push(spot);
      lightSlots--;
    }

    // Real floodlights bounce from nearby steel. A small warm local fill prevents the
    // night rig becoming a black cut-out around a few floating white lenses.
    if (lightSlots > 0) {
      const fill = new PointLight(
        spec.deck ? `floodFillDeck${i}`
          : spec.strong ? `floodFillStrong${i}` : `floodFill${i}`,
        spec.pos.clone(), scene,
      );
      fill.diffuse = spec.deck
        ? new Color3(0.80, 0.88, 1.0)
        : new Color3(1.0, 0.62, 0.27);
      fill.intensity = 0;
      fill.range = 36;
      points.push(fill);
      lightSlots--;
    }
  }

  const warnMat = createLampMaterial(scene, new Color3(1, 0.12, 0.08), 9.5);
  /*
   * Aviation warning lamps, dropped with the tower they are bolted to.
   *
   * They are emissive meshes and point lights, not accumulated geometry, so the
   * mark/translate that lowered the Crane Tower could not reach them — it moves vertex
   * buffers, and these are neither. That is the third time tonight a fixture has been
   * left behind by a structural move, and always the same class: anything that is not
   * accumulator geometry must be offset explicitly.
   */
  for (const wy of [
    TOWER_TOP + 12 + CRANE_TOWER_DROP,
    TOWER_TOP + CRANE_TOWER_DROP,
    MAIN_DECK_Y + 30 + CRANE_TOWER_DROP,
  ]) {
    const lamp = new Mesh('warnLamp', scene);
    CreateCylinderVertexData({ height: 0.8, diameter: 0.7, tessellation: 8 }).applyToMesh(lamp);
    lamp.position.set(towerX, wy, towerZ);
    lamp.material = warnMat;
    lamp.isPickable = false;
    lampMeshes.push(lamp);
    const pl = new PointLight('warnPoint', new Vector3(towerX, wy, towerZ), scene);
    pl.diffuse = new Color3(1, 0.15, 0.1);
    pl.intensity = 0;
    pl.range = 40;
    points.push(pl);
  }

  // =================================================================================
  const meshes: Mesh[] = [];
  const built: [MeshAccumulator, string, PBRMetallicRoughnessMaterial][] = [
    [yellow, 'rigYellowParts', mats.yellowPaint],
    [steel, 'rigSteelParts', mats.steel],
    [dark, 'rigDarkParts', mats.darkSteel],
    [plate, 'rigPlateParts', mats.grating],
    [machined, 'rigMachinedParts', mats.machinedSteel],
    [openGrate, 'rigOpenGrating', mats.openGrating],
  ];
  for (const [acc, name, mat] of built) {
    const m = acc.build(scene, name, mat);
    if (m) meshes.push(m);
  }
  // Ordinary depth testing decides occlusion — the sea must hide the legs below it.
  for (const m of meshes) m.renderingGroupId = 0;
  for (const m of lampMeshes) m.renderingGroupId = 0;

  return {
    meshes,
    lights: { spots, points, lampMeshes, oceanFloods },
    drawCalls: meshes.length + lampMeshes.length,
  };
}
